import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import { dashboardI18n } from '../i18n/visitorPortalI18n'
import { useTranslate } from '../i18n/useTranslate'
import { registerPdfFont } from '../i18n/pdfFont'
import { deliverPdf } from '../utils/pdfOutput'
import logo from "../assets/images/logo.svg";
import SelfConnectImg from "../assets/images/app_logo.webp";
import chitrapatImage from "../assets/images/chitrapat.png";
import jainMandirImage from "../assets/images/jinmandir-salient-features.png";
import mapLegendImage from "../assets/images/map-legend.jpeg";
import jivandarshanMuseum1 from "../assets/images/jivandarshan-museum-1.jpg";
import jivandarshanMuseum2 from "../assets/images/jivandarshan-museum-2.jpg";
import { Helmet } from 'react-helmet-async';
import { load } from "@cashfreepayments/cashfree-js";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';


/* ═══════════════════════════════════════════════════════════════
   V4 — Bold Editorial / Masterclass-inspired
   Condensed typography, high contrast, mixed dark/light cards,
   day timeline, live "now" indicator, wisdom quote
   ═══════════════════════════════════════════════════════════════ */

/* ─── Palette ─── */
const V4 = {
  bg: '#F2F0EB',
  card: '#FFFFFF',
  cardDark: '#3E2A1C',
  cardDarker: '#33221A',
  accent: '#D4622B',
  accentDark: '#B84F1F',
  accentLight: '#E8845A',
  accentBg: 'rgba(212,98,43,0.08)',
  accentBorder: 'rgba(212,98,43,0.20)',

  sage: '#5A7A6B',
  sageBg: 'rgba(90,122,107,0.08)',

  text: '#2C1E14',
  textOnDark: '#FFF8F2',
  textMuted: '#7A6B5E',
  textDim: '#B0A498',

  border: 'rgba(62,42,28,0.08)',
  shadow: '0 1px 4px rgba(62,42,28,0.04), 0 6px 20px rgba(62,42,28,0.05)',
  shadowMd: '0 2px 8px rgba(62,42,28,0.06), 0 12px 32px rgba(62,42,28,0.07)',
}

/* Bold heading style */
const bold = {
  fontFamily: "'Outfit', 'Inter', sans-serif",
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '-0.03em',
  lineHeight: 0.95,
}


/* Explore-after-tour order (Jinmandir, Raj Sabhagruh, Library first) */
const dayPassKey = 'dayPass'
const mealCategoryMap = { breakfast: 'Breakfast', veganBreakfast: 'Vegan Breakfast', lunch: 'Lunch', veganLunch: 'Vegan Lunch', dinner: 'Dinner', veganDinner: 'Vegan Dinner', }

// ENV_APP=local widens the arrival window so the whole evening can be exercised
// while testing. Create React App only injects vars prefixed with REACT_APP_,
// so accept either spelling; dotenv already strips surrounding quotes, but trim
// them defensively in case the value arrives raw.
const IS_LOCAL_ENV = (process.env.REACT_APP_ENV_APP ?? process.env.ENV_APP ?? '')
  .toString().trim().replace(/^['"]|['"]$/g, '').toLowerCase() === 'local'

// Arrival time should be limited to a fixed window for planning:
// 08:00 AM to 06:00 PM (inclusive) — 09:00 PM on local.
const ARRIVAL_MIN_MINUTES = 8 * 60
const ARRIVAL_MAX_MINUTES = (IS_LOCAL_ENV ? 21 : 18) * 60
const ARRIVAL_STEP_MINUTES = 30

// Special-case: allow a "post <cutoff>" option that must remain selectable.
// We store it one minute past the last slot so it is distinguishable from that
// slot — 18:01 normally, 21:01 once the window is widened.
const POST_6PM_MINUTES = ARRIVAL_MAX_MINUTES + 1
const POST_6PM_VALUE = `${String(Math.floor(POST_6PM_MINUTES / 60)).padStart(2, '0')}:${String(POST_6PM_MINUTES % 60).padStart(2, '0')}`
const POST_6PM_LABEL = `Post ${Math.floor(ARRIVAL_MAX_MINUTES / 60) % 12 || 12} pm`

// When "Post 6 PM" is chosen we resolve it to the visitor's *current* clock time,
// snapped to the NEAREST 30-min figure (e.g. 7:23 -> 7:30, 7:34 -> 7:30). These helpers
// format that time for display (12h label), the dropdown, and the planner API payload (24h HH:MM).
const snapToArrivalStep = (mins) => Math.round(mins / ARRIVAL_STEP_MINUTES) * ARRIVAL_STEP_MINUTES
const currentSnappedArrivalMinutes = () => {
  const now = new Date()
  return snapToArrivalStep(now.getHours() * 60 + now.getMinutes())
}
const minsTo12hLabel = (mins) => {
  const h24 = Math.floor(mins / 60) % 24
  const mm = mins % 60
  const period = h24 >= 12 ? 'pm' : 'am'
  const h12 = (h24 % 12) || 12
  return `${String(h12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${period}`
}
const minsToHHMM = (mins) => {
  const h24 = Math.floor(mins / 60) % 24
  const mm = mins % 60
  return `${String(h24).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

// Normalize any AM/PM in a displayed time string to lowercase (am/pm) so the
// whole page stays consistent, regardless of the casing the API returns.
const toLowerAmPm = (s) => (s ?? '').toString().replace(/(am|pm)/gi, (m) => m.toLowerCase())

// Planner config stores 24-hour times either as a bare hour ("10") or as a full
// clock ("10:30"). Normalize both to "HH:MM" so fmtMin() can format them.
// Returns '' for anything unparseable, so callers can just check for falsy.
const to24hHHMM = (value) => {
  const m = (value ?? '').toString().trim().match(/^(\d{1,2})(?::(\d{1,2}))?$/)
  if (!m) return ''
  const h = Number(m[1])
  const mm = m[2] == null ? 0 : Number(m[2])
  if (h > 23 || mm > 59) return ''
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

// Overnight check-in is noon. The planner config does not carry a check-in key yet
// (only OVERNIGHT_CHECKOUT_TIME_IN_24_HRS), so this is the ashram's standing value
// until OVERNIGHT_CHECKIN_TIME_IN_24_HRS is added — config wins once it exists.
const OVERNIGHT_CHECKIN_FALLBACK = '12:00'

// A guided tour's stops arrive as one venue string separated by "·", e.g.
// "Welcome Centre · Raj Darbar · Jinmandir <br/> (Auditorium …)". The first stop
// is where the tour starts, which we show on its own line above the description.
// Returns '' when there is no usable first segment, so callers check for falsy.
const tourStartingPoint = (venue) => {
  const first = (venue ?? '')
    .toString()
    .replace(/<[^>]*>/g, ' ') // drop markup like <br/>
    .split(/[·•]/)[0]
    .replace(/\s+/g, ' ')
    .trim()
  // With no "·" the whole venue comes back — only treat it as a starting point if
  // it reads like a place name rather than a sentence.
  return first.length > 0 && first.length <= 60 ? first : ''
}

// A place slotted into the itinerary and the same place in the Nearby Places
// table arrive as two unrelated records with different names — e.g. the itinerary
// says "Ranchhodbhai House" while the table says "Shri Ranchod Nivas", and
// "Lady Wilson Museum" vs "Lady Wilson Museum (Shrimad Rajchandraji Jivandarshan)".
// There is no shared id to key on, so we compare names after reducing each to its
// distinctive words.
// Words shared by half the ashram's place names, so useless for telling two apart.
const PLACE_STOPWORDS = new Set([
  'shri', 'shrimad', 'rajchandra', 'raj', 'the', 'and', 'for', 'with', 'of', 'at', 'centre', 'center',
])
// Hindi/Gujarati words for "house" that the two sources use interchangeably.
const PLACE_TYPE_SYNONYMS = { nivas: 'house', ghar: 'house', bhavan: 'house' }
// Building types — two different places can share one, so a match on these alone
// means nothing ("Ranchod House" and "Wilson House" are not the same place).
const GENERIC_PLACE_WORDS = new Set(['house', 'temple', 'museum', 'hospital', 'garden', 'shop', 'hall', 'school'])

const placeNameTokens = (name) => new Set(
  (name ?? '')
    .toString()
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ') // drop parentheticals — they're alternate names
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/(.)\1+/g, '$1')) // transliteration variants: "ranchhod" -> "ranchod"
    .map((w) => w.replace(/(?:bhai|ji)$/, '')) // honorific suffixes: "ranchodbhai" -> "ranchod"
    .filter((w) => w.length > 2 && !PLACE_STOPWORDS.has(w))
    .map((w) => PLACE_TYPE_SYNONYMS[w] ?? w)
)

// Same place if they share at least one distinctive (non-building-type) word.
const isSamePlace = (a, b) => {
  const tokensB = placeNameTokens(b)
  for (const w of placeNameTokens(a)) {
    if (!GENERIC_PLACE_WORDS.has(w) && tokensB.has(w)) return true
  }
  return false
}

// Small "open in maps" link for nearby-place plan items. The map URL is taken
// from the item's `note` field (set by the planner API) and opens in a new tab.
// `compact` renders a small "Map" badge (used when the item has no venue);
// otherwise it renders just an external-link icon shown beside the venue.
const NearbyMapLink = ({ url, compact = false, label = 'Map' }) => {
  const href = (url ?? '').toString().trim()
  if (!href) return null
  if (compact) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold active:scale-95 transition-transform"
        style={{ background: 'rgba(127,119,221,0.12)', color: '#5C54A8' }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
        {label}
      </a>
    )
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex items-center gap-1 flex-shrink-0 text-[11px] font-bold active:scale-90 transition-transform"
      style={{ color: '#5C54A8' }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
      </svg>
      {label}
    </a>
  )
}

// waitMins (60) -> "1h", (90) -> "1h 30m", (30) -> "30 min" — the same label shapes
// the planner sends for durationLabel, so fmtDuration can localize them.
const minsToDurationLabel = (mins) => {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m} min`
}

// A nearby place's `needToStay` — how long to budget there. The planner may send
// minutes (number) or a label; duration labels go through fmtDuration
// (auto-translate mangles them, so the key sits in tDeep's skip list), free text
// through t(). Shared by the on-screen table and both PDF paths so the plan on
// screen, in print, and in the download always read the same.
const formatNeedToStay = (raw, t, fmtDuration) => {
  if (raw === null || raw === undefined || raw === '') return ''
  const fmt = fmtDuration ?? ((v) => v)
  const mins = Number(raw)
  if (Number.isFinite(mins) && mins > 0) return fmt(minsToDurationLabel(mins))
  const s = raw.toString().trim()
  if (/^\d+\s*h(\s*\d+\s*m)?$/i.test(s) || /^\d+\s*min(s|utes)?$/i.test(s)) return fmt(s)
  return t(s)
}

// The planner's `extra_suggestion_point`: important activities that start AFTER the
// visitor's chosen duration ends — "stay a little longer and you catch this". Same
// shape as a schedule item, plus waitMins (the gap between the plan ending and it
// starting). Deliberately accent-tinted, unlike the white itinerary cards, so it
// reads as extra time on top of the plan rather than part of it. fmtTime/fmtDuration
// are passed in because they depend on the language.
const ExtraSuggestionCard = ({ items, t, fmtTime, fmtDuration }) => {
  // The API sends a single object today; accept an array too so it can grow to
  // several suggestions without a frontend change.
  const list = (Array.isArray(items) ? items : [items]).filter((it) => it?.title)
  if (list.length === 0) return null
  // With one suggestion the wait reads best next to the heading; with several,
  // each row carries its own (see below) since they differ per activity.
  const headerWaitMins = list.length === 1 ? Number(list[0].waitMins) || 0 : 0
  const waitChip = (mins) => (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: V4.accent, color: '#FFF' }}>
      +{fmtDuration(minsToDurationLabel(mins))}
    </span>
  )
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: V4.accentBg, border: `1.5px solid ${V4.accentBorder}` }}>
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: `1px solid ${V4.accentBorder}` }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill={V4.accent} className="flex-shrink-0" aria-hidden="true">
          <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2L12 16.4l-6.3 4.6L8 13.8 2 9.4h7.6L12 2z" />
        </svg>
        <p className="flex-1" style={{ ...bold, color: V4.accent, fontSize: 12, letterSpacing: '0.04em' }}>
          {t('EXTEND YOUR VISIT')}
        </p>
        {headerWaitMins > 0 && waitChip(headerWaitMins)}
      </div>
      {list.map((item, idx) => {
        const waitMins = Number(item.waitMins) || 0
        return (
          <div
            key={`${item?.title ?? 'extra'}-${idx}`}
            className="px-4 py-3"
            style={{ borderTop: idx > 0 ? `1px solid ${V4.accentBorder}` : 'none' }}
          >
            <p className="text-sm font-bold leading-snug" style={{ color: V4.text }}>
              {(item.title ?? '').toString()}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[12px] tabular-nums" style={{ color: V4.textMuted }}>
                {fmtTime(item.startTime)} - {fmtTime(item.endTime)}
              </span>
              {item.durationLabel && (
                <span className="text-[12px]" style={{ color: V4.textMuted }}>· {fmtDuration(item.durationLabel)}</span>
              )}
              {list.length > 1 && waitMins > 0 && <span className="ml-auto">{waitChip(waitMins)}</span>}
            </div>
            {item.venue && (
              <p className="text-[12px] mt-1 leading-snug" style={{ color: V4.textDim }}>
                {(item.venue ?? '').toString()}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}
// "Nearby Places" table straight from the planner API (`plan.nearbyPlaces`).
// Shared by the hour-based and overnight itineraries — both render it just above
// the Book Stay card — so the two layouts can't drift apart.
const NearbyPlacesTable = ({ places, hideIndices, t, fmtDuration }) => {
  // Rows whose place is already in the itinerary are dropped (see
  // duplicateNearbyIndices) so the visitor isn't told twice about the same place.
  const rows = (Array.isArray(places) ? places : []).filter((_, idx) => !hideIndices?.has(idx))
  if (rows.length === 0) return null
  const stayLabel = (raw) => formatNeedToStay(raw, t, fmtDuration)
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: V4.card, border: `1px solid ${V4.border}`, boxShadow: V4.shadow }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: V4.bg, borderBottom: `1px solid ${V4.border}` }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={V4.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
        <p style={{ ...bold, color: V4.text, fontSize: 12, letterSpacing: '0.04em' }}>
          {t("Nearby Places You Shouldn't Miss") ?? "Nearby Places You Shouldn't Miss"}
        </p>
      </div>
      <div>
        {rows.map((place, pIdx) => {
          // The planner API sends the maps URL as `href`; url-ish keys are in
          // tDeep's skip list, so it stays untranslated.
          const placeMapUrl = (place?.href ?? place?.mapUrl ?? place?.link ?? '').toString().trim()
          return (
            <div
              key={`${place?.name ?? 'place'}-${pIdx}`}
              className="px-4 py-3"
              style={{ borderBottom: pIdx < rows.length - 1 ? `1px solid ${V4.border}` : 'none' }}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold leading-snug" style={{ color: V4.text }}>
                  {(place?.name ?? '').toString()}
                </p>
                {placeMapUrl && (
                  <div className="flex-shrink-0 mt-0.5">
                    <NearbyMapLink url={placeMapUrl} compact label={t('Map') ?? 'Map'} />
                  </div>
                )}
              </div>
              {place?.timing && (
                <div className="flex items-start gap-1.5 mt-1">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={V4.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                  </svg>
                  <span className="text-[12px]" style={{ color: V4.textMuted }}>{(place.timing ?? '').toString()}</span>
                </div>
              )}
              {stayLabel(place?.needToStay) && (
                <div className="flex items-center gap-1.5 mt-1">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={V4.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M6 2h12M6 22h12M6 2c0 5 6 6 6 10S6 17 6 22M18 2c0 5-6 6-6 10s6 5 6 10" />
                  </svg>
                  <span className="text-[12px] font-semibold" style={{ color: V4.accent }}>
                    {stayLabel(place.needToStay)}
                  </span>
                </div>
              )}
              {place?.note && (
                <p className="text-[11px] mt-1" style={{ color: V4.accent }}>* {(place.note ?? '').toString()}</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const EN = dashboardI18n.en

// Intro videos for the "Places to Explore Nearby" cards. Keyed by the English
// place name, lowercased with whitespace collapsed, because the API sends these
// names in mixed/upper case. Attached before tDeep() runs — matching a
// translated name would never hit.
const NEARBY_PLACE_VIDEOS = {
  'shrimad rajchandra sarvamangal centre of excellence for women': 'https://youtu.be/hQU8uFtyes0',
  'shrimad rajchandra hospital and research centre': 'https://youtu.be/xlNQS3skV8M',
}

// Photos for a nearby place's detail slider, keyed the same way. Used when the
// WordPress post carries no ACF images of its own, so uploading them in WP later
// takes over without a code change.
const NEARBY_PLACE_IMAGES = {
  'shrimad rajchandraji jivandarshan at lady wilson museum': [
    jivandarshanMuseum1,
    jivandarshanMuseum2,
  ],
}

// youtu.be/ID, watch?v=ID and /shorts/ID all have to become /embed/ID before an
// iframe will play them. Returns '' for anything unrecognised so the caller can
// simply skip rendering the player.
const toYouTubeEmbedUrl = (url) => {
  const raw = (url ?? '').toString().trim()
  if (!raw) return ''
  const id = raw.match(/(?:youtu\.be\/|\/embed\/|\/shorts\/|[?&]v=)([A-Za-z0-9_-]{6,})/)?.[1]
  if (!id) return ''
  // rel=0 keeps the end screen to this channel; playsinline stops iOS going fullscreen.
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1`
}


// Buggy Finder: normalize the various response shapes the VMS proxy may return
// into a plain array. Handles { data: [...] }, { isSuccess, data }, a raw array,
// and the store-proc style { Success, response: "<json string>" } wrapper.
const unwrapBuggyData = (json) => {
  if (Array.isArray(json)) return json
  if (Array.isArray(json?.data)) return json.data
  if (typeof json?.response === 'string') {
    try {
      const parsed = JSON.parse(json.response)
      return Array.isArray(parsed?.data) ? parsed.data : (Array.isArray(parsed) ? parsed : [])
    } catch { return [] }
  }
  if (Array.isArray(json?.data?.data)) return json.data.data
  return []
}

// Buggy Finder stop order: these five lead the dropdown, in this order, and
// every other stop follows in whatever order the API sent. Matching is loose —
// case, spacing/punctuation and the "Center"/"Centre" spelling are ignored, and
// a prefix counts ("Upper Dining Hall" still ranks as Upper Dining) — so a small
// rename on the VMS side doesn't silently drop a stop back into the tail.
const PRIORITY_BUGGY_STOPS = ['Welcome Centre', 'Raj Darbar', 'Jinmandir', 'Raj Sabhagruh', 'Upper Dining']
const normalizeStopName = (s) => (s ?? '').toString().toLowerCase()
  .replace(/center/g, 'centre')
  .replace(/[^a-z0-9]/g, '')
const PRIORITY_BUGGY_STOP_KEYS = PRIORITY_BUGGY_STOPS.map(normalizeStopName)
const buggyStopRank = (name) => {
  const n = normalizeStopName(name)
  const idx = PRIORITY_BUGGY_STOP_KEYS.findIndex((key) => n.startsWith(key))
  return idx === -1 ? PRIORITY_BUGGY_STOP_KEYS.length : idx
}
// Sorted by rank, ties broken by original index so the API's order survives
// within each group (Array.prototype.sort is only stable per spec since ES2019).
const sortBuggyStops = (stops) => (Array.isArray(stops) ? stops : [])
  .map((name, i) => ({ name, i, rank: buggyStopRank(name) }))
  .sort((a, b) => a.rank - b.rank || a.i - b.i)
  .map((entry) => entry.name)



// Custom dropdown (for iOS custom time picker)
// NOTE: Native <select> dropdown menu height is not reliably styleable across browsers.
// We use a fully controlled popover list with fixed max height + scroll.
const TimeDropdown = ({
  ariaLabel,
  value,
  items,
  onChange,
  buttonClassName = '',
  buttonStyle = {},
  menuMaxHeight = 250,
  menuZIndex = 60,
  selectedLabelOverride,
}) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const menuRef = useRef(null)
  const optionRefs = useRef({})
  const didPositionMenu = useRef(false)

  useEffect(() => {
    if (!open) return
    const onDocDown = (e) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    document.addEventListener('touchstart', onDocDown)
    return () => {
      document.removeEventListener('mousedown', onDocDown)
      document.removeEventListener('touchstart', onDocDown)
    }
  }, [open])

  // On open, scroll the popover to the selected slot instead of starting at the
  // first one. Without this, a 4 PM arrival opens the list at 08:00 am and the
  // visitor has to scroll down to see their own time and the ones after it.
  // Only the popover's own scrollTop is touched (never the page).
  // Positions once per open so a re-render (the `items` array is rebuilt each
  // render at some call sites) never yanks the list back while the user scrolls.
  useLayoutEffect(() => {
    if (!open) {
      didPositionMenu.current = false
      return
    }
    if (didPositionMenu.current) return
    const menu = menuRef.current
    if (!menu) return
    const list = items ?? []
    const match = list.find((x) => String(x.value) === String(value))
      ?? list.find((x) => String(x.value) >= String(value))
    const opt = match ? optionRefs.current[String(match.value)] : null
    if (!opt) return
    // Sit the selected row a little above centre so the following slots
    // (4:30 pm, 5 pm, ...) are visible without scrolling.
    const target = opt.offsetTop - Math.max(0, (menu.clientHeight - opt.offsetHeight) * 0.35)
    menu.scrollTop = Math.max(0, Math.min(target, menu.scrollHeight - menu.clientHeight))
    didPositionMenu.current = true
  }, [open, items, value])

  // The closed-button display can be overridden (e.g. "Post 6 PM" shows the
  // resolved current time) while the list keeps each item's own label.
  const selectedLabel = (selectedLabelOverride ?? null) != null && String(selectedLabelOverride) !== ''
    ? selectedLabelOverride
    : ((items ?? []).find((x) => String(x.value) === String(value))?.label ?? value)
  
  return (
    <div ref={rootRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`w-full py-3 px-3 rounded-xl text-sm font-semibold outline-none flex items-center justify-between gap-2 ${buttonClassName}`}
        style={buttonStyle}
      >
        <span className="truncate">{selectedLabel}</span>
        <svg
          className="w-4 h-4 flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ opacity: 0.55 }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 right-0 mt-2 rounded-xl overflow-auto shadow-xl"
          style={{
            maxHeight: menuMaxHeight,
            background: V4.card,
            border: `1px solid ${V4.border}`,
            zIndex: menuZIndex,
          }}
        >
          {(items ?? []).map((it, idx) => {
            const active = String(it.value) === String(value)
            return (
              <button
                key={String(it.value)}
                ref={(el) => {
                  if (el) optionRefs.current[String(it.value)] = el
                  else delete optionRefs.current[String(it.value)]
                }}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange?.(it.value)
                  setOpen(false)
                }}
                className="w-full text-left px-3 py-2.5 text-sm font-semibold"
                style={{
                  background: active ? V4.accentBg : 'transparent',
                  color: active ? V4.accentDark : V4.text,
                  borderBottom: idx === (items?.length ?? 0) - 1 ? 'none' : `1px solid ${V4.border}`,
                }}
              >
                {it.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Horizontal strip with edge arrows.
// The buggy-stops row scrolls sideways, but the scrollbar is hidden on mobile so
// nothing signalled that more stops existed off-screen. An arrow is rendered
// only on a side that still has content to reveal, over a short fade so the
// clipped label reads as "continues".
const ScrollableStrip = ({ children, className = '', style = {}, wrapperClassName = '', ariaLabel }) => {
  const scrollRef = useRef(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  const syncEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    // 4px slack: sub-pixel widths otherwise leave an arrow stuck on at the end.
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft < max - 4 })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined
    syncEdges()
    el.addEventListener('scroll', syncEdges, { passive: true })
    window.addEventListener('resize', syncEdges)
    // Stops arrive from the API after mount, so re-measure when the size changes.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncEdges) : null
    if (ro) {
      ro.observe(el)
      if (el.firstElementChild) ro.observe(el.firstElementChild)
    }
    return () => {
      el.removeEventListener('scroll', syncEdges)
      window.removeEventListener('resize', syncEdges)
      if (ro) ro.disconnect()
    }
  }, [syncEdges])

  // The items to step through. Most strips wrap them in a single flex row.
  const itemNodes = (el) => {
    const kids = Array.from(el.children)
    if (kids.length === 1 && kids[0].children.length > 1) return Array.from(kids[0].children)
    return kids
  }

  // One arrow tap = exactly one item. Positions are measured in the container's
  // scroll coordinates (offsetLeft can't be used — the positioned wrapper, not
  // the scroll content, is the items' offsetParent).
  const nudge = (dir) => {
    const el = scrollRef.current
    if (!el) return
    const max = Math.max(0, el.scrollWidth - el.clientWidth)
    const items = itemNodes(el)
    if (items.length < 2) {
      el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.7), behavior: 'smooth' })
      return
    }
    const base = el.getBoundingClientRect().left - el.scrollLeft
    const positions = items.map((n) => n.getBoundingClientRect().left - base)
    // Offset by the first item's resting position so leading padding/margin is
    // preserved — each target lands an item exactly where item 0 sits at rest.
    const home = positions[0]
    const targets = positions.map((p) => p - home)
    const cur = el.scrollLeft
    // 1px slack absorbs fractional scroll positions after a smooth scroll.
    const next = dir > 0
      ? targets.find((x) => x > cur + 1)
      : targets.slice().reverse().find((x) => x < cur - 1)
    const fallback = dir > 0 ? max : 0
    el.scrollTo({ left: Math.max(0, Math.min(next ?? fallback, max)), behavior: 'smooth' })
  }

  // Straddles the strip's border — half outside, half inside — so it never sits
  // on top of a stop label. `transform` is inline, so press feedback uses
  // opacity (a Tailwind scale utility would be overridden by it).
  const arrow = (side) => (
    <button
      type="button"
      aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={() => nudge(side === 'left' ? -1 : 1)}
      className="absolute top-1/2 w-6 h-6  border-black rounded-full flex items-center justify-center active:opacity-70 transition-opacity"
      style={{
        zIndex: 2,
        [side]: 0,
        transform: `translate(${side === 'left' ? '-50%' : '50%'}, -50%)`,
        background: V4.card,
        border: `1px solid ${V4.accent}`,
        boxShadow: V4.shadowMd,
        color: V4.text,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d={side === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
      </svg>
    </button>
  )

  return (
    <div className={`relative ${wrapperClassName}`}>
      <div ref={scrollRef} className={className} style={style} role="group" aria-label={ariaLabel}>
        {children}
      </div>
      {edges.left && arrow('left')}
      {edges.right && arrow('right')}
    </div>
  )
}

// Simple drag/swipe slider (no external deps)
// - Works with mouse drag + touch swipe (pointer events)
// - Uses native horizontal scrolling + snap
// - Keeps index in sync with scroll position
const SliderPrevArrow = ({ onClick }) => (
  <button
    type="button"
    aria-label="Previous slide"
    onClick={onClick}
    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
    style={{
      zIndex: 2,
      background: 'rgba(0,0,0,0.35)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.35)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
    }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  </button>
)

const SliderNextArrow = ({ onClick }) => (
  <button
    type="button"
    aria-label="Next slide"
    onClick={onClick}
    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
    style={{
      zIndex: 2,
      background: 'rgba(0,0,0,0.35)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.35)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
    }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  </button>
)

const DragSnapSlider = ({
  images = [],
  title = '',
  index = 0,
  onIndexChange,
  cardStyle = {},
  imgClassName = 'w-full h-auto object-cover',
  showArrows = false,
  autoPlay = false,
  autoPlayInterval = 3500,
}) => {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  )
  const sliderRef = useRef(null)
  const [activeIdx, setActiveIdx] = useState(() =>
    Math.min(Math.max(0, index), Math.max(0, safeImages.length - 1))
  )

  useEffect(() => {
    if (!sliderRef.current || safeImages.length === 0) return
    const clamped = Math.min(Math.max(0, index), Math.max(0, safeImages.length - 1))
    if (clamped === activeIdx) return
    sliderRef.current.slickGoTo(clamped)
  }, [index, safeImages.length, activeIdx])

  if (safeImages.length === 0) return null

  const settings = {
    dots: safeImages.length > 1,
    arrows: showArrows && safeImages.length > 1,
    infinite: safeImages.length > 1,
    speed: 700,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: autoPlay && safeImages.length > 1,
    autoplaySpeed: Math.max(1200, Number(autoPlayInterval) || 3500),
    pauseOnHover: false,
    pauseOnFocus: false,
    swipe: true,
    swipeToSlide: true,
    touchThreshold: 10,
    draggable: true,
    adaptiveHeight: true,
    useTransform: true,
    useCSS: true,
    waitForAnimate: false,
    initialSlide: Math.min(Math.max(0, index), Math.max(0, safeImages.length - 1)),
    beforeChange: (_, newIdx) => {
      setActiveIdx(newIdx)
      onIndexChange?.(newIdx)
    },
    prevArrow: <SliderPrevArrow />,
    nextArrow: <SliderNextArrow />,
    appendDots: (dots) => (
      <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0 }}>
        <ul
          className="flex items-center justify-center gap-2"
          style={{ listStyle: 'none', margin: 0, padding: 0 }}
        >
          {dots}
        </ul>
      </div>
    ),
    customPaging: (i) => (
      <span
        aria-hidden
        className="block"
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: i === activeIdx ? '#FFF' : 'rgba(255,255,255,0.55)',
          border: i === activeIdx
            ? '1px solid rgba(255,255,255,0.9)'
            : '1px solid rgba(255,255,255,0.4)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        }}
      />
    ),
  }

  return (
    <div className="overflow-hidden relative drag-snap-slider" style={cardStyle}>
      <Slider ref={sliderRef} {...settings}>
        {safeImages.map((src, i) => (
          // <div key={`${src}-${i}`}>
          <img
            key={`${src}-${i}`}
            src={src}
            alt={title}
            className={imgClassName}
            draggable={false}
          />
          // </div>
        ))}
      </Slider>
    </div>
  )
}

const DashBoardData = () => {
  const { passId } = useParams();
  const navigate = useNavigate()
  const [visitors, setVisitors] = useState([])
  const [showQR, setShowQR] = useState(false)
  const [activeVisitorIndex, setActiveVisitorIndex] = useState(0)
  const [activeLink, setActiveLink] = useState(null)
  const [planTime, setPlanTime] = useState(null)
  const [planDurationValue, setPlanDurationValue] = useState('')
  const [isPlanning, setIsPlanning] = useState(false)
  const [endOfDayMin, setEndOfDayMin] = useState(0)
  // Ashram check-in / checkout times for overnight stays, as "HH:MM" (24h). Come
  // from the planner master-data config keys OVERNIGHT_CHECKIN_TIME_IN_24_HRS and
  // OVERNIGHT_CHECKOUT_TIME_IN_24_HRS.
  const [overnightCheckinTime, setOvernightCheckinTime] = useState(OVERNIGHT_CHECKIN_FALLBACK)
  const [overnightCheckoutTime, setOvernightCheckoutTime] = useState('')
  const [plannerDurationOptions, setPlannerDurationOptions] = useState([])

  const [BuggyData, setBuggyData] = useState([]);
  const [plan, setPlan] = useState(null)
  const [lang, setLang] = useState('en')
  const { t, tDeep } = useTranslate(lang)
  // Translated view of the API plan (plan-a-visit). tDeep walks the plan tree
  // and translates display text (static → dictionary, dynamic → translate
  // package), leaving urls/times/numbers/html untouched. Recomputed each
  // render so it picks up translations as they stream in.
  const planT = tDeep(plan)
  // Rows of plan.nearbyPlaces that the itinerary already covers as a `nearby`
  // item, so the Nearby Places table can skip them. Matched against the RAW plan
  // because the translated copy has no Latin words left for isSamePlace to
  // compare; tDeep preserves array order, so these indices line up with planT.
  const duplicateNearbyIndices = useMemo(() => {
    const scheduledNames = [
      ...(Array.isArray(plan?.schedule) ? plan.schedule : []),
      ...(Array.isArray(plan?.sections)
        ? plan.sections.flatMap((s) => (Array.isArray(s?.items) ? s.items : []))
        : []),
    ]
      .filter((it) => it?.badge === 'nearby')
      .map((it) => (it?.title ?? '').toString())
      .filter(Boolean)
    const dupes = new Set()
    if (scheduledNames.length === 0) return dupes
    ;(Array.isArray(plan?.nearbyPlaces) ? plan.nearbyPlaces : []).forEach((place, idx) => {
      if (scheduledNames.some((name) => isSamePlace(name, place?.name))) dupes.add(idx)
    })
    return dupes
  }, [plan])
  // const [loginApi, setLoginApi] = useState(null)
  const [tourHistoryList, setTourHistoryList] = useState([])
  const [showBuggyModal, setShowBuggyModal] = useState(false)
  const [showRouteMapModal, setShowRouteMapModal] = useState(false)
  const [buggyQty, setBuggyQty] = useState(0)
  // Same behavior as old DashBoard.jsx: shake animation when user tries invalid +/-
  const [shake, setShake] = useState(false)
  const [buggyStopsData, setBuggyStopsData] = useState([])
  const [activeBuggyStopIndex, setActiveBuggyStopIndex] = useState(null)
  const [routeMapStopsData] = useState([])
  const [isRouteMapLoading] = useState(false)
  // ─── Buggy Finder (live route timings by stop) ───
  const [showBuggyFinder, setShowBuggyFinder] = useState(false)
  const [buggyFinderStops, setBuggyFinderStops] = useState([])
  const [buggyFinderSelectedStop, setBuggyFinderSelectedStop] = useState('Welcome Centre')
  const [buggyFinderRoutes, setBuggyFinderRoutes] = useState([])
  const [buggyFinderLoading, setBuggyFinderLoading] = useState(false)
  // Route stops bottom-sheet: { title, color, stops:[], mapUrl }
  const [showBuggyRoute, setShowBuggyRoute] = useState(false)
  const [buggyRouteData, setBuggyRouteData] = useState(null)
  // Walking distances bottom-sheet (from selected stop to nearby spots)
  const [showBuggyWalking, setShowBuggyWalking] = useState(false)
  const [buggyWalkingLoading, setBuggyWalkingLoading] = useState(false)
  const [buggyWalkingFrom, setBuggyWalkingFrom] = useState('')
  const [buggyWalkingItems, setBuggyWalkingItems] = useState([])
  // Ride stops bottom-sheet (timeline of time + stop for a single ride)
  const [showBuggyRide, setShowBuggyRide] = useState(false)
  const [buggyRideLoading, setBuggyRideLoading] = useState(false)
  const [buggyRideTitle, setBuggyRideTitle] = useState('')
  const [buggyRideColor, setBuggyRideColor] = useState(null)
  const [buggyRideStops, setBuggyRideStops] = useState([])
  // Full Schedule bottom-sheet (Morning / Afternoon / Evening)
  const [showBuggyFullSchedule, setShowBuggyFullSchedule] = useState(false)
  const [buggyFullScheduleLoading, setBuggyFullScheduleLoading] = useState(false)
  // Sections straight from the API: [{ TimeOfDay, TimeRange, BuggyTimings }]
  const [buggyFullScheduleSections, setBuggyFullScheduleSections] = useState([])
  const [fullScheduleSection, setFullScheduleSection] = useState('Morning') // open section's TimeOfDay (or null)
  const [showMealModal, setShowMealModal] = useState(false)
  const [mealQty, setMealQty] = useState({ breakfast: 0, veganBreakfast: 0, lunch: 0, veganLunch: 0, dinner: 0, veganDinner: 0, [dayPassKey]: 0 })
  // Same behavior as old DashBoard.jsx: shake per meal on invalid +/-
  const [mealshake, setMealshake] = useState({
    breakfast: false,
    veganBreakfast: false,
    lunch: false,
    veganLunch: false,
    dinner: false,
    veganDinner: false,
    [dayPassKey]: false,
  })
  const [mealsData, setMealsData] = useState([])
  const [mealPrintTimes, setMealPrintTimes] = useState([]) // from /print API (printFormat)
  const [timeEnd, setTimeEnd] = useState([])
  const [mealShows, setMealShows] = useState({})
  const [secIsLoading, setSecIsLoading] = useState(false)
  const [showCouponsView, setShowCouponsView] = useState(false)
  const [couponFilter, setCouponFilter] = useState('all') // 'all' | 'meals' | 'buggy'
  const touchStartX = useRef(null)
  const [transactionHistoryData, setTransactionHistoryData] = useState([])
  const [scanningHistoryData, setScanningHistoryData] = useState([])
  const [isTxnLoading, setIsTxnLoading] = useState(false)
  const [isScanLoading, setIsScanLoading] = useState(false)
  
  // Map legend modal (opened from in-content //map anchor links)
  const [showMapLegendModal, setShowMapLegendModal] = useState(false)
  // Guided-tour timings popup, opened from the info icon on the plan's Ashram tour row
  const [showTourTimesModal, setShowTourTimesModal] = useState(false)

  // Related Info (WP categories) modal
  const [showRelatedInfoModal, setShowRelatedInfoModal] = useState(false)
  const [showAshramCultureModal, setShowAshramCultureModal] = useState(false)
  const [relatedInfoCategories, setRelatedInfoCategories] = useState([])
  const [isRelatedInfoLoading, setIsRelatedInfoLoading] = useState(false)
  const [relatedInfoError, setRelatedInfoError] = useState('')
  const [relatedInfoQuery, setRelatedInfoQuery] = useState('')
  const [relatedInfoSelectedCategory, setRelatedInfoSelectedCategory] = useState(null) // {id,name,image,detailImage}
  const [relatedInfoItems, setRelatedInfoItems] = useState([])
  const [isRelatedInfoItemsLoading, setIsRelatedInfoItemsLoading] = useState(false)
  const [relatedInfoItemsError, setRelatedInfoItemsError] = useState('')
  const [expandedRelatedInfoItemId, setExpandedRelatedInfoItemId] = useState(null)
  const [relatedInfoCarouselIndex, setRelatedInfoCarouselIndex] = useState(0)
  const [relatedInfoItemCarouselIndexById, setRelatedInfoItemCarouselIndexById] = useState({})

  // Arrival time is stored as HH:MM (24h) for API payload.
  // UI shows a single dropdown with 30-min slots from 08:00 to 18:00.
  const [arrivalTime, setArrivalTime] = useState(() => {
    const d = new Date()
    const mins = d.getHours() * 60 + d.getMinutes()
    // After 6 PM: default to the "Post 6 PM" option so the dropdown shows the
    // visitor's current time instead of snapping back to 06:00 PM.
    if (mins > ARRIVAL_MAX_MINUTES) return POST_6PM_VALUE
    // snap down to a 30-min boundary and clamp to allowed window
    const clamped = Math.min(ARRIVAL_MAX_MINUTES, Math.max(ARRIVAL_MIN_MINUTES, mins))
    const snapped = Math.floor(clamped / ARRIVAL_STEP_MINUTES) * ARRIVAL_STEP_MINUTES
    const h24 = Math.floor(snapped / 60)
    const mm = snapped % 60
    return `${String(h24).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
  })

  const clampArrivalMinutes = useCallback((mins) => {
    const n = Number(mins)
    if (!Number.isFinite(n)) return ARRIVAL_MIN_MINUTES
    // Allow the special post-6PM sentinel minute as a valid selection.
    return Math.min(POST_6PM_MINUTES, Math.max(ARRIVAL_MIN_MINUTES, n))
  }, [])

  const snapArrivalMinutes = useCallback((mins) => {
    const safe = clampArrivalMinutes(mins)
    // Keep the post-6PM sentinel as-is (do not snap it down to 18:00).
    if (safe > ARRIVAL_MAX_MINUTES) return POST_6PM_MINUTES
    // snap down to nearest 30 min step (ensures only valid dropdown values)
    const snapped = Math.floor(safe / ARRIVAL_STEP_MINUTES) * ARRIVAL_STEP_MINUTES
    return clampArrivalMinutes(snapped)
  }, [clampArrivalMinutes])

  const minutesToHHMM = useCallback((mins) => {
    const safe = snapArrivalMinutes(mins)
    if (safe === POST_6PM_MINUTES) return POST_6PM_VALUE
    const h24 = Math.floor(safe / 60)
    const mm = safe % 60
    return `${String(h24).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
  }, [snapArrivalMinutes])

  const hhmmToMinutes = useCallback((value) => {
    const raw = (value ?? '').toString().trim()
    const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (!match24) return null
    const h24 = Math.min(23, Math.max(0, Number(match24[1]) || 0))
    const mm = Math.min(59, Math.max(0, Number(match24[2]) || 0))
    return h24 * 60 + mm
  }, [])

  const formatMinutesTo12hLabel = useCallback((mins) => {
    const safe = clampArrivalMinutes(mins)
    if (safe === POST_6PM_MINUTES) return POST_6PM_LABEL
    const h24 = Math.floor(safe / 60)
    const mm = safe % 60
    const period = h24 >= 12 ? 'pm' : 'am'
    const h12 = (h24 % 12) || 12
    return `${String(h12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${period}`
  }, [clampArrivalMinutes])

  const arrivalSlotOptions = useMemo(() => {
    const out = []
    for (let m = ARRIVAL_MIN_MINUTES; m <= ARRIVAL_MAX_MINUTES; m += ARRIVAL_STEP_MINUTES) {
      out.push({
        value: minutesToHHMM(m),
        label: formatMinutesTo12hLabel(m),
      })
    }
    // The dropdown LIST always shows "Post 6 PM". Only the selected display
    // (closed dropdown + "Selected:" label) resolves to the current time.
    out.push({
      value: POST_6PM_VALUE,
      label: POST_6PM_LABEL,
    })
    return out
  }, [minutesToHHMM, formatMinutesTo12hLabel])

  // Ensure the stored arrivalTime string is also clamped (so planner API always
  // receives a valid HH:MM within the window).
  useEffect(() => {
    const raw = (arrivalTime ?? '').toString().trim()
    // Special-case: keep "post 6 PM" stable.
    if (raw === POST_6PM_VALUE) return
    const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (!match24) return
    const h24 = Math.min(23, Math.max(0, Number(match24[1]) || 0))
    const mm = Math.min(59, Math.max(0, Number(match24[2]) || 0))
    const next = minutesToHHMM(h24 * 60 + mm)
    if (next !== `${String(h24).padStart(2, '0')}:${String(mm).padStart(2, '0')}`) {
      setArrivalTime(next)
      setPlan(null)
    }
  }, [arrivalTime, minutesToHHMM])

  const arrivalTimeLabel = useMemo(() => {
    // "Post 6 PM": show the *current* clock time snapped down to the nearest
    // 30-min figure, instead of the generic "Post 6 PM" label.
    if ((arrivalTime ?? '').toString().trim() === POST_6PM_VALUE) {
      return minsTo12hLabel(currentSnappedArrivalMinutes())
    }
    const mins = hhmmToMinutes(arrivalTime)
    if (mins == null) return ''
    return formatMinutesTo12hLabel(mins)
  }, [arrivalTime, hhmmToMinutes, formatMinutesTo12hLabel])
  // Explore spaces/cards data (from external WP API)
  const [spacesFromApi, setSpacesFromApi] = useState([])

  // Nearby places data (from WP API - category 81)
  const [nearbyPlacesFromApi, setNearbyPlacesFromApi] = useState([])
  const [ashramcultureDetails, setAshramcultureDetails] = useState([])

  // Explore details sheet (for a selected space)
  const [showExploreDetails, setShowExploreDetails] = useState(false)
  const [selectedExplorePost, setSelectedExplorePost] = useState(null) // full WP post (title,categories,acf,...)
  const [exploreGalleryIndex, setExploreGalleryIndex] = useState(0)
  const [exploreFeatureCarouselIndexByKey, setExploreFeatureCarouselIndexByKey] = useState({})
  const [nearByDetails, openNearByDetails] = useState({})

  const formatDateTime = (isoString) => {
    const date = new Date(isoString)
    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: 'UTC',
    }
    return date.toLocaleString('en-GB', options)
  }

  const groupId = visitors?.[0]?.GroupId

  console.log(groupId, ">>>groupId")
  const activeVisitor = useMemo(
    () => visitors?.[activeVisitorIndex] ?? visitors?.[0],
    [visitors, activeVisitorIndex]
  )

  // A visitor "has a pass" only when we have visitor/group data loaded.
  const hasPass = visitors.length > 0 && !!activeVisitor

  // Buy meal / buy pass flows need a GroupId. Without a pass, prompt the
  // user to register first instead of opening the purchase modal.
  const openMealModal = useCallback(() => {
    if (!hasPass) {
      toast.error(t("Please get your pass first") ?? 'Please get your pass first')
      return
    }
    setShowMealModal(true)
    setSecIsLoading(false)
  }, [hasPass, t])

  const openBuggyModal = useCallback(() => {
    if (!hasPass) {
      toast.error(t("Please get your pass first") ?? 'Please get your pass first')
      return
    }
    setShowBuggyModal(true)
    setSecIsLoading(false)
  }, [hasPass, t])

  // Day Pass key can be overridden by i18n; keep it available early
  // so handlers can safely reference it.
  const mealDayPass = tDeep(EN.mealDayPass)
  const mealDayPassKey = mealDayPass?.key ?? dayPassKey

  const maxSelectableQty = useMemo(() => (visitors?.length ?? 0), [visitors])

  const triggerShake = useCallback(() => {
    setShake(true)
    window.setTimeout(() => setShake(false), 500)
  }, [])

  const triggerMealShake = useCallback((key) => {
    setMealshake((prev) => ({ ...prev, [key]: true }))
    window.setTimeout(() => {
      setMealshake((prev) => ({ ...prev, [key]: false }))
    }, 500)
  }, [])

  // Buggy +/- with same constraints as old dashboard: 0..number of visitors
  const handleBuggyDecrement = useCallback(() => {
    setBuggyQty((q) => {
      if (q > 0) return q - 1
      triggerShake()
      return q
    })
  }, [triggerShake])

  const handleBuggyIncrement = useCallback(() => {
    setBuggyQty((q) => {
      if (maxSelectableQty > 0 && q < maxSelectableQty) return q + 1
      triggerShake()
      return q
    })
  }, [maxSelectableQty, triggerShake])

  // Meal +/- with same constraints as old dashboard: 0..number of visitors
  // NOTE: day pass qty also consumes slots for breakfast/lunch/dinner.
  const handleMealDecrement = useCallback((key) => {
    setMealQty((prev) => {
      const current = prev?.[key] ?? 0
      if (current > 0) return { ...prev, [key]: Math.max(current - 1, 0) }
      triggerMealShake(key)
      return prev
    })
  }, [triggerMealShake])

  const handleMealIncrement = useCallback((key) => {
    setMealQty((prev) => {
      const current = prev?.[key] ?? 0
      if (maxSelectableQty <= 0) {
        triggerMealShake(key)
        return prev
      }

      const dayPassQty = prev?.[mealDayPassKey] ?? 0

      // For the day pass, just cap at maxSelectableQty.
      if (key === mealDayPassKey) {
        if (current < maxSelectableQty) return { ...prev, [key]: current + 1 }
        triggerMealShake(key)
        return prev
      }

      // For individual meal, total (dayPass + individual) must not exceed maxSelectableQty.
      const totalForThisMeal = dayPassQty + current
      if (totalForThisMeal < maxSelectableQty) return { ...prev, [key]: current + 1 }

      triggerMealShake(key)
      return prev
    })
  }, [maxSelectableQty, triggerMealShake, mealDayPassKey])


  // Fetch planner config (time options for "Plan your visit")
//   useEffect(() => {
//     // Note (DEV): React.StrictMode mounts/unmounts components twice.
//     // If we abort on cleanup, DevTools shows first request as "Canceled".
//     // That's harmless but confusing, so we avoid aborting and instead guard setState.
//     let alive = true

//     const fetchPlannerConfig = async () => {
//       try {
//         translate.engine = "google";
// const result = await translate("how are you ?", "hi");
// console.log(result); // कैसे हो?
//         // Use same base as other VMS endpoints via env (helps for DEV/PROD + avoids hardcoding)
//         const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
//         if (!base) return

//         const res = await fetch(`${base}/planner/config`, {
//           method: 'GET',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         })

//         if (!res.ok) return
//         const json = await res.json()

        
//       } catch (e) {
//         // Silent fail: we will fall back to hardcoded defaults
//         // Ignore abort-like errors (in case browser cancels due to navigation)
//         if (e?.name !== 'AbortError') console.log(e)
//       }
//     }

//     fetchPlannerConfig()
//     return () => {
//       alive = false
//     }
//   }, [])

  useEffect(() => {
    const sanitizeWpText = (value) => {
      const raw = (value ?? '').toString()
      return raw
        .replace(/<[^>]*>/g, '')
        .replace(/&/g, '&')
        .replace(/"/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/'/g, "'")
        .replace(/</g, '<')
        .replace(/>/g, '>')
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
        .trim()
    }

    const apiUrl = `${process.env.REACT_APP_NEXT_ASHRAM_APP_URL}/wp-json/wp/v2/posts?&_fields=title,categories,acf&categories=52,&per_page=100&status=publish`

    const controller = new AbortController()

    const fetchExploreCards = async () => {
      try {
        const res = await fetch(apiUrl, { signal: controller.signal })
        if (!res.ok) throw new Error(`Spaces API failed: ${res.status}`)

        const json = await res.json()
        const posts = Array.isArray(json) ? json : []

        const excludedSpaceTitleMatchers = [
          /^\s*amenities\s*$/i,
          /^\s*welcome\s*centre\s*$/i,
          /^\s*dining\s*complex\s*$/i,
        ]

        const isExcludedSpaceTitle = (name) => {
          const n = (name ?? '').toString().trim()
          if (!n) return false
          return excludedSpaceTitleMatchers.some((re) => re.test(n))
        }

        const mappedAll = posts
          .map((post) => {
            const name = sanitizeWpText(post?.title?.rendered)
            const img = post?.acf?.slider_gallery?.[0] ?? post?.acf?.vertical_gallery?.[0] ?? ''
            if (!name) return null
            if (isExcludedSpaceTitle(name)) return null
            return { name, img, featured: false, post }
          })
          .filter(Boolean)

        const usedSpaceNames = new Set()
        const pickOne = (predicate) => {
          const item = mappedAll.find((x) => !usedSpaceNames.has(x.name) && predicate(x.name))
          if (item) usedSpaceNames.add(item.name)
          return item
        }

        // Keeps your current layout positions meaningful by picking known titles first
        const chosenSpaces = [
          pickOne((n) => /jinmandir/i.test(n)),
          pickOne((n) => /Gurumandir/i.test(n)),
          pickOne((n) => /raj\s*darbar/i.test(n)),
          pickOne((n) => /raj\s*sabhagruh/i.test(n)),
          pickOne((n) => /meditation\s*hall/i.test(n)),
          // Intentionally excluded from Explore section (per requirement):
          // - DINING COMPLEX
          // - WELCOME CENTRE
          pickOne((n) => /garden/i.test(n)),
          pickOne((n) => /library/i.test(n)),
          pickOne((n) => /divine\s*shop/i.test(n)),
        ].filter(Boolean)

        const remainingForSpaces = mappedAll.filter((x) => !usedSpaceNames.has(x.name))
        const finalSpaces = [...chosenSpaces, ...remainingForSpaces].map((x, idx) => ({
          ...x,
          featured: idx === 0 || /jinmandir/i.test(x.name),
        }))
        if (finalSpaces.length) setSpacesFromApi(finalSpaces)
      } catch (error) {
        console.log(error)
      }
    }

    fetchExploreCards()
    return () => controller.abort()
  }, [])
   const transformRelatedInfoHtml = useCallback((html) => {
    // Strip CR/LF line breaks and wide-space entities WordPress often emits;
    // they surface as stray gaps after dangerouslySetInnerHTML.
    const raw = (html ?? '')
      .toString()
      .replace(/\r\n?|\n/g, '')
      .replace(/&emsp;|&ensp;/gi, ' ')
    if (!raw) return ''

    const cleanupSpacing = (root) => {
      // Collapse runs of consecutive <br> (allowing whitespace between) to one
      root.querySelectorAll('br').forEach((br) => {
        let next = br.nextSibling
        while (
          next &&
          ((next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) ||
            (next.nodeType === Node.ELEMENT_NODE && next.tagName === 'BR'))
        ) {
          const toRemove = next
          next = next.nextSibling
          if (toRemove.nodeType === Node.ELEMENT_NODE && toRemove.tagName === 'BR') {
            toRemove.remove()
          } else {
            toRemove.remove()
          }
        }
      })

      // Drop empty paragraphs / paragraphs containing only <br> or whitespace
      root.querySelectorAll('p').forEach((p) => {
        const onlyBrs = Array.from(p.childNodes).every(
          (n) =>
            (n.nodeType === Node.TEXT_NODE && !n.textContent.trim()) ||
            (n.nodeType === Node.ELEMENT_NODE && n.tagName === 'BR')
        )
        if (onlyBrs) p.remove()
      })

      // Trim leading/trailing <br> inside the body
      while (root.firstChild && root.firstChild.nodeType === Node.ELEMENT_NODE && root.firstChild.tagName === 'BR') {
        root.firstChild.remove()
      }
      while (root.lastChild && root.lastChild.nodeType === Node.ELEMENT_NODE && root.lastChild.tagName === 'BR') {
        root.lastChild.remove()
      }
    }

    // Remove specific marketing / CTA phrases that should not be shown inside
    // the Related Info HTML content.
    // Requirement: if content contains e.g. "Purchase ePass" / "Buggy Finder",
    // it should not be displayed.
    const stripForbiddenPhrases = (root) => {
      const forbidden = [
        /\bpurchase\s*epass\b/i,
        /\bbuggy\s*finder\b/i,
        /\bPujya Gurudevshri’s Schedule\b/i,
        /\bRegister Online\b/i
      ]

      const matchesForbidden = (text) => {
        const t = (text ?? '').toString().replace(/\s+/g, ' ').trim()
        if (!t) return false
        return forbidden.some((re) => re.test(t))
      }

      // Remove block-ish nodes (p/li/a/custom button tags) that contain forbidden text.
      // We intentionally remove the whole block to avoid leaving stray punctuation.
      const candidates = root.querySelectorAll('p, li, a, srmdbtn')
      candidates.forEach((node) => {
        const txt = node.textContent
        if (!matchesForbidden(txt)) return

        // If it's a link inside a larger paragraph/list item, remove the parent block.
        const block = node.closest('p, li') || node
        block.remove()
      })

      // Remove any now-empty lists.
      root.querySelectorAll('ul, ol').forEach((list) => {
        const hasItems = Array.from(list.querySelectorAll('li')).some((li) => li.textContent?.trim())
        if (!hasItems) list.remove()
      })
    }

    try {
      // DOMParser is available in browser runtime
      const doc = new DOMParser().parseFromString(raw, 'text/html')

      // Style tables consistently
      doc.querySelectorAll('table').forEach((table) => {
        table.classList.add('related-info-table')
      })

      // Drop forbidden phrases/CTAs before any further transforms.
      stripForbiddenPhrases(doc.body)

      // Sentinel anchors like <a href="//map"> should not navigate — mark
      // them so the click handler on the wrapper can open the map modal.
      doc.querySelectorAll('a').forEach((node) => {
        const href = (node.getAttribute('href') || '').trim()
        if (/^\/?\/map\/?$/i.test(href)) {
          node.setAttribute('href', '#')
          node.setAttribute('data-action', 'open-map')
          node.removeAttribute('target')
          node.removeAttribute('rel')
          node.classList.add('related-info-map-link')
        }
      })

      // Replace <srmdbtn> with <a>
      doc.querySelectorAll('srmdbtn').forEach((node) => {
        const href = node.getAttribute('href') || '#'
        const width = node.getAttribute('width')
        const label = (node.textContent ?? '').trim()

        // If the button itself is a forbidden CTA, remove it.
        if (/\bpurchase\s*epass\b/i.test(label) || /\bbuggy\s*finder\b/i.test(label)) {
          node.remove()
          return
        }

        const a = doc.createElement('a')
        a.setAttribute('href', href)
        a.setAttribute('target', '_blank')
        a.setAttribute('rel', 'noopener noreferrer')
        a.className = 'related-info-srmdbtn'
        if (width && !Number.isNaN(Number(width))) {
          a.style.maxWidth = `${Number(width)}px`
          a.style.width = '100%'
        }
        a.textContent = label || 'Open'

        node.replaceWith(a)
      })

      cleanupSpacing(doc.body)

      return doc.body.innerHTML
    } catch (e) {
      // Fallback: best-effort string transforms.
      // 1) Convert <srmdbtn> to <a>
      // 2) Remove forbidden CTA phrases
      // 3) Collapse <br> runs and remove empty paragraphs
      return raw
        .replace(
          /<srmdbtn\s+[^>]*href\s*=\s*"([^"]+)"[^>]*>([\s\S]*?)<\/srmdbtn>/gi,
          (_m, href, label) => {
            const text = (label ?? '').toString().replace(/<[^>]*>/g, '').trim() || 'Open'
            // Remove forbidden CTA labels in fallback mode too.
            if (/\bpurchase\s*epass\b/i.test(text) || /\bbuggy\s*finder\b/i.test(text)) return ''
            return `<a class="related-info-srmdbtn" href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
          }
        )
        // Remove any remaining forbidden phrases (best-effort fallback)
        .replace(/\bpurchase\s*epass\b/gi, '')
        .replace(/\bbuggy\s*finder\b/gi, '')
        .replace(/(?:\s*<br\s*\/?\s*>\s*){2,}/gi, '<br/>')
        .replace(/<p>(?:\s|&nbsp;|<br\s*\/?\s*>)*<\/p>/gi, '')
    }
  }, [])

  // Fetch nearby places (category 81)
  useEffect(() => {
    const sanitizeWpText = (value) => {
      const raw = (value ?? '').toString()
      return raw
        .replace(/<[^>]*>/g, '')
        .replace(/&/g, '&')
        .replace(/"/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/'/g, "'")
        .replace(/</g, '<')
        .replace(/>/g, '>')
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
        .trim()
    }

    const apiUrl = `${process.env.REACT_APP_NEXT_ASHRAM_APP_URL}/wp-json/wp/v2/information?_embed&_fields=id,title,content,acf,_links.wp:featuredmedia,_embedded.wp:featuredmedia,categories&per_page=100&status=publish`
    const controller = new AbortController()

    const fetchNearbyPlaces = async () => {
      try {
        const res = await fetch(apiUrl, { signal: controller.signal })
        if (!res.ok) throw new Error(`Nearby places API failed: ${res.status}`)

        const json = await res.json()
        const posts = Array.isArray(json) ? json : []

        // Filter posts that have category 81
        const filtered = posts.filter((post) =>
          Array.isArray(post?.categories) && (post.categories.includes(81) || post.categories.includes(110))
        )
        const cFiltered = posts.filter((post) =>
          Array.isArray(post?.categories) && (post.categories.includes(76))
        )
        const cMapped = cFiltered.map((p) => {
          const postTitle = sanitizeWpText(p?.title?.rendered)
          const contentHtml = transformRelatedInfoHtml(p?.content?.rendered)
          return {
            id: p?.id,
            title: postTitle,
            contentHtml,
            contact_person: transformRelatedInfoHtml(p?.acf?.contact_person ?? ''),
            contact_number: transformRelatedInfoHtml(p?.acf?.contact_number ?? ''),
          contact2_number: transformRelatedInfoHtml(p?.acf?.contact2_number ?? ''),
          email: p?.acf?.email,
          instruction: transformRelatedInfoHtml(p?.acf?.instruction ?? ''),
          note: transformRelatedInfoHtml(p?.acf?.note ?? ''),
            imageUrl: pickInfoImage(p),
          }
        })

        // Optional: stable ordering if WP provides ACF order_by
        cMapped.sort((a, b) => {
          const ao = Number(a?.order_by)
          const bo = Number(b?.order_by)
          if (Number.isFinite(ao) && Number.isFinite(bo) && ao !== bo) return ao - bo
          return (a?.title ?? '').localeCompare(b?.title ?? '')
        })
        setAshramcultureDetails(cMapped)

        console.log('Nearby places raw posts:', filtered)
        // https://d2fiisw3kyl7ku.cloudfront.net/wp-content/uploads/2025/04/Param-Krupalu-Dev-Section-Museum-2-1.jpg
        // https://d2fiisw3kyl7ku.cloudfront.net/wp-content/uploads/2025/04/Lady-Wilson-Museum.jpg
        const mapped = filtered.map((post) => {
          const name = sanitizeWpText(post?.title?.rendered)

          // Default image: ACF images[0] -> featured media -> empty
          let img = post?.acf?.images?.[0]
            ?? post?._embedded?.['wp:featuredmedia']?.[0]?.source_url
            ?? ''

          // Force specific images for known nearby places (as per requirement)
          if (name === 'Shri Ranchod Nivas') {
            img = 'https://d2fiisw3kyl7ku.cloudfront.net/wp-content/uploads/2025/04/Param-Krupalu-Dev-Section-Museum-2-1.jpg'
          }
          if (name === 'Shrimad Rajchandraji Jivandarshan at Lady Wilson Museum') {
            img = 'https://d2fiisw3kyl7ku.cloudfront.net/wp-content/uploads/2025/04/Lady-Wilson-Museum.jpg'
          }

          const orderBy = Number(post?.acf?.order_by)
          return { name, img, post, orderBy: Number.isFinite(orderBy) ? orderBy : Number.POSITIVE_INFINITY }
        }).filter((item) => item.name)

        mapped.sort((a, b) => a.orderBy - b.orderBy)
        
        if (mapped.length) setNearbyPlacesFromApi(mapped)
      } catch (error) {
        console.log(error)
      }
    }

    fetchNearbyPlaces()
    return () => controller.abort()
  }, [transformRelatedInfoHtml])

  useEffect(() => {
    if (!groupId) return

    const fetchTransactionHistory = async () => {
      const payload = { groupId }
      try {
        setIsTxnLoading(true)
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/transactionHistory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const result = await response.json()
        if (result?.success) {
          const sortData = (result?.data?.transactionHistory ?? []).sort(
            (a, b) => new Date(b.bill_date) - new Date(a.bill_date)
          )

          const groupedBills = []
          const billMap = new Map()
          for (const bill of sortData) {
            if (!billMap.has(bill.bill_no)) billMap.set(bill.bill_no, [])
            billMap.get(bill.bill_no).push(bill)
          }

          for (const billGroup of billMap.values()) {
            groupedBills.push(billGroup.length > 1 ? billGroup : billGroup[0])
          }
          setTransactionHistoryData(groupedBills)
        } else {
          console.log('transactionHistory error')
        }
      } catch (e) {
        console.log(e)
      } finally {
        setIsTxnLoading(false)
      }
    }

    const fetchScanningHistory = async () => {
      const payload = { groupId }
      try {
        setIsScanLoading(true)
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scanningHistory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const result = await response.json()
        if (result?.success) {
          const sortedArray = (result?.data?.scanningHistory ?? []).sort(
            (a, b) =>
              new Date(`${b?.date}T${b?.time}.000Z`) - new Date(`${a?.date}T${a?.time}.000Z`)
          )
          setScanningHistoryData(sortedArray)
        } else {
          console.log('scanningHistory error')
        }
      } catch (e) {
        console.log(e)
      } finally {
        setIsScanLoading(false)
      }
    }

    fetchTransactionHistory()
    fetchScanningHistory()
  }, [groupId])

  const transactionsForView = useMemo(() => {
    const normalizeType = (categoryName) => {
      const c = (categoryName ?? '').toString().toLowerCase()
      if (c.includes('buggy')) return 'buggy'
      return 'meals'
    }

    const rows = (transactionHistoryData ?? []).map((bill) => {
      const arr = Array.isArray(bill) ? bill : [bill]
      const primary = arr[0] ?? {}
      const type = normalizeType(primary?.categoryName)

      const items = arr.map((x) => ({
        name: `${x?.quantity ?? ''} ${(x?.categoryName ?? '').toString().toUpperCase()}`.trim(),
        rate: x?.rate ?? x?.rate ?? '',
      }))

      return {
        id: primary?.bill_no ?? primary?.billNo ?? `${primary?.bill_date ?? ''}-${items.length}`,
        type,
        date: primary?.bill_date ? formatDateTime(primary.bill_date) : '',
        items,
        amountPaid: Array.isArray(bill) ? (primary?.total_amount ?? '') : (primary?.total_amount ?? ''),
      }
    })

    return couponFilter === 'all' ? rows : rows.filter((r) => r.type === couponFilter)
  }, [transactionHistoryData, couponFilter])



  const relatedInfoI18n = tDeep(EN.relatedInfo) ?? {
    title: 'Help & Information',
    loading: 'Loading…',
    error: 'Unable to load information right now.',
    searchPlaceholder: 'Search',
    close: 'Close',
  }

  const pickInfoImage = (post) => {
    // Try common fields used in this WP instance:
    // - ACF images/gallery
    // - Featured media (via _embed)
    // If none, return empty string.
    const acf = post?.acf
    const imgFromAcf =
      acf?.images?.[0]
      ?? acf?.image
      ?? acf?.thumbnail
      ?? acf?.slider_gallery?.[0]
      ?? acf?.vertical_gallery?.[0]

    const imgFromFeatured = post?._embedded?.['wp:featuredmedia']?.[0]?.source_url
    return (imgFromAcf || imgFromFeatured || "").toString()
  }

  const decodeHtmlEntities = useCallback((value) => {
    const raw = (value ?? '').toString()
    return raw
      .replace(/<[^>]*>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .trim()
  }, [])

  /**
   * WordPress content comes as raw HTML.
   * We do 2 transforms:
   * 1) Add classes for styling tables.
   * 2) Convert `<srmdbtn href="...">Label</srmdbtn>` (custom tag) into a real clickable `<a>`
   *    with our project button styling.
   */
 

  // Click delegation for any related-info-content block: catches the
  // sentinel <a data-action="open-map"> links and opens the map modal
  // instead of letting the browser follow the (invalid) href.
  const handleRelatedInfoContentClick = useCallback((e) => {
    const target = e.target.closest?.('a[data-action="open-map"]')
    if (!target) return
    e.preventDefault()
    e.stopPropagation()
    setShowMapLegendModal(true)
  }, [])

  const fetchRelatedInfoCategories = useCallback(async () => {
    const url = `${process.env.REACT_APP_NEXT_ASHRAM_APP_URL}/wp-json/wp/v2/categories?_fields=name,id,acf&per_page=100&hide_empty=1&show_in_app=yes`
    const controller = new AbortController()
    try {
      setIsRelatedInfoLoading(true)
      setRelatedInfoError('')

      const res = await fetch(url, { signal: controller.signal })
      if (!res.ok) throw new Error(`Categories API failed: ${res.status}`)

      const json = await res.json()
      const rows = Array.isArray(json) ? json : []
      const mapped = rows
        .map((c) => {
          const name = decodeHtmlEntities(c?.name)
          const orderRaw = c?.acf?.order
          const order = Number(orderRaw)
          return {
            id: c?.id,
            name,
            order: Number.isFinite(order) ? order : 9999,
            image: c?.acf?.category_image || '',
            detailImage: c?.acf?.category_detail_image || '',
            images: Array.isArray(c?.acf?.images) ? c.acf.images : [],
          }
        })
        .filter((c) => c?.id && c?.name)
        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))

      setRelatedInfoCategories(mapped)
    } catch (e) {
      console.log(e)
      setRelatedInfoError(relatedInfoI18n.error)
    } finally {
      setIsRelatedInfoLoading(false)
    }

    return () => controller.abort()
  }, [decodeHtmlEntities, relatedInfoI18n.error])

  const fetchRelatedInfoItems = useCallback(async (categoryId) => {
    if (categoryId === undefined || categoryId === null) return
    const controller = new AbortController()
    let mealmenu = []
    console.log(categoryId, ">>>categoryId")
    // Shared helpers for extra, non-WP entries in Related Info.
    const escapeHtml = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

    const formatDateDash = (dStr) => {
      // input: dd-mm-yyyy
      const parts = String(dStr ?? '').split('-')
      if (parts.length !== 3) return dStr ?? ''
      const [d, m, y] = parts
      const mi = parseInt(m, 10) - 1
      if (Number.isNaN(mi) || mi < 0 || mi > 11) return dStr
      return `${parseInt(d, 10)} ${monthNames[mi]} ${y}`
    }

    const formatDateSlash = (dStr) => {
      // input: dd/mm/yyyy
      const parts = String(dStr ?? '').split('/')
      if (parts.length !== 3) return dStr ?? ''
      const [d, m, y] = parts
      const mi = parseInt(m, 10) - 1
      if (Number.isNaN(mi) || mi < 0 || mi > 11) return dStr
      return `${parseInt(d, 10)} ${monthNames[mi]} ${y}`
    }



    const url = `${process.env.REACT_APP_NEXT_ASHRAM_APP_URL}/wp-json/wp/v2/information?_embed&_fields=id,title,content,acf,_links.wp:featuredmedia,_embedded.wp:featuredmedia,categories&per_page=100&status=publish`

    try {
      setIsRelatedInfoItemsLoading(true)
      setRelatedInfoItemsError('')
      setExpandedRelatedInfoItemId(null)
      if (categoryId === 3) {
        // 1) Meal Menu (existing API)
        try {
          const mealUrl = `${process.env.REACT_APP_API_BASE_URL}/ashramFoodMenu`
          const res = await fetch(mealUrl, { signal: controller.signal })
          if (!res.ok) throw new Error(`Ashram food menu API failed: ${res.status}`)

          const json = await res.json()
          const menuRows = Array.isArray(json?.data?.data) ? json.data.data : []

          const validEntries = menuRows.filter((entry) => {
            const m = entry?.Menu || {}
            return (Array.isArray(m.breakfast) && m.breakfast.length) ||
              (Array.isArray(m.lunch) && m.lunch.length) ||
              (Array.isArray(m.dinner) && m.dinner.length)
          })

          const sectionsHtml = validEntries.map((entry) => {
            const m = entry?.Menu || {}
            const breakfast = Array.isArray(m.breakfast) ? m.breakfast.map(escapeHtml).join(', ') : ''
            const lunch = Array.isArray(m.lunch) ? m.lunch.map(escapeHtml).join(', ') : ''
            const dinner = Array.isArray(m.dinner) ? m.dinner.map(escapeHtml).join(', ') : ''
            return [
              `<p><strong>${escapeHtml(formatDateDash(entry?.Date))}</strong></p>`,
              breakfast ? `<p><strong>Breakfast:</strong></p><p>${breakfast}</p>` : '',
              lunch ? `<p><strong>Lunch:</strong></p><p>${lunch}</p>` : '',
              dinner ? `<p><strong>Dinner:</strong></p><p>${dinner}</p>` : '',
            ].join('')
          }).join('')

          const contentHtml = sectionsHtml
            ? `${sectionsHtml}<p><em>Menu is Subject to Change due to Unavoidable Circumstances</em></p>`
            : '<p>No menu available.</p>'

          mealmenu = [
            {
              id: 'meal-menu',
              title: 'Meal Menu',
              contentHtml,
              location: '',
              timings: '',
              email: '',
              images: [],
              contact_person: '',
              contact_number: '',
              instruction: '',
              order_by: 0,
            },
          ]
        } catch (e) {
          console.log(e)
          // Do not block WP items if this fails.
        }

        // 2) Cafeteria menu (Google script API)
        try {
          const cafeteriaUrl = `${process.env.REACT_APP_API_BASE_URL}/cafeteriaMenu`
          const res = await fetch(cafeteriaUrl, { signal: controller.signal })
          if (!res.ok) throw new Error(`Cafeteria menu API failed: ${res.status}`)

          const json = await res.json()
          const rows = Array.isArray(json?.data) ? json.data : []

          const valid = rows.filter((entry) => {
            const cafe = (entry?.Menu?.cafe ?? '').toString().trim()
            return Boolean(cafe)
          })

          const sectionsHtml = valid.map((entry) => {
            const rawCafe = (entry?.Menu?.cafe ?? '').toString()
            const cafeLines = rawCafe
              .split(/\r?\n/)
              .map((x) => x.trim())
              .filter(Boolean)
              .map(escapeHtml)
              .join('<br/>')

            return [
              `<p><strong>${escapeHtml(formatDateSlash(entry?.Date))}</strong></p>`,
              cafeLines ? `<p>${cafeLines}</p>` : '',
            ].join('')
          }).join('')

          const contentHtml = sectionsHtml
            ? `${sectionsHtml}<p><em>Menu is Subject to Change due to Unavoidable Circumstances</em></p>`
            : '<p>No menu available.</p>'

          mealmenu = [
            ...mealmenu,
            {
              id: 'cafeteria-menu',
              title: 'Cafeteria menu',
              contentHtml,
              location: '',
              timings: '',
              email: '',
              images: [],
              contact_person: '',
              contact_number: '',
              instruction: '',
              order_by: 0,
            },
          ]
        } catch (e) {
          console.log(e)
          // Do not block WP items if this fails.
        }
      }

      const res = await fetch(url, { signal: controller.signal })
      if (!res.ok) throw new Error(`Information API failed: ${res.status}`)

      const json = await res.json()
      const rows = Array.isArray(json) ? json : []
      const normalizeImages = (value) => {
        if (!value) return []
        const arr = Array.isArray(value) ? value : []
        return arr
          .map((img) => {
            if (!img) return ''
            if (typeof img === 'string') return img
            return img?.url || img?.sizes?.large || img?.sizes?.medium_large || img?.sizes?.medium || ''
          })
          .filter(Boolean)
      }

      const mapped = rows
        .filter((p) => Array.isArray(p?.categories) && p.categories.includes(categoryId))
        .map((p) => ({
          id: p?.id,
          title: decodeHtmlEntities(p?.title?.rendered),
          contentHtml: transformRelatedInfoHtml(p?.content?.rendered ?? ''),
          location: transformRelatedInfoHtml(p?.acf?.location ?? ''),
          timings: transformRelatedInfoHtml(p?.acf?.timings ?? ''),
          images: normalizeImages(p?.acf?.images),
          contact_person: transformRelatedInfoHtml(p?.acf?.contact_person ?? ''),
          contact_number: transformRelatedInfoHtml(p?.acf?.contact_number ?? ''),
          contact2_number: transformRelatedInfoHtml(p?.acf?.contact2_number ?? ''),
          email: p?.acf?.email,
          instruction: transformRelatedInfoHtml(p?.acf?.instruction ?? ''),
          note: transformRelatedInfoHtml(p?.acf?.note ?? ''),
          order_by: Number(p?.acf?.order_by)
        }))
        .filter((p) => p?.id && p?.title)
        .sort((a, b) => {
          const aOrder = Number.isFinite(a.order_by) ? a.order_by : Number.POSITIVE_INFINITY
          const bOrder = Number.isFinite(b.order_by) ? b.order_by : Number.POSITIVE_INFINITY
          if (aOrder !== bOrder) return aOrder - bOrder
          return a.title.localeCompare(b.title)
        })
      setRelatedInfoItems([...mealmenu, ...mapped])
    } catch (e) {
      console.log(e)
      setRelatedInfoItemsError(relatedInfoI18n.error)
    } finally {
      setIsRelatedInfoItemsLoading(false)
    }

    return () => controller.abort()
  }, [decodeHtmlEntities, transformRelatedInfoHtml, relatedInfoI18n.error])

  const openRelatedInfoModal = useCallback(() => {
    setShowRelatedInfoModal(true)
    // reset view each open
    setRelatedInfoSelectedCategory(null)
    setRelatedInfoItems([])
    setRelatedInfoItemsError('')
    setExpandedRelatedInfoItemId(null)
    setRelatedInfoCarouselIndex(0)
    setRelatedInfoItemCarouselIndexById({})
  }, [])
  const openAshramCultureModal = useCallback(() => {
    setShowAshramCultureModal(true)
  }, [])

  // Reset carousel index when changing category
  useEffect(() => {
    setRelatedInfoCarouselIndex(0)
    setRelatedInfoItemCarouselIndexById({})
  }, [relatedInfoSelectedCategory?.id])

  // Auto-slide item images (only when item has multiple images and is expanded)
  useEffect(() => {
    const itemId = expandedRelatedInfoItemId
    if (!showRelatedInfoModal) return
    if (!itemId) return

    const item = (relatedInfoItems ?? []).find((x) => x?.id === itemId)
    const images = Array.isArray(item?.images) ? item.images.filter(Boolean) : []
    if (images.length <= 1) return

    const id = window.setInterval(() => {
      setRelatedInfoItemCarouselIndexById((prev) => {
        const current = prev?.[itemId] ?? 0
        const next = (current + 1) % images.length
        return { ...prev, [itemId]: next }
      })
    }, 3500)

    return () => window.clearInterval(id)
  }, [expandedRelatedInfoItemId, relatedInfoItems, showRelatedInfoModal])

  // Auto-slide category images (only when detailImage is not present)
  useEffect(() => {
    if (!showRelatedInfoModal) return
    if (!relatedInfoSelectedCategory) return
    if (relatedInfoSelectedCategory.detailImage) return

    const images = Array.isArray(relatedInfoSelectedCategory.images)
      ? relatedInfoSelectedCategory.images.filter(Boolean)
      : []
    if (images.length <= 1) return

    const id = window.setInterval(() => {
      setRelatedInfoCarouselIndex((i) => (i + 1) % images.length)
    }, 3500)

    return () => window.clearInterval(id)
  }, [showRelatedInfoModal, relatedInfoSelectedCategory])

  // Fetch categories on first open
  useEffect(() => {
    if (!showRelatedInfoModal) return
    if (relatedInfoCategories.length > 0) return
    fetchRelatedInfoCategories()
  }, [showRelatedInfoModal, relatedInfoCategories.length, fetchRelatedInfoCategories])

  const relatedInfoFiltered = useMemo(() => {
    const q = relatedInfoQuery.trim().toLowerCase()
    if (!q) return relatedInfoCategories
    return relatedInfoCategories.filter((c) => c.name.toLowerCase().includes(q))
  }, [relatedInfoCategories, relatedInfoQuery])

  const relatedInfoItemsFiltered = useMemo(() => {
    const q = relatedInfoQuery.trim().toLowerCase()
    if (!q) return relatedInfoItems
    return relatedInfoItems.filter((x) => x.title.toLowerCase().includes(q))
  }, [relatedInfoItems, relatedInfoQuery])

  const spacesToRender = tDeep(spacesFromApi.length > 0 ? spacesFromApi : EN.spaces)

  // Use API data if available, otherwise fallback to i18n.
  // Requirement: Nearby section should render ONLY these two places.
  // NOTE: filter on the ENGLISH source (the `allowed` names below are English),
  // and translate only afterwards — filtering translated names would match
  // nothing in gu/hi and the whole section would render empty.
  const placesNearbyRaw = nearbyPlacesFromApi.length > 0 ? nearbyPlacesFromApi : EN.placesNearby
  const placesNearbyFiltered = useMemo(() => {
    const allowed = [
      'SHRIMAD RAJCHANDRA SARVAMANGAL CENTRE OF EXCELLENCE FOR WOMEN',
      'SHRIMAD RAJCHANDRA JIVAMAITRIDHAM',
      'Shrimad Rajchandraji Jivandarshan at Lady Wilson Museum',
      'Shri Ranchod Nivas'
    ]

    const normalize = (s) => (s ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()

    const allowedSet = new Set(allowed.map(normalize))

    const filtered = (Array.isArray(placesNearbyRaw) ? placesNearbyRaw : [])
      .filter((p) => allowedSet.has(normalize(p?.name)))

    // Keep the order as defined in `allowed`.
    filtered.sort(
      (a, b) =>
        allowed.findIndex((x) => normalize(x) === normalize(a?.name))
        - allowed.findIndex((x) => normalize(x) === normalize(b?.name))
    )

    // tDeep leaves https:// strings alone, so the url survives translation.
    return filtered.map((p) => ({
      ...p,
      videoUrl: NEARBY_PLACE_VIDEOS[normalize(p?.name)] ?? '',
      extraImages: NEARBY_PLACE_IMAGES[normalize(p?.name)] ?? [],
    }))
  }, [placesNearbyRaw])

  const placesNearbyToRender = tDeep(placesNearbyFiltered)



  const buggyStops = useMemo(() => {
    return buggyStopsData
  }, [buggyStopsData])

  

  const guidedToursSchedule = useMemo(() => {
    const guidedTourTimes = [
      { time: '9:00 AM', startMin: 540, label: '9 AM' },
      { time: '11:00 AM', startMin: 660, label: '11 AM' },
      { time: '5:00 PM', startMin: 1020, label: '5 PM' },
      { time: '6:30 PM', startMin: 1110, label: '6:30 PM' },
    ]
    const fromWelcome = t("Welcome Centre")
    return guidedTourTimes.map((tour) => ({
      ...tour,
      from: fromWelcome,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang])
 
  const guidedToursDisplay = useMemo(() => {
    if (!tourHistoryList.length) return guidedToursSchedule

    return tourHistoryList.map((tour) => {
      // const eventId = tour?.event_id?.[0]
      // const eventData = tourHistoryById?.[eventId]?.[0]
      // const label = eventData?.date_begin
      //   ? formatTourLabel(eventData.date_begin)
      //   : (tour?.name ?? t("Ashram Tour"))
      // const startMin = eventData?.date_begin ? getTourStartMin(eventData.date_begin) : null

      return {
        ...tour,
        
      }
    })
  }, [tourHistoryList, guidedToursSchedule])


  const isOvernightDuration = useCallback((value) => {
    return /overnight/i.test((value ?? '').toString())
  }, [])
  const visitDate = useMemo(
    () => {
      const date = new Date();
      const day = date.getDate(); // 9
      const month = date.toLocaleString('en-IN', { month: 'long' })
        .replace(/^\w/, c => c.toUpperCase()); // "May"
      const year = date.getFullYear(); // 2026
      return `${day} ${month}, ${year}`;
    },
    []
  );

  const langOptions = [
    { id: 'en', label: 'English' },
    { id: 'gu', label: 'ગુજરાતી' },
    { id: 'hi', label: 'हिन्दी' },
  ]

  const mealsRef = useRef(null)
  const buggyRef = useRef(null)
  const exploreRef = useRef(null)
  const nearbyRef = useRef(null)
  const toursRef = useRef(null)
  // Guided tours horizontal strip: keep the "next" card scrolled into view
  const toursStripRef = useRef(null)
  const tourCardRefs = useRef({}) // key -> HTMLElement
  const planRef = useRef(null)
  // Which PDF action is in flight: null | 'download' | 'print'. Both buttons
  // build the SAME document — only the last step differs (save vs print dialog).
  const [pdfAction, setPdfAction] = useState(null)
  const isDownloadingPdf = pdfAction !== null

    const handlePlanPdf = async (mode = 'download') => {
      if (!plan || isDownloadingPdf) return
      const pdfName = `tour-plan-${new Date().toISOString().split('T')[0]}.pdf`
      // The plan prints on a pocket-size page: A6, 105 x 148 mm - an A4 sheet
      // folded twice, so it reads a little larger than a phone screen. Both
      // export paths below (vector for Latin, rasterized HTML for gu/hi) build
      // to these dimensions, so every measurement here is relative to them.
      const PAGE_MM = { w: 105, h: 148 }
      const newPdfDoc = () => new jsPDF('p', 'mm', [PAGE_MM.w, PAGE_MM.h])
      try {
        setPdfAction(mode)

        const fmtTime = (time) => {
          if (!time) return ''
          const [h, m] = time.split(':').map(Number)
          const ap = h >= 12 ? 'pm' : 'am'
          const h12 = h % 12 || 12
          return `${h12}:${String(m).padStart(2, '0')} ${ap}`
        }
        const stripHtml = (html) => {
          const tmp = document.createElement('div')
          tmp.innerHTML = (html ?? '').toString()
          return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim()
        }
        // Like stripHtml, but keeps <br> / block breaks as real "\n" newlines so
        // multi-line messages don't collapse onto a single line in the PDF.
        const stripHtmlKeepBreaks = (html) => {
          const withBreaks = (html ?? '')
            .toString()
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/(?:p|div|li)>/gi, '\n')
          const tmp = document.createElement('div')
          tmp.innerHTML = withBreaks
          return (tmp.textContent || tmp.innerText || '')
            .replace(/[ \t]+/g, ' ')
            .replace(/\n\s*\n+/g, '\n')
            .replace(/[ \t]*\n[ \t]*/g, '\n')
            .trim()
        }
        // Rasterize an image (incl. SVG) to a PNG data URL so jsPDF can embed it.
        const loadImageData = (src) => new Promise((resolve) => {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = () => {
            try {
              const scale = 4 // upscale for crisp print rendering
              const w = (img.naturalWidth || 289) * scale
              const h = (img.naturalHeight || 52) * scale
              const canvas = document.createElement('canvas')
              canvas.width = w
              canvas.height = h
              const ctx = canvas.getContext('2d')
              ctx.drawImage(img, 0, 0, w, h)
              resolve({ dataUrl: canvas.toDataURL('image/png'), w, h })
            } catch (e) {
              resolve(null)
            }
          }
          img.onerror = () => resolve(null)
          img.src = src
        })
        const badgeLabel = (b) => b === 'must' ? 'Must visit'
          : b === 'tour' ? 'Ashram tour'
            : b === 'act' ? 'Activity'
              : b === 'meal' ? 'Meal'
                : b === 'vid' ? 'Video'
                : b === 'free' ? 'Free'
                  : 'Near By Place'
        const badgeColors = {
          must: { bg: [248, 224, 211], fg: [212, 98, 43] },
          tour: { bg: [220, 232, 240], fg: [62, 109, 142] },
          act: { bg: [221, 230, 224], fg: [90, 122, 107] },
          meal: { bg: [245, 234, 210], fg: [192, 138, 36] },
          vid: { bg: [223, 220, 244], fg: [92, 84, 168] },
          nearby: { bg: [238, 220, 234], fg: [156, 79, 134] },
          free: { bg: [218, 240, 246], fg: [8, 145, 178] },
        }

        // Gujarati / Hindi are complex scripts: jsPDF's pdf.text() draws each
        // codepoint in logical order with no shaping, so matras don't reorder
        // and conjuncts don't ligate (the garbled glyphs in the broken export).
        // The browser DOES shape these correctly, so for gu/hi we build the plan
        // as HTML, rasterize it with html2canvas, and paginate it into the PDF.
        if (lang === 'gu' || lang === 'hi') {
          // const rgb = (a) => `rgb(${a[0]},${a[1]},${a[2]})`
          const esc = (s) => String(s ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          const fontStack = lang === 'gu'
            ? "'Noto Sans Gujarati','Outfit',sans-serif"
            : "'Noto Sans Devanagari','Outfit',sans-serif"
          const fontFamily = lang === 'gu' ? 'Noto Sans Gujarati' : 'Noto Sans Devanagari'

          // Make sure the Indic web font is loaded so html2canvas renders proper
          // glyphs even on devices without a matching system font.
          const fontHref = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@400;700&display=swap`
          if (!document.querySelector(`link[href="${fontHref}"]`)) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = fontHref
            document.head.appendChild(link)
          }

          const todayStr = new Date().toLocaleDateString(
            lang === 'gu' ? 'gu-IN' : 'hi-IN',
            { day: 'numeric', month: 'long', year: 'numeric' },
          )

          const itemHtml = (item) => {
            const title = esc(stripHtml(item.title))
            const venueRaw = stripHtml(item.venue)
            // Guided tours get their first stop called out, same as on screen.
            const startPoint = item.badge === 'tour' ? tourStartingPoint(item.venue) : ''
            const venue = venueRaw
              ? `${startPoint ? `${t('Starting point')}: ${startPoint} — ` : ''}${t('Venue')}: ${venueRaw}`
              : ''
            const time = `${fmtTime(item.startTime)} - ${fmtTime(item.endTime)}`
            const duration = fmtDuration(item.durationLabel)
            const badge = t(badgeLabel(item.badge))
            // const c = badgeColors[item.badge] || badgeColors.free
            return `
              <div class="brk" style="border:1px solid #e1dcd2;border-radius:8px;overflow:hidden;margin-bottom:8px;background:#fff;">
                <div style="display:flex;align-items:center;gap:6px;background:#f7f4ee;padding:7px 9px;border-bottom:${venue ? '1px solid #e1dcd2' : 'none'};line-height:1.35;">
               
                  <span style="flex:1 1 auto;min-width:0;font-weight:700;font-size:12.5px;line-height:1.35;color:#2c1e14;">${title}</span>
                
                  <span style="flex:0 0 auto;display:flex;align-items:center;justify-content:center;height:16px;line-height:1;font-weight:700;font-size:9px;padding:0 7px;border-radius:8px;white-space:nowrap;">${esc(badge)}</span>
                </div>
                ${venue ? `
                <div style="padding:7px 9px;font-size:10.5px;line-height:1.45;">
                  <div style="display:flex;justify-content:space-between;gap:8px;color:#7a6b5e;">
                    <span>${esc(time)}</span>
                    ${duration ? `<span style="color:#a0968a;font-size:9.5px;">${esc(duration)}</span>` : ''}
                  </div>
                  <div style="color:#968a7d;margin-top:2px;">${esc(venue)}</div>
                </div>` : ''}
              </div>`
          }

          const sectionLabelHtml = (label, rawLabel) => {
            if (!label) return ''
            const norm = (rawLabel ?? label ?? '').toString().trim().toLowerCase()
            let dateSuffix = ''
            if (norm === 'today' || norm === 'tomorrow') {
              const d = new Date()
              if (norm === 'tomorrow') d.setDate(d.getDate() + 1)
              dateSuffix = d.toLocaleDateString(lang === 'gu' ? 'gu-IN' : 'hi-IN',
                { day: 'numeric', month: 'long', year: 'numeric' })
            }
            return `
              <div style="display:flex;justify-content:space-between;align-items:center;background:#f2f0eb;border-radius:5px;padding:5px 9px;margin-bottom:8px;">
                <span style="font-weight:700;font-size:10px;color:#7a6b5e;text-transform:uppercase;">${esc(label)}</span>
                ${dateSuffix ? `<span style="font-size:9px;color:#7a6b5e;">${esc(dateSuffix)}</span>` : ''}
              </div>`
          }

          let body = ''
          if (planT?.tourMealReminder) {
            body += `<div style="background:#f8e0d3;border-radius:6px;padding:8px 10px;margin-bottom:10px;font-weight:700;font-size:10.5px;color:#2c1e14;">${esc(stripHtmlKeepBreaks(planT.tourMealReminderMessage)).replace(/\n/g, '<br/>')}</div>`
          }
          if (planT?.isOvernight && Array.isArray(planT?.sections) && planT.sections.length > 0) {
            planT.sections.forEach((section, sIdx) => {
              body += sectionLabelHtml(section?.label, plan?.sections?.[sIdx]?.label)
              body += (section?.items ?? []).map(itemHtml).join('')
              body += '<div style="height:6px;"></div>'
            })
            if (Array.isArray(planT?.exitRecommendations) && planT.exitRecommendations.length > 0) {
              body += sectionLabelHtml(t('Exit Recommendations'))
              planT.exitRecommendations.forEach((rec) => {
                const name = esc((rec?.name ?? '').toString())
                const dur = rec?.durationRange ? ` · ${esc(rec.durationRange)}` : ''
                const note = esc((rec?.note ?? '').toString())
                body += `<div class="brk" style="margin-bottom:8px;">
                  <div style="font-weight:700;font-size:12px;color:#2c1e14;">${name}${dur}</div>
                  ${note ? `<div style="font-size:9.5px;color:#7a6b5e;margin-top:2px;">${note}</div>` : ''}
                </div>`
              })
              if (planT?.checkoutTime) {
                body += `<div style="font-size:13px;color:#7a6b5e;margin-top:4px;">${esc(t('Checkout time'))}: ${esc(planT.checkoutTime)}</div>`
              }
            }
          } else if (Array.isArray(planT?.schedule) && planT.schedule.length > 0) {
            body += planT.schedule.map(itemHtml).join('')
          }

          // Important activities starting just after the plan ends. A single object today;
          // an array is tolerated so this survives the API sending several.
          const extraSuggestions = (Array.isArray(planT?.extra_suggestion_point)
            ? planT.extra_suggestion_point
            : [planT?.extra_suggestion_point]).filter((it) => it?.title)
          if (extraSuggestions.length > 0) {
            body += sectionLabelHtml(t('EXTEND YOUR VISIT'))
            extraSuggestions.forEach((sug) => {
              body += itemHtml(sug)
              const waitMins = Number(sug.waitMins) || 0
              if (waitMins > 0) {
                body += `<div style="font-size:9.5px;font-weight:700;color:#b5732e;margin:-4px 0 12px 2px;">${esc(t('Wait'))}: ${esc(fmtDuration(minsToDurationLabel(waitMins)))}</div>`
              }
            })
          }

          // Nearby places, minus the ones the itinerary already covers.
          const pdfNearby = (Array.isArray(planT?.nearbyPlaces) ? planT.nearbyPlaces : [])
            .filter((_, idx) => !duplicateNearbyIndices.has(idx))
          if (pdfNearby.length > 0) {
            body += sectionLabelHtml(t("Nearby Places You Shouldn't Miss"))
            pdfNearby.forEach((place) => {
              const name = esc((place?.name ?? '').toString())
              const timing = esc((place?.timing ?? '').toString())
              const stay = esc(formatNeedToStay(place?.needToStay, t, fmtDuration))
              const note = esc((place?.note ?? '').toString())
              body += `<div class="brk" style="margin-bottom:8px;">
                <div style="font-weight:700;font-size:12px;color:#2c1e14;">${name}</div>
                ${timing ? `<div style="font-size:9.5px;color:#7a6b5e;margin-top:2px;">${timing}</div>` : ''}
                ${stay ? `<div style="font-size:9.5px;font-weight:700;color:#b5732e;margin-top:2px;">${stay}</div>` : ''}
                ${note ? `<div style="font-size:9.5px;color:#b5732e;margin-top:2px;">* ${note}</div>` : ''}
              </div>`
            })
          }

          // html2canvas can't reliably rasterize an <img> pointing at an SVG,
          // so pre-convert the logo to a PNG data URL (same helper the vector
          // path uses) and embed that instead.
          const logoData = await loadImageData(logo)
          const logoImg = logoData
            ? `<img src="${logoData.dataUrl}" style="height:18px;width:${(18 * logoData.w / logoData.h).toFixed(1)}px;display:block;"/>`
            : ''

          // Lay the HTML out at the page's true physical width (CSS px are 1/96
          // inch) so the rasterized text keeps the same real-world size it had on
          // the old A4 sheet, and simply reflows onto more, smaller pages.
          const nodeW = Math.round(PAGE_MM.w * 96 / 25.4)
          const node = document.createElement('div')
          node.style.cssText = `position:fixed;left:-99999px;top:0;width:${nodeW}px;background:#fff;font-family:${fontStack};`
          node.innerHTML = `
            <div style="background:#2c1e20;display:flex;justify-content:space-between;align-items:center;padding:8px 12px;">
              <span style="background:#fff;border-radius:4px;padding:4px 6px;display:inline-flex;">${logoImg}</span>
              <span style="text-align:right;color:#fff;">
                <span style="display:block;font-weight:700;font-size:12px;">${esc(t('YOUR RECOMMENDED TOUR'))}</span>
                <span style="display:block;font-size:9px;opacity:.9;">${esc(todayStr)}</span>
              </span>
            </div>
            <div style="padding:12px 14px;">${body}</div>`
          document.body.appendChild(node)

          try {
            // Make sure the Indic web fonts are ready before snapshotting.
            if (document.fonts?.load) {
              try {
                await Promise.all([
                  document.fonts.load(`400 16px "${fontFamily}"`),
                  document.fonts.load(`700 16px "${fontFamily}"`),
                ])
              } catch { /* fall back to system font */ }
            }
            if (document.fonts?.ready) await document.fonts.ready
            const scale = 3 // ~288 dpi on this small page
            const canvas = await html2canvas(node, { scale, backgroundColor: '#ffffff', useCORS: true })

            const pdf = newPdfDoc()
            const pageW = pdf.internal.pageSize.getWidth()
            const pageH = pdf.internal.pageSize.getHeight()
            const pxPerMm = canvas.width / pageW
            // Reserve space at the bottom of every page for the footer so the
            // rasterized content is never drawn underneath it (it was overlapping
            // the last row when downloading in gu/hi).
            const footerH = 8 // mm
            const pageHpx = (pageH - footerH) * pxPerMm

            // Slice boundaries that avoid cutting a card in half.
            const nodeTop = node.getBoundingClientRect().top
            const breaks = Array.from(node.querySelectorAll('.brk'))
              .map((el) => (el.getBoundingClientRect().bottom - nodeTop) * scale)

            let posPx = 0
            let pageNum = 0
            while (posPx < canvas.height - 1) {
              const start = posPx
              let sliceEnd = Math.min(start + pageHpx, canvas.height)
              const fit = breaks.filter((b) => b > start + 1 && b <= sliceEnd)
              if (fit.length && sliceEnd < canvas.height) sliceEnd = Math.max(...fit)
              const sliceH = Math.max(1, Math.round(sliceEnd - start))

              const pageCanvas = document.createElement('canvas')
              pageCanvas.width = canvas.width
              pageCanvas.height = sliceH
              const ctx = pageCanvas.getContext('2d')
              ctx.fillStyle = '#ffffff'
              ctx.fillRect(0, 0, canvas.width, sliceH)
              ctx.drawImage(canvas, 0, start, canvas.width, sliceH, 0, 0, canvas.width, sliceH)

              if (pageNum > 0) pdf.addPage()
              pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageW, sliceH / pxPerMm)
              posPx = sliceEnd
              pageNum++
            }

            // Footer (Latin text — safe to draw as vector on every page).
            const pageCount = pdf.internal.getNumberOfPages()
            for (let i = 1; i <= pageCount; i++) {
              pdf.setPage(i)
              pdf.setTextColor(160, 150, 138)
              pdf.setFont('helvetica', 'normal')
              pdf.setFontSize(6.5)
              pdf.text(`Page ${i} of ${pageCount}`, pageW - 8, pageH - 4, { align: 'right' })
              pdf.text('Shrimad Rajchandra Mission Dharampur', 8, pageH - 4)
            }

            deliverPdf(pdf, mode, pdfName)
            toast.success(mode === 'print' ? 'Opening print…' : 'PDF downloaded successfully')
          } finally {
            document.body.removeChild(node)
          }
          return
        }

        const pdf = newPdfDoc()
        // Register a Gujarati/Hindi Unicode font so translated text renders.
        const PDF_FONT = await registerPdfFont(pdf, lang)
        const pageW = pdf.internal.pageSize.getWidth()
        const pageH = pdf.internal.pageSize.getHeight()
        const margin = 8
        const contentW = pageW - margin * 2
        let y = margin

        const ensureSpace = (needed) => {
          if (y + needed > pageH - margin) {
            pdf.addPage()
            y = margin
          }
        }

        // Header bar
        pdf.setFillColor(44, 30, 20)
        pdf.rect(0, 0, pageW, 17, 'F')

        // Brand logo (left) — placed on a white chip so it reads on the dark bar
        const logoData = await loadImageData(logo)
        if (logoData) {
          const logoH = 6
          const logoW = logoH * (logoData.w / logoData.h)
          const pad = 1.4
          const chipW = logoW + pad * 2
          const chipH = logoH + pad * 2
          const chipY = (17 - chipH) / 2
          pdf.setFillColor(255, 255, 255)
          pdf.roundedRect(margin, chipY, chipW, chipH, 1.3, 1.3, 'F')
          pdf.addImage(logoData.dataUrl, 'PNG', margin + pad, chipY + pad, logoW, logoH)
        }

        // Title + date (right aligned)
        pdf.setTextColor(255, 255, 255)
        pdf.setFont(PDF_FONT, 'bold')
        pdf.setFontSize(9.5)
        pdf.text(t('YOUR RECOMMENDED TOUR'), pageW - margin, 8.5, { align: 'right' })
        pdf.setFont(PDF_FONT, 'normal')
        pdf.setFontSize(7.5)
        const todayStr = new Date().toLocaleDateString(lang === 'gu' ? 'gu-IN' : lang === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
        pdf.text(todayStr, pageW - margin, 13, { align: 'right' })
        // if (planT?.isOvernight) {
        //   pdf.setFillColor(212, 98, 43)
        //   const pillText = 'OVERNIGHT'
        //   pdf.setFontSize(8)
        //   const pw = pdf.getTextWidth(pillText) + 6
        //   pdf.roundedRect(pageW - margin - pw, 17, pw, 5, 2.5, 2.5, 'F')
        //   pdf.setTextColor(255, 255, 255)
        //   pdf.text(pillText, pageW - margin - pw / 2, 20.5, { align: 'center' })
        // }
        y = 23

        // Alert message
        if (planT?.tourMealReminder) {
          // Set the font BEFORE measuring/splitting so wrapping and the box height
          // are computed with the same metrics the text is actually drawn with.
          pdf.setFont(PDF_FONT, 'bold')
          pdf.setFontSize(8)
          const lineH = 4.2
          const lines = pdf.splitTextToSize(stripHtmlKeepBreaks(planT.tourMealReminderMessage), contentW - 8)
          const h = lines.length * lineH + 3.5
          ensureSpace(h)
          pdf.setFillColor(248, 224, 211)
          pdf.roundedRect(margin, y, contentW, h, 2, 2, 'F')
          pdf.setTextColor(44, 30, 20)
          pdf.text(lines, margin + 4, y + 5, { lineHeightFactor: 1.5 })
          y += h + 3.5
        }

        const drawItem = (item) => {
          const title = stripHtml(item.title)
          const venueRaw = stripHtml(item.venue)
          // Guided tours get their first stop called out, same as on screen.
          const startPoint = item.badge === 'tour' ? tourStartingPoint(item.venue) : ''
          const venue = venueRaw
            ? `${startPoint ? `${t('Starting point')}: ${startPoint} — ` : ''}${t('Venue')}: ${venueRaw}`
            : ''
          const time = `${fmtTime(item.startTime)} - ${fmtTime(item.endTime)}`
          const duration = fmtDuration(item.durationLabel)
          const badge = t(badgeLabel(item.badge))
          const bColor = badgeColors[item.badge] || badgeColors.free

          // Compute heights
          pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9.5)
          const titleLines = pdf.splitTextToSize(title, contentW - 28)
          pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(8)
          // The venue gets its own full-width line under the time row - on a
          // 105 mm page there isn't room for them to share one.
          const venueLines = venue ? pdf.splitTextToSize(venue, contentW - 6) : []

          const headerH = Math.max(8.5, titleLines.length * 4.4 + 4.2)
          const bodyH = venue ? 8 + venueLines.length * 3.6 : 0
          const cardH = headerH + bodyH

          ensureSpace(cardH + 2.5)

          // Outer card
          pdf.setDrawColor(225, 220, 210)
          pdf.setLineWidth(0.2)
          pdf.setFillColor(255, 255, 255)
          pdf.roundedRect(margin, y, contentW, cardH, 2, 2, 'FD')

          // Header strip (slight shade)
          pdf.setFillColor(247, 244, 238)
          pdf.roundedRect(margin, y, contentW, headerH, 2, 2, 'F')
          if (bodyH > 0) {
            pdf.setFillColor(247, 244, 238)
            pdf.rect(margin, y + headerH - 2, contentW, 2, 'F')
            pdf.setDrawColor(225, 220, 210)
            pdf.line(margin, y + headerH, margin + contentW, y + headerH)
          }

          // Colored dot
          pdf.setFillColor(...bColor.fg)
          pdf.circle(margin + 3.2, y + headerH / 2, 1.1, 'F')

          // Title
          pdf.setTextColor(44, 30, 20)
          pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9.5)
          pdf.text(titleLines, margin + 6, y + 5.2)

          // Badge pill (top-right)
          pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(6.5)
          const bw = pdf.getTextWidth(badge) + 4
          const bh = 3.8
          const bx = margin + contentW - bw - 2.5
          const byy = y + headerH / 2 - bh / 2
          pdf.setFillColor(...bColor.bg)
          pdf.roundedRect(bx, byy, bw, bh, 2, 2, 'F')
          pdf.setTextColor(...bColor.fg)
          pdf.text(badge, bx + bw / 2, byy + 2.7, { align: 'center' })

          // Body: time (+ duration, right-aligned) on one line, venue below it.
          if (bodyH > 0) {
            const timeY = y + headerH + 4.2
            pdf.setTextColor(122, 107, 94)
            pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(8)
            pdf.text(time, margin + 3, timeY)
            if (duration) {
              pdf.setTextColor(160, 150, 138)
              pdf.setFontSize(7)
              pdf.text(duration, margin + contentW - 3, timeY, { align: 'right' })
            }
            pdf.setTextColor(150, 138, 125)
            pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(8)
            pdf.text(venueLines, margin + 3, timeY + 4)
          }

          y += cardH + 2.5
        }

        const drawSectionLabel = (label, rawLabel) => {
          if (!label) return

          // If section is Today/Tomorrow, show the appropriate date on the right.
          // Detect from the ORIGINAL (untranslated) label so it still works in gu/hi.
          const norm = (rawLabel ?? label ?? '').toString().trim().toLowerCase()
          const dateLocale = lang === 'gu' ? 'gu-IN' : lang === 'hi' ? 'hi-IN' : 'en-IN'
          let dateSuffix = ''
          if (norm === 'today' || norm === 'tomorrow') {
            const d = new Date()
            if (norm === 'tomorrow') d.setDate(d.getDate() + 1)
            dateSuffix = d.toLocaleDateString(dateLocale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })
          }

          ensureSpace(9)
          pdf.setFillColor(242, 240, 235)
          pdf.roundedRect(margin, y, contentW, 6, 1.2, 1.2, 'F')
          pdf.setTextColor(122, 107, 94)
          pdf.setFont(PDF_FONT, 'bold')
          pdf.setFontSize(7.5)
          // Left: section label
          pdf.text(label.toUpperCase(), margin + 3, y + 4.1)

          // Right: date (only for Today/Tomorrow)
          if (dateSuffix) {
            pdf.setFont(PDF_FONT, 'normal')
            pdf.setFontSize(6.5)
            pdf.text(dateSuffix, margin + contentW - 3, y + 4.1, { align: 'right' })
          }
          y += 8.5
        }

        if (planT?.isOvernight && Array.isArray(planT?.sections) && planT.sections.length > 0) {
          planT.sections.forEach((section, sIdx) => {
            drawSectionLabel(section?.label, plan?.sections?.[sIdx]?.label)
              ; (section?.items ?? []).forEach(drawItem)
            y += 2
          })

          if (Array.isArray(planT?.exitRecommendations) && planT.exitRecommendations.length > 0) {
            drawSectionLabel(t('Exit Recommendations'))
            planT.exitRecommendations.forEach((rec) => {
              const name = (rec?.name ?? '').toString()
              const dur = rec?.durationRange ? ` · ${rec.durationRange}` : ''
              const note = (rec?.note ?? '').toString()
              pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(8.5)
              const nameLines = pdf.splitTextToSize(name + dur, contentW - 6)
              pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5)
              const noteLines = note ? pdf.splitTextToSize(note, contentW - 6) : []
              const blockH = nameLines.length * 3.8 + noteLines.length * 3.4 + 3.5
              ensureSpace(blockH)
              pdf.setTextColor(44, 30, 20)
              pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(8.5)
              pdf.text(nameLines, margin + 3, y + 3.6)
              if (noteLines.length) {
                pdf.setTextColor(122, 107, 94)
                pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5)
                pdf.text(noteLines, margin + 3, y + 3.6 + nameLines.length * 3.8)
              }
              y += blockH
            })
            if (planT?.checkoutTime) {
              ensureSpace(8)
              pdf.setTextColor(122, 107, 94)
              pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5)
              pdf.text(`${t('Checkout time')}: ${planT.checkoutTime}`, margin + 3, y + 3.6)
              y += 7
            }
          }
        } else if (Array.isArray(planT?.schedule) && planT.schedule.length > 0) {
          planT.schedule.forEach(drawItem)
        }

        // Important activities starting just after the plan ends. A single object today;
        // an array is tolerated so this survives the API sending several.
        const extraSuggestions = (Array.isArray(planT?.extra_suggestion_point)
          ? planT.extra_suggestion_point
          : [planT?.extra_suggestion_point]).filter((it) => it?.title)
        if (extraSuggestions.length > 0) {
          drawSectionLabel(t('EXTEND YOUR VISIT'))
          extraSuggestions.forEach((sug) => {
            drawItem(sug)
            const waitMins = Number(sug.waitMins) || 0
            if (waitMins > 0) {
              ensureSpace(8)
              pdf.setTextColor(181, 115, 46)
              pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(7.5)
              pdf.text(`${t('Wait')}: ${fmtDuration(minsToDurationLabel(waitMins))}`, margin + 3, y + 3)
              y += 6
            }
          })
        }

        // Nearby places, minus the ones the itinerary already covers.
        const pdfNearby = (Array.isArray(planT?.nearbyPlaces) ? planT.nearbyPlaces : [])
          .filter((_, idx) => !duplicateNearbyIndices.has(idx))
        if (pdfNearby.length > 0) {
          drawSectionLabel(t("Nearby Places You Shouldn't Miss"))
          pdfNearby.forEach((place) => {
            const name = (place?.name ?? '').toString()
            const timing = (place?.timing ?? '').toString()
            const stay = formatNeedToStay(place?.needToStay, t, fmtDuration)
            const note = (place?.note ?? '').toString()
            pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(8.5)
            const nameLines = pdf.splitTextToSize(name, contentW - 6)
            pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5)
            const timingLines = timing ? pdf.splitTextToSize(timing, contentW - 6) : []
            const stayLines = stay ? pdf.splitTextToSize(stay, contentW - 6) : []
            const noteLines = note ? pdf.splitTextToSize(`* ${note}`, contentW - 6) : []
            const blockH = nameLines.length * 3.8 + (timingLines.length + stayLines.length + noteLines.length) * 3.4 + 3.5
            ensureSpace(blockH)
            pdf.setTextColor(44, 30, 20)
            pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(8.5)
            pdf.text(nameLines, margin + 3, y + 3.6)
            let lineY = y + 3.6 + nameLines.length * 3.8
            pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5)
            if (timingLines.length) {
              pdf.setTextColor(122, 107, 94)
              pdf.text(timingLines, margin + 3, lineY)
              lineY += timingLines.length * 3.4
            }
            if (stayLines.length) {
              pdf.setTextColor(181, 115, 46)
              pdf.setFont(PDF_FONT, 'bold')
              pdf.text(stayLines, margin + 3, lineY)
              pdf.setFont(PDF_FONT, 'normal')
              lineY += stayLines.length * 3.4
            }
            if (noteLines.length) {
              pdf.setTextColor(181, 115, 46)
              pdf.text(noteLines, margin + 3, lineY)
            }
            y += blockH
          })
        }

        // Footer with page numbers
        const pageCount = pdf.internal.getNumberOfPages()
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i)
          pdf.setTextColor(160, 150, 138)
          pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(6.5)
          pdf.text(`Page ${i} of ${pageCount}`, pageW - margin, pageH - 4, { align: 'right' })
          pdf.text('Shrimad Rajchandra Mission Dharampur', margin, pageH - 4)
        }

        deliverPdf(pdf, mode, pdfName)
        toast.success(mode === 'print' ? 'Opening print…' : 'PDF downloaded successfully')
      } catch (err) {
        console.error(mode === 'print' ? 'PDF print failed' : 'PDF download failed', err)
        toast.error(mode === 'print' ? 'Failed to print PDF' : 'Failed to download PDF')
      } finally {
        setPdfAction(null)
      }
    }

  const scrollTo = (id) => {
    setActiveLink(id)
    const map = {
      meals: mealsRef,
      buggy: buggyRef,
      explore: exploreRef,
      nearby: nearbyRef,
      tours: toursRef,
      plan: planRef,
    }

    const element = map[id]?.current
    if (element) {
      const yOffset = -120 // 20px upar thi
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  /* Swipe between visitor passes */
  const onPassTouchStart = useCallback((e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? e.clientX
  }, [])
  const onPassTouchEnd = useCallback((e) => {
    const endX = e.changedTouches?.[0]?.clientX ?? e.clientX
    const startX = touchStartX.current
    if (startX == null || visitors.length <= 1) return
    const delta = startX - endX
    if (Math.abs(delta) < 50) return
    setActiveVisitorIndex((i) => {
      if (delta > 0) return Math.min(i + 1, visitors.length - 1)
      return Math.max(i - 1, 0)
    })
  }, [visitors.length])

  useEffect(() => {
    const DetailsData = async () => {
      const payload = { data: passId };
      try {
        // setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_VMS_URL}/getVisitorDataByGroupId`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );
        const result = await response.json();

        if (result?.isSuccess) {
          // setIsLoading(false);
          setVisitors(
            result?.data
              ?.filter((user) => user?.isDefault === 1)
              .concat(result?.data?.filter((user) => user?.isDefault !== 1))
          );
        } else {
          // setIsLoading(false);
          console.log("error");
        }
      } catch (error) {
        // setIsLoading(false);
        console.log(error);
      }
    };
    DetailsData();
  }, [passId]);

  useEffect(() => {
    const LoginTour = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ODOO_URL}/odoo_connect`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              login: `${process.env.REACT_APP_ODOO_USERNAME}`,
              password: `${process.env.REACT_APP_ODOO_PASSWORD}`,
              db: 'itportal.srmd.org',
            },
          }
        )
        const result = await response.json()
        if (result?.Status) {
          // setLoginApi(result['api-key'])
        } else {
          console.log('Tour login error')
        }
      } catch (error) {
        console.log(error)
      }
    }

    LoginTour()
  }, [])

  



  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.async = true
    document.body.appendChild(script)
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  useEffect(() => {
    const fetchLiveSchedule = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_VMS_URL}/planner/master-data`
        )

        if (!response.ok) return

        const todayEvents = await response.json()


        setTourHistoryList(todayEvents?.data?.tours)
        const durationOptions = todayEvents?.data?.durationOptions
        const config = todayEvents?.data?.config
        // OVERNIGHT_CHECKIN/CHECKOUT_TIME_IN_24_HRS — the API may surface these config
        // keys camelCased with or without the "In24Hrs" suffix, so accept either.
        setOvernightCheckinTime(to24hHHMM(
          config?.overnightCheckinTime ?? config?.overnightCheckinTimeIn24Hrs
        ) || OVERNIGHT_CHECKIN_FALLBACK)
        setOvernightCheckoutTime(to24hHHMM(
          config?.overnightCheckoutTime ?? config?.overnightCheckoutTimeIn24Hrs
        ))
        const time = config?.endOfDayTime;
        const [hours, minutes] = time.split(':').map(Number);
        const totalMinutes = (hours * 60) + minutes;
        setEndOfDayMin(totalMinutes);
        if (Array.isArray(durationOptions)) {
          setPlannerDurationOptions(durationOptions)
        }
        // setTourHistoryList(todayEvents?.data?.durationOptions)
      } catch (error) {
        console.log(error)
      }
    }

    fetchLiveSchedule()
  }, [])

  useEffect(() => {
    setActiveBuggyStopIndex(null)
  }, [buggyStops.length])
  // NOTE: Route map modal is currently opened from elsewhere (or feature gated).
  // Keeping only the modal UI state; removed unused openRouteMapModal handler to satisfy ESLint.

  useEffect(() => {
    const MealsItem = async () => {
      const payload = { posAppDisplayType: 'E', groupId: 1 }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const result = await response.json()
        if (result?.success) setMealsData(result?.data?.items ?? [])
        else console.log('Meals items fetch error')
      } catch (e) {
        console.log(e)
      }
    }

    // Meal timings are served by /print API (same as old DashBoard.jsx)
    const fetchMealPrintTimes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/print`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        })
        const result = await response.json()
        if (result?.success) setMealPrintTimes(result?.data?.printFormat ?? [])
        else console.log('print API fetch error')
      } catch (e) {
        console.log(e)
      }
    }

    const fetchPassConfigs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/passConfigs`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        })
        const result = await response.json()
        if (result?.success) setTimeEnd(result?.data?.passConfigs ?? [])
        else console.log('passConfigs fetch error')
      } catch (error) {
        console.log(error)
      }
    }

    const fetchBuggyStops = async () => {
      try {
        const stops = EN.buggyStops
        setBuggyStopsData(stops)
      } catch (error) {
        console.log(error)
      }
    }

    const BuggyItem = async () => {
      const payload = { posAppDisplayType: 'E', groupId: 2 }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const result = await response.json()
        if (result?.success) setBuggyData(result?.data?.items?.[0])
        else console.log('Buggy items fetch error')
      } catch (error) {
        console.log(error)
      }
    }

    MealsItem()
    fetchMealPrintTimes()
    fetchPassConfigs()
    fetchBuggyStops()
    BuggyItem()
  }, [lang])

  // Buggy Finder: load all stops once when the screen first opens.
  useEffect(() => {
    if (!showBuggyFinder || buggyFinderStops.length > 0) return
    let mounted = true
    const run = async () => {
      try {
        const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
        const res = await fetch(`${base}/planner/buggy-stops`)
        const json = await res.json()
        const data = unwrapBuggyData(json)
        const stops = (Array.isArray(data) ? data : [])
          .map((s) => (typeof s === 'string' ? s : (s?.StopName ?? '')).toString())
          .filter(Boolean)
        if (mounted) setBuggyFinderStops(sortBuggyStops(stops))
      } catch (e) {
        console.log('Buggy Finder stops error', e)
      }
    }
    run()
    return () => { mounted = false }
  }, [showBuggyFinder, buggyFinderStops.length])

  // Buggy Finder: load routes/timings whenever the selected stop changes.
  useEffect(() => {
    if (!showBuggyFinder || !buggyFinderSelectedStop) return
    let mounted = true
    const run = async () => {
      try {
        setBuggyFinderLoading(true)
        const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
        const res = await fetch(
          `${base}/planner/buggy-timings?stopName=${encodeURIComponent(buggyFinderSelectedStop)}`
        )
        const json = await res.json()
        const data = unwrapBuggyData(json)
        if (mounted) setBuggyFinderRoutes(Array.isArray(data) ? data : [])
      } catch (e) {
        console.log('Buggy Finder routes error', e)
        if (mounted) setBuggyFinderRoutes([])
      } finally {
        if (mounted) setBuggyFinderLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [showBuggyFinder, buggyFinderSelectedStop])

  // Buggy Finder: open the "Full Schedule" bottom-sheet for a route and load
  // its full-day timings (bucketed into Morning / Afternoon / Evening).
  const openBuggyFullSchedule = useCallback(async (route) => {
    setBuggyFullScheduleSections([])
    setShowBuggyFullSchedule(true)
    setBuggyFullScheduleLoading(true)
    // Default-open the section that covers the current time.
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes()
    setFullScheduleSection(nowMin < 12 * 60 ? 'Morning' : nowMin < 17 * 60 ? 'Afternoon' : 'Evening')
    try {
      const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
      const res = await fetch(
        `${base}/planner/buggy-full-schedule?routeId=${encodeURIComponent(route?.RouteId ?? '')}&stopName=${encodeURIComponent(buggyFinderSelectedStop)}`
      )
      const json = await res.json()
      const data = unwrapBuggyData(json)
      setBuggyFullScheduleSections(Array.isArray(data) ? data : [])
    } catch (e) {
      console.log('Buggy Finder full schedule error', e)
      setBuggyFullScheduleSections([])
    } finally {
      setBuggyFullScheduleLoading(false)
    }
  }, [buggyFinderSelectedStop])

  // Buggy Finder: open the route-stops bottom-sheet for a route.
  const openBuggyRoute = useCallback((route) => {
    const color = `#${(route?.ColorCode ?? '7A6B5E').toString().replace(/^#/, '')}`
    const title = [route?.MainRouteName, route?.SubRouteName].filter(Boolean).join(' | ')
    const stops = (route?.RouteStops ?? '').toString().split(',').map((x) => x.trim()).filter(Boolean)
    const mapUrl = (route?.MapURL ?? '').toString().trim()
    setBuggyRouteData({ title, color, stops, mapUrl })
    setShowBuggyRoute(true)
  }, [])

  // Buggy Finder: open walking distances from the currently-selected stop.
  const openBuggyWalking = useCallback(async () => {
    setBuggyWalkingFrom(buggyFinderSelectedStop)
    setBuggyWalkingItems([])
    setShowBuggyWalking(true)
    setBuggyWalkingLoading(true)
    try {
      const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
      const res = await fetch(`${base}/planner/buggy-walking-distance?stopName=${encodeURIComponent(buggyFinderSelectedStop)}`)
      const json = await res.json()
      const data = unwrapBuggyData(json)
      setBuggyWalkingItems(Array.isArray(data) ? data : [])
    } catch (e) {
      console.log('Buggy Finder walking distance error', e)
      setBuggyWalkingItems([])
    } finally {
      setBuggyWalkingLoading(false)
    }
  }, [buggyFinderSelectedStop])

  // Buggy Finder: open the ride-stops timeline (time + stop) for a single ride.
  const openBuggyRide = useCallback(async (route, rideNo) => {
    const color = `#${(route?.ColorCode ?? '7A6B5E').toString().replace(/^#/, '')}`
    setBuggyRideTitle((route?.MainRouteName ?? '').toString())
    setBuggyRideColor(color)
    setBuggyRideStops([])
    setShowBuggyRide(true)
    setBuggyRideLoading(true)
    try {
      const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
      const res = await fetch(`${base}/planner/buggy-stops-by-ride?rideNo=${encodeURIComponent(rideNo ?? '')}`)
      const json = await res.json()
      const data = unwrapBuggyData(json)
      const stops = (Array.isArray(data) ? data : []).map((d) => ({
        name: (d?.StopName ?? '').toString().trim(),
        time: (d?.NextTiming ?? '').toString().trim(),
      }))
      setBuggyRideStops(stops)
    } catch (e) {
      console.log('Buggy Finder ride stops error', e)
      setBuggyRideStops([])
    } finally {
      setBuggyRideLoading(false)
    }
  }, [])

  const getCategoryTime = useCallback((categoryId) => {
    if (!categoryId) return null
    const category = mealPrintTimes?.find((x) => x?.categoryId === categoryId)
    return category ?? null
  }, [mealPrintTimes])

  // Same formatting approach as DashBoard.jsx, but generalized for any meal label.
  const formatMealTimeFromHeader = useCallback((header, mealLabel) => {
    const raw = (header ?? '').toString()
    if (!raw) return ''

    const label = (mealLabel ?? '').toString().trim()
    const withoutLabel = label
      ? raw.replace(`${label} (`, '')
      : raw

    return withoutLabel
      .replace(')', '')
      .replace(
        /(\d+):?(\d{0,2})?(am|pm)/gi,
        (_match, p1, p2, p3) =>
          `${p1.length === 1 ? p1 : p1.padStart(2, '0')}${p2 ? `:${p2}` : `.00`} ${p3.toLowerCase()}`
      )
      .replace(/(\d+)\.(\d{2})\.(\d{2})/g, '$1.$2')
      .replace('-', ' - ')
      .trim()
  }, [])

  useEffect(() => {
    const isWithinTimeFrame = (name) => {
      const now = new Date()
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
      const categoryData = mealsData?.find((meal) => meal?.categoryName === name)
      const categoryId = categoryData?.categoryId
      const category = timeEnd?.find((c) => c.CategoryId === categoryId)

      if (!category || !category.PurchaseEndTime) {
        setMealShows((prev) => ({ ...prev, [name]: false }))
        return
      }

      const endTime = category.PurchaseEndTime.split('T')[1].substring(0, 8)
      const isWithinTime = currentTime < endTime
      setMealShows((prev) => ({ ...prev, [name]: isWithinTime }))
    }

    if (mealsData?.length > 0 && timeEnd?.length > 0) {
      isWithinTimeFrame(mealCategoryMap.breakfast)
      isWithinTimeFrame(mealCategoryMap.lunch)
      isWithinTimeFrame(mealCategoryMap.dinner)
      isWithinTimeFrame(mealCategoryMap.veganBreakfast)
      isWithinTimeFrame(mealCategoryMap.veganLunch)
      isWithinTimeFrame(mealCategoryMap.veganDinner)
    }
  }, [timeEnd, mealsData])

  const DateFunction = () => {
    const currentDate = new Date()
    const year = currentDate.getFullYear()
    const month = String(currentDate.getMonth() + 1).padStart(2, '0')
    const day = String(currentDate.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getCategoryRate = (categoryName) => {
    const category = mealsData?.find((meal) => meal?.categoryName === categoryName)
    return category ? category : null
  }

  const isMealVisible = useCallback((mealKey) => {
    const categoryName = mealCategoryMap[mealKey] ?? mealKey
    return Boolean(mealShows?.[categoryName])
  }, [mealShows])

  const dayPassAvailable = useMemo(
    () => isMealVisible('breakfast') && isMealVisible('lunch') && isMealVisible('dinner'),
    [isMealVisible]
  )

  // NOTE:
  // Earlier this UI used `t.meals` (i18n) for meal name/price.
  // Requirement: use MealsItem API (`/items` => `mealsData`) as the source of truth
  // for meal labels/prices, and only use i18n as a fallback for time/note.
  const filteredMeals = useMemo(() => {
    const i18nMeals = Array.isArray(t?.meals) ? t.meals : []
    const i18nByKey = new Map(i18nMeals.map((m) => [m.key ?? m.name, m]))

    // Render order
    const mealKeys = [
      'breakfast',
      'lunch',
      'dinner',
    ]

    return mealKeys
      .map((key) => {
        const categoryName = mealCategoryMap[key] ?? key
        const apiItem = mealsData?.find((x) => x?.categoryName === categoryName)
        const i18nItem = i18nByKey.get(key)

        const priceRaw = apiItem?.categoryRate ?? apiItem?.category_rate ?? i18nItem?.price
        const price = Number(priceRaw)

        // Meal time should come from /print API (printFormat) using categoryId.
        const timeHeader = getCategoryTime(apiItem?.categoryId)?.header
        const time = formatMealTimeFromHeader(timeHeader, categoryName) || ''

        return {
          key,
          // API label is the source of truth; t() translates it (English →
          // gu/hi via dictionary or translate package; already-localized
          // labels are left untouched).
          name: t(apiItem?.categoryName ?? i18nItem?.name ?? categoryName),
          time,
          note: t(i18nItem?.note),
          price: Number.isFinite(price) ? price : 0,
        }
      })
      .filter((m) => m.price > 0)
      .filter((m) => isMealVisible(m.key))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, mealsData, isMealVisible, getCategoryTime, formatMealTimeFromHeader])

  // const CreateOrder = async (billNo, trustId, name, mobile) => {
  //   const payload = {
  //     visitorId: activeVisitor?.Id,
  //     billNo,
  //     trustId,
  //     system: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 'EntryForm' : 'OmniQR',
  //     paymentType: 'PG',
  //     outputType: 'pg',
  //     gateway: 'razorpay',
  //     returnUrl: `${process.env.REACT_APP_BASE_URL}visitorPortal/${passId}`,
  //     name,
  //     mobileNo: mobile?.length === 10 ? mobile : '9999999999',
  //   }

  //   try {
  //     setSecIsLoading(true)
  //     const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/createOrder`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(payload),
  //     })
  //     const result = await response.json()
  //     if (!result?.success) {
  //       console.log('createOrder error')
  //       setSecIsLoading(false)
  //       return
  //     }

  //     const order = result?.data?.data?.data?.order
  //     const RazorpayCtor = window?.Razorpay
  //     if (!RazorpayCtor || !order?.id) {
  //       console.log('Razorpay not available or invalid order')
  //       setSecIsLoading(false)
  //       return
  //     }

  //     const options = {
  //       key: trustId === 4 ? process.env.REACT_APP_RZP_SRASSK_KEY : process.env.REACT_APP_RZP_SRDPS_KEY,
  //       amount: order?.amount,
  //       currency: order?.currency,
  //       name: 'Meals',
  //       description: order?.receipt,
  //       order_id: order?.id,
  //       handler: function () {
  //         window.location.href = payload.returnUrl
  //       },
  //       prefill: { name, contact: mobile },
  //       theme: { color: V4.accent },
  //       modal: {
  //         ondismiss: function () {
  //           setSecIsLoading(false)
  //         },
  //       },
  //     }

  //     const rzp = new RazorpayCtor(options)
  //     rzp.open()
  //   } catch (e) {
  //     console.log(e)
  //     setSecIsLoading(false)
  //   }
  // }
  const CreateOrder = async (billNo, trustId, name, mobile) => {
    const payload = {
      visitorId: activeVisitor?.Id,
      billNo: billNo,
      trustId: trustId,
      system:
        process.env.REACT_APP_API_ENV_TYPE === "DEV" ? "EntryForm" : "OmniQR",
      paymentType: "PG",
      outputType: "pg",
      gateway: trustId === 4 ? "razorpay" : "razorpay", //temp hardcoding till payment gateway issues are sorted
      returnUrl: `${process.env.REACT_APP_BASE_URL}visitorPortal/${passId}`,
      name: name,
      mobileNo: mobile?.length === 10 ? mobile : "9999999999",
    };

    try {
      setSecIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/createOrder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();

      if (result?.success) {
        if (payload.gateway === "cashfree") {
          const cashfree = await load({
            mode:
              process.env.REACT_APP_API_ENV_TYPE === "LIVE"
                ? "production"
                : "sandbox",
          });
          const checkoutOptions = {
            paymentSessionId: result?.data?.data?.data?.order?.paymentSessionId,
            redirectTarget: "_self",
          };

          cashfree.checkout(checkoutOptions).then(function (result) {
            if (result?.error) {
              toast.error(result.error.message);
            }
            if (result?.redirect) {
              console.log("Redirection");
            }
            setSecIsLoading(false);
          });
        } else {
          const options = {
            key:
              trustId === 4
                ? process.env.REACT_APP_RZP_SRASSK_KEY
                : process.env.REACT_APP_RZP_SRDPS_KEY, // Sandbox key for Razorpay (replace with your actual test key)
            amount: result?.data?.data?.data?.order?.amount, // Amount is in currency subunits. Default currency is INR. Hence, 10000 refers to 100 INR
            currency: result?.data?.data?.data?.order?.currency,
            name: trustId === 4 ? "Meals" : "Buggy Pass",
            description: result?.data?.data?.data?.order?.receipt,
            order_id: result?.data?.data?.data?.order?.id, // Pass the order ID if you have it
            handler: function (response) {
              //setLoading(true);
              setSecIsLoading(true);
              //window.location.href = `${window.location.origin}/events/${eventId}/payment-summary?order_id=${createOrderResponse.data.order_number}`;
              window.location.href = payload.returnUrl;
              //setLoading(false);
            },
            prefill: {
              name: name ?? "",
              /*email: createOrderRequest.members[0]?.wsrdt_email?.replace(/,/g, '') 
                      ?? createOrderRequest.wsrmn_email?.replace(/,/g, '') 
                      ?? '',*/
              contact: mobile ?? "",
            },
            theme: {
              color: "#c66a10",
            },
          };
          //console.log('options', options)
          const rzp1 = new window.Razorpay(options);
          rzp1.on("payment.failed", function (response) {
            console.log(response);
            //setLoading(false);
            setSecIsLoading(false);
          });
          setTimeout(() => {
            if (rzp1._state !== "paid") {
              setSecIsLoading(false);
            }
          }, 5000);
          rzp1.open();
        }
      } else {
        toast.error(result?.message);
        setSecIsLoading(false);
        console.log("error");
      }
    } catch (error) {
      setSecIsLoading(false);
      console.log(error);
    }
  };
  const PayOnlineBuggy = async () => {
    if (buggyQty <= 0) return
    if (!activeVisitor) {
      return toast.error("Your pass has expired because your day pass validity has ended, so you can no longer take an ePass.")
    }
    const payload = {
      items: [
        {
          startDate: DateFunction(),
          endDate: DateFunction(),
          categoryId: BuggyData?.categoryId,
          quantity: buggyQty,
          group: 'Buggy',
        },
      ],
      source: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 'EntryForm' : 'OmniQR',
      paymentType: 'PG',
      centerId: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 14 : 41,
      posId: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 47 : 55,
      discountPct: 0,
      name: activeVisitor?.FullName,
      mobile: activeVisitor?.CountryDialingCode?.toString()?.includes('+')
        ? `${activeVisitor?.CountryDialingCode}${activeVisitor?.MobileNo ?? ''}`
        : `+${activeVisitor?.CountryDialingCode ?? ''}${activeVisitor?.MobileNo ?? ''}`,
      email: '',
      groupId: activeVisitor?.GroupId,
    }

    try {
      setSecIsLoading(true)
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items/savePurchaseData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = await response.json()

      if (result?.success) {
        setSecIsLoading(false)
        CreateOrder(result?.data?.billNumbers?.[0] ?? result?.data?.billNo, BuggyData?.trustID, payload?.name, activeVisitor?.MobileNo)
      } else {
        setSecIsLoading(false)
        console.log('savePurchaseData error')
      }
    } catch (error) {
      setSecIsLoading(false)
      console.log(error)
    }
  }

  const PayOnlineMeals = async () => {

    if (!activeVisitor) return

    const getMealQty = (key) => mealQty?.[key] ?? 0
    const dayPassQty = getMealQty(mealDayPassKey)
    const breakfastQty = getMealQty('breakfast') + dayPassQty
    const lunchQty = getMealQty('lunch') + dayPassQty
    const dinnerQty = getMealQty('dinner') + dayPassQty

    const payload = {
      items: [],
      source: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 'EntryForm' : 'OmniQR',
      paymentType: 'PG',
      centerId: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 14 : 41,
      posId: process.env.REACT_APP_API_ENV_TYPE === 'DEV' ? 47 : 55,
      discountPct: 0,
      name: activeVisitor?.FullName,
      mobile: activeVisitor?.CountryDialingCode?.toString()?.includes('+')
        ? `${activeVisitor?.CountryDialingCode}${activeVisitor?.MobileNo ?? ''}`
        : `+${activeVisitor?.CountryDialingCode ?? ''}${activeVisitor?.MobileNo ?? ''}`,
      email: '',
      groupId: activeVisitor?.GroupId,
    }

    if (breakfastQty > 0) {
      payload.items.push({
        startDate: DateFunction(),
        endDate: DateFunction(),
        categoryId: getCategoryRate(mealCategoryMap.breakfast)?.categoryId,
        quantity: breakfastQty,
        group: 'Meals',
      })
    }

    if (lunchQty > 0) {
      payload.items.push({
        startDate: DateFunction(),
        endDate: DateFunction(),
        categoryId: getCategoryRate(mealCategoryMap.lunch)?.categoryId,
        quantity: lunchQty,
        group: 'Meals',
      })
    }

    if (dinnerQty > 0) {
      payload.items.push({
        startDate: DateFunction(),
        endDate: DateFunction(),
        categoryId: getCategoryRate(mealCategoryMap.dinner)?.categoryId,
        quantity: dinnerQty,
        group: 'Meals',
      })
    }


    try {
      setSecIsLoading(true)
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items/savePurchaseData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = await response.json()

      if (result?.success) {
        setSecIsLoading(false)
        CreateOrder(
          result?.data?.billNumbers?.[0] ?? result?.data?.billNo,
          getCategoryRate(mealCategoryMap.dinner)?.trustID
          ?? getCategoryRate(mealCategoryMap.lunch)?.trustID
          ?? getCategoryRate(mealCategoryMap.breakfast)?.trustID
          ?? getCategoryRate(mealCategoryMap.veganBreakfast)?.trustID
          ?? getCategoryRate(mealCategoryMap.veganLunch)?.trustID
          ?? getCategoryRate(mealCategoryMap.veganDinner)?.trustID,
          payload?.name,
          activeVisitor?.MobileNo
        )
      } else {
        setSecIsLoading(false)
        console.log('savePurchaseData error')
      }
    } catch (e) {
      console.log(e)
      setSecIsLoading(false)
    }
  }


  // Convert arrivalTime (string like "HH:MM" from <input type="time"> or "09:37 AM")
  // into minutes since midnight, so all schedule comparisons work as numeric.
  const currentMin = useMemo(() => {
    // If some caller ever sets arrivalTime as a number, respect it.
    if (typeof arrivalTime === 'number' && Number.isFinite(arrivalTime)) return arrivalTime

    const raw = (arrivalTime ?? '').toString().trim()
    if (!raw) {
      const now = new Date()
      return now.getHours() * 60 + now.getMinutes()
    }

    // Special-case: “Post 6 PM” is stored as 18:01 (sentinel) so it stays selectable.
    // It resolves to the visitor's *current* clock time (snapped to the nearest
    // 30 min), so remaining-time / tour-duration math reflects how much of the day
    // is actually left (e.g. 7:30 PM with a 9 PM end-of-day -> only 1.5h, so 2H/3H disable).
    if (raw === POST_6PM_VALUE) {
      return currentSnappedArrivalMinutes()
    }

    // Case 1: 24h format from <input type="time"> => "HH:MM" or sometimes "HH:MM:SS"
    const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (match24) {
      const h = Number(match24[1])
      const m = Number(match24[2])
      if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m
    }

    // Case 2: 12h format => "HH:MM AM" / "H:MM PM" (like buggy timing API)
    const match12 = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i)
    if (match12) {
      let h = Number(match12[1])
      const m = Number(match12[2] ?? 0)
      const ap = (match12[3] ?? '').toUpperCase()
      if (ap === 'PM' && h < 12) h += 12
      if (ap === 'AM' && h === 12) h = 0
      if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m
    }

    // Fallback: use current clock time.
    const now = new Date()
    return now.getHours() * 60 + now.getMinutes()
  }, [arrivalTime])

  // Real clock minutes (used for LIVE schedule widgets like "Right now" / "Next up")
  // NOTE: `currentMin` above is based on the *planner arrival time* which is clamped
  // to 08:00–18:00. At night, that clamp would make `currentMin` max out at 18:00,
  // which incorrectly makes 19:30 look like an upcoming event. So we keep a separate
  // "now" time for live schedule.
  const [clockMin, setClockMin] = useState(() => {
    const d = new Date()
    return d.getHours() * 60 + d.getMinutes()
  })

  useEffect(() => {
    const tick = () => {
      const d = new Date()
      setClockMin(d.getHours() * 60 + d.getMinutes())
    }

    // initial sync + then every minute
    tick()
    const id = window.setInterval(tick, 60 * 1000)
    return () => window.clearInterval(id)
  }, [])

  const [nextBuggyTimeStr, setNextBuggyTimeStr] = useState('')

  useEffect(() => {
    let mounted = true

    const fetchNextBuggy = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_VMS_URL}/planner/fetch-next-buggy`)

        if (!res.ok) return
        const json = await res.json()
        // response shape: { success: true, data: { success: true, data: [ [ { Timing: "09:37 AM", ... } ] ] } }
        const timing = json?.data?.data?.[0]?.[0]?.Timing || json?.data?.data?.[0]?.[0]?.timing || ''
        if (mounted && timing) setNextBuggyTimeStr(timing)
      } catch (e) {
        console.log(e)
      }
    }

    fetchNextBuggy()
    const id = setInterval(fetchNextBuggy, 60 * 1000) // refresh every 60s
    return () => {
      mounted = false
      clearInterval(id)
    }
  }, [])



  /* Next guided tour */
  const nextTourIdx = useMemo(() => {
    const idx = guidedToursDisplay.findIndex(t => hhmmToMinutes(t.startTime) > clockMin)
    return idx >= 0 ? idx : -1
  }, [clockMin, guidedToursDisplay, hhmmToMinutes])

  // Auto-scroll so the active/next tour card is always visible in the strip.
  useEffect(() => {
    if (nextTourIdx < 0) return
    const tour = guidedToursDisplay?.[nextTourIdx]
    if (!tour) return

    const key = tour?.keyId ?? `${tour?.label ?? tour?.startTime ?? 'tour'}-${nextTourIdx}`
    const el = tourCardRefs.current?.[key]
    if (!el) return

    // IMPORTANT:
    // `element.scrollIntoView()` can trigger *vertical* scrolling if the browser
    // decides the element isn't fully visible in the viewport (common on refresh
    // / data-load when layout shifts). We only want to scroll the horizontal strip.
    const strip = toursStripRef.current
    if (!strip) return

    // Run after layout so refs are attached and sizes are known.
    const id = window.requestAnimationFrame(() => {
      try {
        // Compute the child's center relative to the strip scroll container
        // and scroll only on the X axis.
        const elRect = el.getBoundingClientRect()
        const stripRect = strip.getBoundingClientRect()

        // Distance from strip's left edge to element's left edge, within scrollable content.
        const elLeftInStrip = (elRect.left - stripRect.left) + strip.scrollLeft
        const targetLeft = elLeftInStrip - (strip.clientWidth / 2) + (elRect.width / 2)

        // Clamp within bounds.
        const maxLeft = Math.max(0, strip.scrollWidth - strip.clientWidth)
        const clamped = Math.max(0, Math.min(targetLeft, maxLeft))

        strip.scrollTo({ left: clamped, behavior: 'smooth' })
      } catch (_e) {
        // Fallback: non-smooth.
        try {
          const elRect = el.getBoundingClientRect()
          const stripRect = strip.getBoundingClientRect()
          const elLeftInStrip = (elRect.left - stripRect.left) + strip.scrollLeft
          const targetLeft = elLeftInStrip - (strip.clientWidth / 2) + (elRect.width / 2)
          const maxLeft = Math.max(0, strip.scrollWidth - strip.clientWidth)
          const clamped = Math.max(0, Math.min(targetLeft, maxLeft))
          strip.scrollLeft = clamped
        } catch (_e2) {
          // no-op
        }
      }
    })

    return () => window.cancelAnimationFrame(id)
  }, [nextTourIdx, guidedToursDisplay])

  const selectTime = useCallback((opt) => {
    const title = opt?.title
    

    setPlanTime((prev) => {
      const isSame = prev === title
      setPlanDurationValue(isSame ? '' : (title ?? ''))
      return isSame ? null : title
    })
    setPlan(null)
  }, [])

  const openExploreDetails = useCallback((space) => {
    const post = space?.post
    if (!post) return
    setSelectedExplorePost(post)
    setExploreGalleryIndex(0)
    setExploreFeatureCarouselIndexByKey({})
    setShowExploreDetails(true)
  }, [])

  const closeExploreDetails = useCallback(() => {
    setShowExploreDetails(false)
    // Keep selectedExplorePost as-is for smoother close animation / re-open
  }, [])

  /* Clear selected time if it no longer fits remaining day (keep Overnight) */
  const remainingMins = Math.max(0, endOfDayMin - currentMin)

  // Latest guided-tour start time of the day. If the visitor arrives after this,
  // no day tour can be generated — only an overnight plan makes sense.
  const lastTourStartMin = useMemo(() => {
    const mins = guidedToursDisplay
      .map((tr) => (Number.isFinite(tr?.startMin) ? tr.startMin : hhmmToMinutes(tr?.startTime)))
      .filter((m) => Number.isFinite(m))
    return mins.length ? Math.max(...mins) : -1
  }, [guidedToursDisplay, hhmmToMinutes])
  const noDayTourAvailable = lastTourStartMin >= 0 && currentMin > lastTourStartMin

  useEffect(() => {
    const isOvernight = isOvernightDuration(planDurationValue)
    if (planTime != null && !isOvernight && (noDayTourAvailable || planTime > remainingMins)) {
      setPlanTime(null)
      setPlanDurationValue('')
      setPlan(null)
    }
  }, [currentMin, remainingMins, noDayTourAvailable, planTime, planDurationValue, isOvernightDuration])

  const buildPlan = useCallback(async () => {
    if (!planTime || !planDurationValue) return

    // Use same base as other VMS endpoints via env (DEV/PROD friendly)
    const base = (process.env.REACT_APP_API_VMS_URL ?? '').replace(/\/+$/, '')
    if (!base) return


    // "Post 6 PM" (18:01 sentinel) is sent to the planner as the visitor's
    // current clock time, snapped to the nearest 30-min figure.
    const payloadArrivalTime = arrivalTime === POST_6PM_VALUE
      ? minsToHHMM(currentSnappedArrivalMinutes())
      : arrivalTime

    const payload = {
      arrivalTime: payloadArrivalTime,
      duration: planDurationValue,
    }

    try {
      setIsPlanning(true)
      const res = await fetch(`${base}/planner/plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      const json = await res.json()
      if (!res.ok) {
        // If API fails, hide existing plan + show error toast
        setPlan(null)
        toast.error(json?.message ?? 'Unable to create plan. Please try again.')
        console.log('planner/plan failed', res.status)
        return
      }


      if (json?.isSuccess) {
        setPlan(json?.data ?? null)
      } else {
        // Non-success response should also hide plan + show error toast
        setPlan(null)
        toast.error(
          json?.message
          ?? json?.error?.message
          ?? t?.plannerPlanError
          ?? 'Unable to create plan. Please try again.'
        )
      }
    } catch (e) {
      console.log(e)
      setPlan(null)
      toast.error(t?.plannerPlanError ?? 'Unable to create plan. Please try again.')
    } finally {
      setIsPlanning(false)
    }
  }, [planTime, planDurationValue, arrivalTime, t])

  const mealSubtotal = useMemo(() => {
    const dayPassTotal = dayPassAvailable ? (mealQty[mealDayPassKey] ?? 0) * mealDayPass.price : 0
    const mealsTotal = filteredMeals.reduce((sum, m) => {
      const key = m.key ?? m.name
      return sum + (m.price * (mealQty[key] ?? 0))
    }, 0)
    return dayPassTotal + mealsTotal
  }, [mealQty, filteredMeals, dayPassAvailable, mealDayPassKey, mealDayPass.price])

  const hasMealSelection = useMemo(
    () => (dayPassAvailable && (mealQty[mealDayPassKey] ?? 0) > 0)
      || filteredMeals.some((m) => (mealQty[m.key ?? m.name] ?? 0) > 0),
    [mealQty, filteredMeals, dayPassAvailable, mealDayPassKey]
  )

  useEffect(() => {
    setMealQty((prev) => {
      const next = { ...prev }
      if (!isMealVisible('breakfast')) next.breakfast = 0
      if (!isMealVisible('lunch')) next.lunch = 0
      if (!isMealVisible('dinner')) next.dinner = 0
      if (!isMealVisible('veganBreakfast')) next.veganBreakfast = 0
      if (!isMealVisible('veganLunch')) next.veganLunch = 0
      if (!isMealVisible('veganDinner')) next.veganDinner = 0
      if (!dayPassAvailable) next[mealDayPassKey] = 0
      return next
    })
  }, [isMealVisible, dayPassAvailable, mealDayPassKey])

  const fmtMin = (time) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) return ''
    const [h, m] = time.split(':').map(Number)
    if (Number.isNaN(h) || Number.isNaN(m)) return ''
    const h12 = h % 12 || 12
    const clock = `${h12}:${String(m).padStart(2, '0')}`
    if (lang !== 'gu' && lang !== 'hi') return `${clock} ${h >= 12 ? 'pm' : 'am'}`
    // Natural phrasing: time-of-day word as a PREFIX, e.g. "સવારે 9:00" /
    // "સાંજે 5:00" / "सुबह 9:00" / "शाम 5:00".
    const idx = h >= 4 && h < 12 ? 0 : h >= 12 && h < 16 ? 1 : h >= 16 && h < 20 ? 2 : 3
    const words = lang === 'gu'
      ? ['સવારે', 'બપોરે', 'સાંજે', 'રાત્રે']
      : ['सुबह', 'दोपहर', 'शाम', 'रात']
    return `${words[idx]} ${clock}`
  }
  // Localize a duration label like "1h" / "45 min" / "1h 30m".
  // English is returned unchanged; gu/hi get કલાક/घंटा·घंटे and મિનિટ/मिनट.
  const fmtDuration = (label) => {
    if (!label || typeof label !== 'string') return label || ''
    // English: keep the label but always use the plural "mins" (e.g. "15 min" -> "15 mins").
    if (lang !== 'gu' && lang !== 'hi') return label.replace(/(\d+)\s*min\b/gi, '$1 mins')
    const hMatch = label.match(/(\d+)\s*h/i)
    const mMatch = label.match(/(\d+)\s*m/i)
    if (!hMatch && !mMatch) return label // unknown format — leave as-is
    const hours = hMatch ? parseInt(hMatch[1], 10) : 0
    const mins = mMatch ? parseInt(mMatch[1], 10) : 0
    const minWord = lang === 'gu' ? 'મિનિટ' : 'मिनट'
    const hourWord = lang === 'gu' ? 'કલાક' : (hours === 1 ? 'घंटा' : 'घंटे')
    const parts = []
    if (hours) parts.push(`${hours} ${hourWord}`)
    if (mins) parts.push(`${mins} ${minWord}`)
    return parts.join(' ')
  }
  return (

    <div className="min-h-screen" style={{ background: V4.bg }}>
      <Helmet >
        <title>Shrimad Rajchandra Mission Dharampur - DashBoard</title>{" "}
        {/* Set specific title for EPassPage */}
      </Helmet>
      {/* ═══ STICKY TOP BAR ═══ */}
      <div
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(242,240,235,0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: `1px solid ${V4.border}`,
        }}
      >
        <div className="max-w-lg mx-auto px-5 py-3">
          {/* Logo left, Visitor Dashboard right */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <img src={logo} alt="Ashram" className="h-8 flex-shrink-0" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.06))' }} />
            <span className="font-bold tracking-widest flex-shrink-0" style={{ ...bold, color: V4.textMuted, fontSize: 10, letterSpacing: '0.12em' }}>
              {t("VISITOR DASHBOARD")}
            </span>
          </div>
          {/* Languages — slightly larger font */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {langOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setLang(opt.id)}
                className="px-3 py-2 rounded-lg font-semibold transition-colors"
                style={{
                  fontSize: 14,
                  background: lang === opt.id ? V4.accent : 'transparent',
                  color: lang === opt.id ? '#FFF' : V4.textMuted,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-lg mx-auto px-5 space-y-5 pb-10">

        {/* ─── BOLD HERO HEADER ─── */}
        <div className="pt-8 pb-2 flex items-start justify-between gap-2">
          <div>
            <h1 style={{ ...bold, color: V4.text, fontSize: 40, lineHeight: 0.92 }}>
              {t("YOUR", lang === 'gu' ? 'તમારો' : lang === 'hi' ? 'आपकी' : undefined)}<br />{t("ASHRAM", lang === 'gu' ? 'આશ્રમ' : lang === 'hi' ? 'आश्रम' : undefined)}<br />
              <span style={{ color: V4.accent }}>{t("VISIT", lang === 'gu' ? 'પ્રવાસ' : lang === 'hi' ? 'यात्रा' : undefined)}</span>
            </h1>
            <p className="text-sm mt-3" style={{ color: V4.textMuted }}>
              {visitDate} 
            </p>
          </div>
          <img
            src={chitrapatImage}
            alt=""
            className="rounded-full flex-shrink-0 border-2 shadow-md bg-white"
            style={{
              width: 144,
              height: 144,
              borderColor: V4.accentBorder,
              objectFit: 'contain',
              objectPosition: 'center',
            }}
          />
        </div>

        {/* ─── QR PASS (dark card) — tap to expand, swipe to switch user ─── */}
        {hasPass ? (
        <section
          className="rounded-2xl overflow-hidden touch-manipulation"
          style={{ background: V4.cardDark, boxShadow: V4.shadowMd }}
          onTouchStart={onPassTouchStart}
          onTouchEnd={onPassTouchEnd}
        >
          <button
            type="button"
            onClick={() => setShowQR(!showQR)}
            className="w-full flex items-center gap-5 p-5 touch-manipulation text-left min-h-[88px] active:opacity-95 transition-opacity"
          >
            <div className="w-12 h-12  flex items-center justify-center flex-shrink-0"
            >
              {/* iOS Safari gives a viewBox-only SVG no intrinsic size and collapses it
                  to 0 inside a flex row, so width/height must be explicit. */}
              <svg
                className="w-12 h-12 flex-shrink-0"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="#ffffff"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M4,4h6v6H4V4M20,4v6H14V4h6M14,15h2V13H14V11h2v2h2V11h2v2H18v2h2v3H18v2H16V18H13v2H11V16h3V15m2,0v3h2V15H16M4,20V14h6v6H4M6,6V8H8V6H6M16,6V8h2V6H16M6,16v2H8V16H6M4,11H6v2H4V11m5,0h4v4H11V13H9V11m2-5h2v4H11V6M2,2V6H0V2A2,2,0,0,1,2,0H6V2H2M22,0a2,2,0,0,1,2,2V6H22V2H18V0h4M2,18v4H6v2H2a2,2,0,0,1-2-2V18H2m20,4V18h2v4a2,2,0,0,1-2,2H18V22Z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-base truncate" style={{ color: V4.textOnDark }}>{activeVisitor?.FullName}</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{t("Day Visitor Pass")}</p>
              <p className="text-[11px] mt-2 font-semibold" style={{ color: V4.accent }}>
                {showQR ? t("Tap to hide") : t("Tap to show QR code at gate")}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                style={{ background: V4.accent, color: '#FFF' }}>
                {t("Active")}
              </span>
              <svg className="w-5 h-5 transition-transform duration-300" style={{ color: 'rgba(255,255,255,0.5)', transform: showQR ? 'rotate(180deg)' : '' }}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {visitors.length > 1 && (
            <div className="flex items-center justify-center gap-2 px-4 pb-2">
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{t("Swipe to switch pass")}</span>
              <div className="flex gap-1.5">
                {visitors.map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full transition-all"
                    style={{
                      width: activeVisitorIndex === i ? 10 : 6,
                      height: 6,
                      background: activeVisitorIndex === i ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="grid transition-[grid-template-rows] duration-300 ease-in-out" style={{ gridTemplateRows: showQR ? '1fr' : '0fr' }}>
            <div className="overflow-hidden min-h-0">
              <div className="px-4 pb-6 pt-1 flex flex-col items-center">
                <img
                  src={activeVisitor?.qrString}
                  alt="Scanner"
                  className="w-44 sm:w-42 h-42 cursor-pointer"
                />
                <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>{t("Show this at the gate")}</p>
              </div>
            </div>
          </div>
        </section>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/entry')}
            className="w-full flex items-center justify-center gap-2.5 py-4 rounded-full font-bold active:scale-[0.97] transition-all touch-manipulation"
            style={{ ...bold, fontSize: 14, letterSpacing: '0.06em', lineHeight: 1, background: V4.accent, color: '#FFF', boxShadow: '0 4px 16px rgba(212,98,43,0.25)' }}
          >
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M4,4h6v6H4V4M20,4v6H14V4h6M14,15h2V13H14V11h2v2h2V11h2v2H18v2h2v3H18v2H16V18H13v2H11V16h3V15m2,0v3h2V15H16M4,20V14h6v6H4M6,6V8H8V6H6M16,6V8h2V6H16M6,16v2H8V16H6M4,11H6v2H4V11m5,0h4v4H11V13H9V11m2-5h2v4H11V6M2,2V6H0V2A2,2,0,0,1,2,0H6V2H2M22,0a2,2,0,0,1,2,2V6H22V2H18V0h4M2,18v4H6v2H2a2,2,0,0,1-2-2V18H2m20,4V18h2v4a2,2,0,0,1-2,2H18V22Z"></path></svg>
            {t("Get my QR")}
          </button>
        )}

        {/* ─── QUICK LINKS STRIP (V4 theme) ─── */}
        <div className="flex gap-2.5 overflow-x-auto scrollbar-hide -mx-1 px-1 py-1.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {tDeep(EN.quickLinks).map(link => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="flex-shrink-0 px-4 py-2.5 rounded-full text-[12px] font-bold tracking-wide transition-all duration-200 active:scale-95 touch-manipulation whitespace-nowrap"
              style={{
                background: activeLink === link.id ? V4.accent : V4.card,
                color: activeLink === link.id ? '#FFF' : V4.text,
                border: `1px solid ${activeLink === link.id ? 'transparent' : V4.border}`,
                boxShadow: activeLink === link.id ? `0 2px 12px ${V4.accent}40` : V4.shadow,
              }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* ─── RIGHT NOW + WISDOM (2 col) ─── */}
        {/* <div className="grid grid-cols-2 gap-3">
         <div className="rounded-2xl p-4 flex flex-col justify-between" style={{ background: V4.cardDark, minHeight: 170 }}>
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <span style={{ ...bold, color: V4.textOnDark, fontSize: 11, letterSpacing: '0.08em' }}>{t("RIGHT NOW")}</span>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: V4.accent }} />
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: V4.accent }} />
                </span>
              </div>
              {liveInfo.current ? (
                <div>
                  <p style={{ ...bold, color: V4.textOnDark, fontSize: 20, lineHeight: 1.1 }}>
                    {liveInfo.current.label}
                  </p>
                  {liveInfo.current.from && (
                    <p className="text-[11px] mt-1.5 font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {t("Starting from")} {liveInfo.current.from}
                    </p>
                  )}
                </div>
              ) : (
                <p style={{ ...bold, color: 'rgba(255,255,255,0.4)', fontSize: 16, lineHeight: 1.1 }}>
                  {t("Free time") ?? 'FREE TIME'}
                </p>
              )}
            </div>
            {liveInfo.next && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{t("NEXT UP")}</p>
                <p className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {liveInfo.next.label} · {liveInfo.next.time}
                </p>
                {liveInfo.next.from && (
                  <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {t("From")} {liveInfo.next.from}
                  </p>
                )}
              </div>
            )}
          </div>

         <div className="rounded-2xl p-4 flex flex-col justify-between" style={{ background: V4.accent, minHeight: 170 }}>
            <p style={{ ...bold, color: '#FFFFFF', fontSize: 12, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
              "{wisdomText}"
            </p>
            <p className="text-[10px] mt-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
              — {wisdomSource}
            </p>
          </div>
        </div> */}

        {/* ─── PLAN YOUR VISIT ─── */}
        <section ref={planRef} className="scroll-mt-4">
          <p className="mb-4" style={{ ...bold, color: V4.text, fontSize: 28, lineHeight: 1 }}>
            {t("PLAN YOUR")} <span style={{ color: V4.accent }}>{t("VISIT")}</span>
          </p>

          <div className="rounded-2xl p-5" style={{ background: V4.card, boxShadow: V4.shadow, border: `1px solid ${V4.border}` }}>
            <p className="mb-2" style={{ ...bold, color: V4.textMuted, fontSize: 15, letterSpacing: '0.08em' }}>
              {t("WHEN ARE YOU ARRIVING?")}
            </p>

            <div
              className="w-full rounded-xl p-2"
              style={{ border: `1.5px solid ${V4.border}`, background: V4.card }}
            >
              <div className="flex items-center gap-2">
                <TimeDropdown
                  ariaLabel="Arrival time"
                  value={arrivalTime}
                  onChange={(val) => {
                    setArrivalTime(String(val))
                    setPlan(null)
                  }}
                  items={arrivalSlotOptions}
                  selectedLabelOverride={arrivalTime === POST_6PM_VALUE ? arrivalTimeLabel : undefined}
                  buttonStyle={{
                    background: V4.bg,
                    color: V4.text,
                    border: `1px solid ${V4.border}`,
                  }}
                />
              </div>
            </div>
            <p className="my-3" style={{ ...bold, color: V4.text, fontSize: 11, letterSpacing: '0.08em' }}>{t("FOR HOW LONG?")}</p>
            <div className="flex gap-2 mb-5">
              {plannerDurationOptions.map(opt => {
                const isOvernight = isOvernightDuration(opt?.title)
                // Disable every non-overnight duration once no day tour can be
                // generated (arrival past the last guided tour), or it can't fit
                // the remaining day. Only OVERNIGHT stays selectable.
                const disabled = !isOvernight && (noDayTourAvailable || opt.duration > remainingMins)
                const active = planTime === opt.title && !disabled
                return (
                  <button
                    key={String(opt.mins)}
                    type="button"
                    onClick={() => selectTime(opt)}
                    disabled={disabled}
                    className="flex-1 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 touch-manipulation disabled:cursor-not-allowed"
                    style={{
                      ...bold, fontSize: 12, letterSpacing: '0.02em', lineHeight: 1,
                      background: disabled ? V4.bg : (active ? V4.cardDark : V4.bg),
                      color: disabled ? V4.textDim : (active ? V4.textOnDark : V4.text),
                      border: `1px solid ${disabled ? V4.border : (active ? V4.cardDark : V4.border)}`,
                      opacity: disabled ? 0.6 : 1,
                    }}>
                    {isOvernight ? t('OVERNIGHT') : opt.title}
                  </button>
                )
              })}
            </div>

            <button onClick={buildPlan} disabled={!planTime || isPlanning}
              className="w-full py-3.5 rounded-full font-bold text-sm active:scale-[0.97] transition-all disabled:opacity-25"
              style={{
                ...bold, fontSize: 13, letterSpacing: '0.06em', lineHeight: 1,
                background: planTime ? V4.accent : V4.bg, color: planTime ? '#FFF' : V4.textDim,
                boxShadow: planTime ? '0 4px 16px rgba(212,98,43,0.25)' : 'none'
              }}>
              {isPlanning ? (t("Processing…") ?? 'Processing...') : t("CREATE MY TOUR")}
            </button>

            {/* Itinerary (Planner API) */}
            {plan && (
              <div className="mt-5 pt-5" style={{ borderTop: `1px solid ${V4.border}` }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <p style={{ ...bold, color: V4.text, fontSize: 12 }}>
                      {t("Your Recommended Tour")}
                    </p>
                    <button
                      type="button"
                      onClick={() => handlePlanPdf('download')}
                      disabled={isDownloadingPdf}
                      aria-label="Download plan as PDF"
                      className="p-1 rounded-md active:scale-95 transition-transform disabled:opacity-50"
                      style={{ color: V4.accent }}
                    >
                      {pdfAction === 'download' ? (
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePlanPdf('print')}
                      disabled={isDownloadingPdf}
                      aria-label="Print plan"
                      className="p-1 rounded-md active:scale-95 transition-transform disabled:opacity-50"
                      style={{ color: V4.accent }}
                    >
                      {pdfAction === 'print' ? (
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 6 2 18 2 18 9" />
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                          <rect x="6" y="14" width="12" height="8" />
                        </svg>
                      )}
                    </button>
                  </div>
                
                </div>

                {planT?.alertMessage && (
                  <div className="rounded-xl p-4 mb-4" style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}>
                    <p className="text-xs font-semibold" style={{ color: V4.text }}>{planT.alertMessage}</p>
                  </div>
                )}

                {/* Overnight (new API shape): { isOvernight: true, sections: [...] } */}
                {planT?.isOvernight && Array.isArray(planT?.sections) && planT.sections.length > 0 ? (
                  <div className="space-y-5">
                    {planT.sections.map((section, sIdx) => (
                      <div key={`${section?.label ?? 'section'}-${sIdx}`}>
                        {section?.label && (
                          <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: V4.textMuted }}>
                            {section.label}
                          </p>
                        )}
                        <div className="space-y-4">
                          {
                            (section?.items ?? []).map((item, iIdx) => {
                              console.log({ item })
                              const badgeStyle = {
                                must: { bg: 'rgba(212,98,43,0.12)', color: V4.accent },
                                tour: { bg: 'rgba(62,109,142,0.12)', color: '#3E6D8E' },
                                act: { bg: V4.sageBg, color: V4.sage },
                                meal: { bg: 'rgba(192,138,36,0.15)', color: '#C08A24' },
                                vid: { bg: 'rgba(127,119,221,0.12)', color: '#5C54A8' },
                                nearby: { bg: 'rgba(156,79,134,0.12)', color: '#9C4F86' },
                                free: { bg: 'rgba(8,145,178,0.12)', color: '#0891B2' },
                              }
                              const style = badgeStyle[item.badge] || badgeStyle.free
                              return (
                                <div key={iIdx} className="rounded-xl overflow-hidden shadow-lg" style={{ background: V4.card, border: `1px solid ${V4.border}` }}>
                                  <div className="flex items-center gap-2 px-4 py-3" style={{ background: V4.bg, borderBottom: `1px solid ${V4.border}` }}>
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: style.color }} />
                                    <span className="text-md font-bold flex-1 gap-2" >

                                      <span style={{ color: V4.text }}>{item.title}</span>
                                    </span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: style.bg, color: style.color }}>
                                      {t(item.badge === 'must' ? 'Must visit' : item.badge === 'tour' ? 'Ashram tour' : item.badge === 'act' ? 'Activity' : item.badge === 'meal' ? 'Meal' : item.badge === 'vid' ? 'Video' :  item.badge === 'nearby' ? 'Near By Place' : item.badge === 'free' ? 'Free time' : 'Free')}
                                    </span>
                                  </div>
                                  {/* Time + duration on one line; the venue/description gets its own
                                      full-width line below so long API text stays readable. */}
                                  <div className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm tabular-nums" style={{ color: V4.textMuted }}>{fmtMin(item.startTime)} - {fmtMin(item.endTime)}</span>
                                      {item.badge === 'tour' && (
                                        <button
                                          type="button"
                                          onClick={() => setShowTourTimesModal(true)}
                                          aria-label={t('Guided tour timings') ?? 'Guided tour timings'}
                                          className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center active:opacity-70 transition-opacity"
                                          style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                                        >
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <circle cx="12" cy="12" r="9" /><path d="M12 16v-4" /><path d="M12 8h.01" />
                                          </svg>
                                        </button>
                                      )}
                                      {item.durationLabel && (
                                        <span className="text-[12px] flex-shrink-0 ml-auto" style={{ color: V4.textMuted }}>{fmtDuration(item.durationLabel)}</span>
                                      )}
                                    </div>
                                    {item.badge === 'tour' && tourStartingPoint(item.venue) && (
                                      <p className="text-[12px] font-semibold mt-1.5" style={{ color: V4.textMuted }}>
                                        {t('Starting point')}: <span style={{ color: V4.text }}>{t(tourStartingPoint(item.venue))}</span>
                                      </p>
                                    )}
                                    {item.venue && (
                                      <div
                                        className="text-sm mt-1.5 leading-snug break-words"
                                        style={{ color: V4.textDim }}
                                        dangerouslySetInnerHTML={{ __html: item.venue }}
                                      />
                                    )}
                                    {item.badge === 'nearby' && item.note && (
                                      <div className="mt-1.5">
                                        <NearbyMapLink url={item.note} compact label={t('Map') ?? 'Map'} />
                                      </div>
                                    )}
                                  </div>

                                  {item.badge === 'meal' && (plan?.sections?.[sIdx]?.label ?? section?.label ?? '').toString().trim().toLowerCase() !== 'tomorrow' && (
                                    <div className="px-4 pb-3 -mt-1 pt-2 border-t mx-2 border-gray-200">
                                      <button
                                        type="button"
                                        onClick={openMealModal}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                        style={{ background: V4.accent, color: '#FFF' }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                        </svg>
                                        {t("Click to buy meal") ?? 'Click to buy meal'}
                                      </button>
                                    </div>
                                  )}

                                  {/orientation/i.test(plan?.sections?.[sIdx]?.items?.[iIdx]?.title ?? '') && (
                                    <div className="px-4 pb-3 -mt-1 flex flex-wrap gap-2 pt-2 border-t mx-2 border-gray-200">
                                      <button
                                        type="button"
                                        onClick={() => setShowBuggyFinder(true)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                        style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                        </svg>
                                        {t("Buggy Finder") ?? 'Buggy Finder'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={openBuggyModal}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                        style={{ background: V4.cardDark, color: V4.textOnDark }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M2 9V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z" /><path d="M13 5v14" />
                                        </svg>
                                        {t("Buy Buggy Coupon") ?? 'Buy Buggy Coupon'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={openMealModal}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                        style={{ background: V4.accent, color: '#FFF' }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                        </svg>
                                        {t("Buy Meal Pass") ?? 'Buy Meal Pass'}
                                      </button>
                                    </div>
                                  )}

                                </div>
                              )
                            })}
                        </div>

                      </div>
                    ))}

                    {/* An important activity just after the plan ends */}
                    <ExtraSuggestionCard items={planT?.extra_suggestion_point} t={t} fmtTime={fmtMin} fmtDuration={fmtDuration} />

                    <div className="flex flex-wrap gap-3 pt-2" style={{ color: V4.textMuted }}>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: V4.accent }} /><span>{t('Must visit')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#3E6D8E' }} /><span>{t('Ashram tour')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: V4.sage }} /><span>{t('Activity')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#C08A24' }} /><span>{t('Meal')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#5C54A8' }} /><span>{t('Video')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#9C4F86' }} /><span>{t('Near By Place')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#0891B2' }} /><span>{t('Free time')}</span></span>
                    </div>

                    {/* Overnight stays have fixed ashram check-in / checkout times
                        (OVERNIGHT_CHECKIN/CHECKOUT_TIME_IN_24_HRS in the planner config). */}
                    {planT?.isOvernight && (overnightCheckinTime || overnightCheckoutTime) && (
                      <div
                        className="flex items-start gap-2 rounded-xl px-4 py-3"
                        style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={V4.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                        </svg>
                        <div className="text-[13px] font-semibold leading-snug" style={{ color: V4.text }}>
                          {overnightCheckinTime && (
                            <p>{t('Check-in from')} <span className="tabular-nums">{fmtMin(overnightCheckinTime)}</span></p>
                          )}
                          {overnightCheckoutTime && (
                            <p>{t('Check-out by')} <span className="tabular-nums">{fmtMin(overnightCheckoutTime)}</span></p>
                          )}
                        </div>
                      </div>
                    )}

                    {(Array.isArray(planT?.exitRecommendations) && planT.exitRecommendations.length > 0) && (
                      <div className="rounded-xl p-4" style={{ background: V4.bg, border: `1px solid ${V4.border}` }}>
                        <p style={{ ...bold, color: V4.text, fontSize: 11, letterSpacing: '0.06em' }}>
                          {t('Exit Recommendations')}
                        </p>
                        <div className="mt-3 space-y-3">
                          {planT.exitRecommendations.map((rec, rIdx) => (
                            <div key={`${rec?.name ?? 'rec'}-${rIdx}`}>
                              <p className="text-sm font-bold" style={{ color: V4.text }}>
                                {(rec?.name ?? '').toString()}
                                {rec?.durationRange ? (
                                  <span className="text-[11px] font-semibold" style={{ color: V4.textDim }}>{` · ${rec.durationRange}`}</span>
                                ) : null}
                              </p>
                              {rec?.note && (
                                <p className="text-[11px] mt-0.5" style={{ color: V4.textMuted }}>
                                  {(rec.note ?? '').toString()}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        {planT?.checkoutTime && (
                          <p className="text-[11px] mt-4" style={{ color: V4.textMuted }}>
                            {(t('Checkout time'))}: <span className="font-semibold" style={{ color: V4.text }}>{planT.checkoutTime}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Nearby places, shown above Book Stay */}
                    <NearbyPlacesTable places={planT?.nearbyPlaces} hideIndices={duplicateNearbyIndices} t={t} fmtDuration={fmtDuration} />

                    <div className="rounded-xl p-4" style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}>
                      <p className="text-sm font-semibold mb-3" style={{ color: V4.text }}>{t("Book your stay at the ashram")}</p>
                      <button
                        type="button"
                        onClick={() => {
                          window.open('https://www.srmd.org/stay', '_blank', 'noopener,noreferrer')
                        }}
                        className="w-full py-3.5 rounded-full font-bold text-sm active:scale-[0.97] transition-all"
                        style={{ ...bold, fontSize: 13, letterSpacing: '0.06em', lineHeight: 1, background: V4.accent, color: '#FFF', boxShadow: '0 4px 16px rgba(212,98,43,0.25)' }}>
                        {t("BOOK STAY")}
                      </button>
                    </div>
                    {
                      planT?.tourMealReminder && (
                        <div className="rounded-xl p-4" style={{ background: V4.sageBg, border: `1px solid ${V4.sageBorder}` }}>

                          <p className="text-[15px]" style={{ color: V4.sageText }}>
                            <div dangerouslySetInnerHTML={{ __html: planT.tourMealReminderMessage }} />
                          </p>
                        </div>
                      )
                    }
                    <div className="flex flex-col gap-3">
                      <button
                        type="button"
                        onClick={() => { setPlan(null); setPlanTime(null); scrollTo('plan') }}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold mt-2"
                        style={{ background: V4.card, border: `1.5px solid ${V4.border}`, color: V4.textMuted }}>
                        ← {t("Plan another visit")}
                      </button>
                      {/* Print / Download sit side by side on their own row, under
                          the full-width "Plan another visit". */}
                      <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handlePlanPdf('print')}
                        disabled={isDownloadingPdf}
                        aria-label="Print plan"
                        className="w-full py-2.5 rounded-xl text-sm font-semibold"
                        style={{ background: V4.accentBg, border: `1.5px solid ${V4.border}`, color: V4.textMuted }}
                      >
                        {pdfAction === 'print' ?
                          'Loading...'
                          :
                          <div className='flex gap-2 items-center justify-center'>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 6 2 18 2 18 9" />
                              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                              <rect x="6" y="14" width="12" height="8" />
                            </svg> {t("Print")}</div>
                        }
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePlanPdf('download')}
                        disabled={isDownloadingPdf}
                        aria-label="Download plan as PDF"
                        className="w-full py-2.5 rounded-xl text-sm font-semibold"
                        style={{ background: V4.accentBg, border: `1.5px solid ${V4.border}`, color: V4.textMuted }}
                      >
                        {pdfAction === 'download' ?
                          'Loading...'
                          :
                          <div className='flex gap-2 items-center justify-center'>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>  {t("Download PDF")}</div>
                        }
                      </button>
                      </div>
                    </div>
                  </div>

                ) : Array.isArray(planT?.schedule) && planT.schedule.length > 0 ? (
                  <div className="space-y-5">

                    {planT.schedule.map((item, i) => {
                      const badgeStyle = {
                        must: { bg: 'rgba(212,98,43,0.12)', color: V4.accent },
                        tour: { bg: 'rgba(62,109,142,0.12)', color: '#3E6D8E' },
                        act: { bg: V4.sageBg, color: V4.sage },
                        meal: { bg: 'rgba(192,138,36,0.15)', color: '#C08A24' },
                        vid: { bg: 'rgba(127,119,221,0.12)', color: '#5C54A8' },
                        nearby: { bg: 'rgba(156,79,134,0.12)', color: '#9C4F86' },
                        free: { bg: 'rgba(8,145,178,0.12)', color: '#0891B2' },
                      }
                      const style = badgeStyle[item.badge] || badgeStyle.free
                      return (
                        <div key={i} className="rounded-xl overflow-hidden shadow-lg" style={{ background: V4.card, border: `1px solid ${V4.border}` }}>
                          <div className="flex items-center gap-2 px-4 py-3" style={{ background: V4.bg, borderBottom: `1px solid ${V4.border}` }}>
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: style.color }} />
                            <span className="text-md font-bold flex-1" style={{ color: V4.text }}>{item.title}</span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: style.bg, color: style.color }}>
                              {t(item.badge === 'must' ? 'Must visit' : item.badge === 'tour' ? 'Ashram tour' : item.badge === 'act' ? 'Activity' : item.badge === 'meal' ? 'Meal' : item.badge === 'vid' ? 'Video' : item.badge === 'nearby' ? 'Near By Place' : item.badge === 'free' ? 'Free time' : 'Free')}
                            </span>
                          </div>
                          {/* Time + duration on one line; the venue/description gets its own
                              full-width line below so long API text stays readable. */}
                          <div className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm tabular-nums" style={{ color: V4.textMuted }}>{fmtMin(item.startTime)} – {fmtMin(item.endTime)}</span>
                              {item.badge === 'tour' && (
                                <button
                                  type="button"
                                  onClick={() => setShowTourTimesModal(true)}
                                  aria-label={t('Guided tour timings') ?? 'Guided tour timings'}
                                  className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center active:opacity-70 transition-opacity"
                                  style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" /><path d="M12 16v-4" /><path d="M12 8h.01" />
                                  </svg>
                                </button>
                              )}
                              {item.durationLabel && (
                                <span className="text-[12px] flex-shrink-0 ml-auto" style={{ color: V4.textMuted }}>{fmtDuration(item.durationLabel)}</span>
                              )}
                            </div>
                            {item.badge === 'tour' && tourStartingPoint(item.venue) && (
                              <p className="text-[12px] font-semibold mt-1.5" style={{ color: V4.textMuted }}>
                                {t('Starting point')}: <span style={{ color: V4.text }}>{t(tourStartingPoint(item.venue))}</span>
                              </p>
                            )}
                            {item.venue && (
                              <div
                                className="text-sm mt-1.5 leading-snug break-words"
                                style={{ color: V4.textDim }}
                                dangerouslySetInnerHTML={{ __html: item.venue }}
                              />
                            )}
                            {item.badge === 'nearby' && item.note && (
                              <div className="mt-1.5">
                                <NearbyMapLink url={item.note} compact label={t('Map') ?? 'Map'} />
                              </div>
                            )}
                          </div>
                          {item.badge === 'meal' && (
                            <div className="px-4 pb-3 -mt-1 pt-2 border-t mx-2 border-gray-200">
                              <button
                                type="button"
                                onClick={openMealModal}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                style={{ background: V4.accent, color: '#FFF' }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                                {t("Click to buy meal") ?? 'Click to buy meal'}
                              </button>
                            </div>
                          )}

                          {/orientation/i.test(plan?.schedule?.[i]?.title ?? '') && (
                            <div className="px-4 pb-3 -mt-1 flex flex-wrap gap-2 pt-2 border-t mx-2 border-gray-200">
                              <button
                                type="button"
                                onClick={() => setShowBuggyFinder(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                </svg>
                                {t("Buggy Finder") ?? 'Buggy Finder'}
                              </button>
                              <button
                                type="button"
                                onClick={openBuggyModal}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                style={{ background: V4.cardDark, color: V4.textOnDark }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M2 9V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z" /><path d="M13 5v14" />
                                </svg>
                                {t("Buy Buggy Coupon") ?? 'Buy Buggy Coupon'}
                              </button>
                              <button
                                type="button"
                                onClick={openMealModal}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold active:scale-95 transition-transform"
                                style={{ background: V4.accent, color: '#FFF' }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                                {t("Buy Meal Pass") ?? 'Buy Meal Pass'}
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {/* An important activity just after the plan ends */}
                    <ExtraSuggestionCard items={planT?.extra_suggestion_point} t={t} fmtTime={fmtMin} fmtDuration={fmtDuration} />

                    <div className="flex flex-wrap gap-3 pt-2" style={{ color: V4.textMuted }}>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: V4.accent }} /><span>{t('Must visit')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#3E6D8E' }} /><span>{t('Ashram tour')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: V4.sage }} /><span>{t('Activity')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#C08A24' }} /><span>{t('Meal')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#5C54A8' }} /><span>{t('Video')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#9C4F86' }} /><span>{t('Near By Place')}</span></span>
                      <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ background: '#0891B2' }} /><span>{t('Free time')}</span></span>
                    </div>

                    {/* Overnight stays have fixed ashram check-in / checkout times
                        (OVERNIGHT_CHECKIN/CHECKOUT_TIME_IN_24_HRS in the planner config). */}
                    {planT?.isOvernight && (overnightCheckinTime || overnightCheckoutTime) && (
                      <div
                        className="flex items-start gap-2 rounded-xl px-4 py-3"
                        style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={V4.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                        </svg>
                        <div className="text-[13px] font-semibold leading-snug" style={{ color: V4.text }}>
                          {overnightCheckinTime && (
                            <p>{t('Check-in from')} <span className="tabular-nums">{fmtMin(overnightCheckinTime)}</span></p>
                          )}
                          {overnightCheckoutTime && (
                            <p>{t('Check-out by')} <span className="tabular-nums">{fmtMin(overnightCheckoutTime)}</span></p>
                          )}
                        </div>
                      </div>
                    )}
                    {
                      planT?.tourMealReminder && (
                        <div className="rounded-xl p-4 font-medium" style={{ background: V4.accentBg, border: `1px solid ${V4.sageBorder}` }}>

                          <p className="text-[15px]" style={{ color: V4.sageText }}>
                            <div dangerouslySetInnerHTML={{ __html: planT.tourMealReminderMessage }} />
                            
                          </p>
                        </div>
                      )
                    }

                    {/* Nearby places, shown above Book Stay */}
                    <NearbyPlacesTable places={planT?.nearbyPlaces} hideIndices={duplicateNearbyIndices} t={t} fmtDuration={fmtDuration} />

                    <div className="rounded-xl p-4" style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}>
                      <p className="text-sm font-semibold mb-3" style={{ color: V4.text }}>{t("Book your stay at the ashram")}</p>
                      <button
                        type="button"
                        onClick={() => {
                          window.open('https://www.srmd.org/stay', '_blank', 'noopener,noreferrer')
                        }}
                        className="w-full py-3.5 rounded-full font-bold text-sm active:scale-[0.97] transition-all"
                        style={{ ...bold, fontSize: 13, letterSpacing: '0.06em', lineHeight: 1, background: V4.accent, color: '#FFF', boxShadow: '0 4px 16px rgba(212,98,43,0.25)' }}>
                        {t("BOOK STAY")}
                      </button>
                    </div>
                    <div className="flex flex-col gap-3">
                      <button
                        type="button"
                        onClick={() => { setPlan(null); setPlanTime(null); scrollTo('plan') }}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold mt-2"
                        style={{ background: V4.card, border: `1.5px solid ${V4.border}`, color: V4.textMuted }}>
                        ← {t("Plan another visit")}
                      </button>
                      {/* Print / Download sit side by side on their own row, under
                          the full-width "Plan another visit". */}
                      <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handlePlanPdf('print')}
                        disabled={isDownloadingPdf}
                        aria-label="Print plan"
                        className="w-full py-2.5 rounded-xl text-sm font-semibold"
                        style={{ background: V4.accentBg, border: `1.5px solid ${V4.border}`, color: V4.textMuted }}
                      >
                        {pdfAction === 'print' ?
                          'Loading...'
                          :
                          <div className='flex gap-2 items-center justify-center'>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 6 2 18 2 18 9" />
                              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                              <rect x="6" y="14" width="12" height="8" />
                            </svg> {t("Print")}</div>
                        }
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePlanPdf('download')}
                        disabled={isDownloadingPdf}
                        aria-label="Download plan as PDF"
                        className="w-full py-2.5 rounded-xl text-sm font-semibold"
                        style={{ background: V4.accentBg, border: `1.5px solid ${V4.border}`, color: V4.textMuted }}
                      >
                        {pdfAction === 'download' ?
                          'Loading...'
                          :
                          <div className='flex gap-2 items-center justify-center'>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg> {t("Download PDF")}</div>
                        }
                      </button>
                      </div>
                    </div>
                  </div>

                ) : (
                  <p className="text-xs" style={{ color: V4.textMuted }}>
                    {t('No plan available.')}
                  </p>
                )}
              </div>
            )}


          </div>
        </section>

        {/* ─── GUIDED TOURS ─── */}
        <section ref={toursRef} className="scroll-mt-4">
          <p className="mb-4" style={{ ...bold, color: V4.text, fontSize: 13, letterSpacing: '0.08em' }}>
            {t("GUIDED TOURS")} <span style={{ fontWeight: 400, color: V4.textMuted, textTransform: 'none', letterSpacing: 0 }}>· {t("45 mins from Welcome Centre")}</span>
          </p>

          <div
            ref={toursStripRef}
            className="flex gap-3 overflow-x-auto pb-2 px-5 snap-x scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {guidedToursDisplay.map((tour, i) => {
              // NOTE: `tour.startTime` can be string ("09:00") from API or label-based.
              // We always compare using minutes.
               const startMin = hhmmToMinutes(tour?.startTime)
              const hasTime = Number.isFinite(startMin)
              const isNext = hasTime && i === nextTourIdx
              const isPast = hasTime && nextTourIdx >= 0 ? i < nextTourIdx : false
              const _key = tour?.keyId ?? `${tour?.label ?? tour?.startTime ?? 'tour'}-${i}`
              return (
                <div
                  key={_key}
                  ref={(node) => {
                    if (!node) {
                      // cleanup when unmounting
                      if (tourCardRefs.current) delete tourCardRefs.current[_key]
                      return
                    }
                    tourCardRefs.current[_key] = node
                  }}
                  className="flex-shrink-0 w-[130px] rounded-2xl p-4 snap-start relative"
                  style={{
                    background: isNext ? V4.cardDark : V4.card,
                    border: `1px solid ${isNext ? V4.accent : V4.border}`,
                    opacity: isPast ? 0.4 : 1,
                    boxShadow: isNext ? `0 0 0 2px ${V4.accent}33, ${V4.shadowMd}` : V4.shadow,
                  }}>
                  {/* {isNext && (
                    <span
                      className="absolute -top-2 -right-2 text-[9px] font-extrabold px-2 py-1 rounded-full"
                      style={{ background: V4.accent, color: '#fff', letterSpacing: '0.12em' }}
                    >
                      {t('NEXT UP')}
                    </span>
                  )} */}
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2"
                    style={{ color: isNext ? 'rgba(255,255,255,0.4)' : V4.textDim }}>{t("Tour")} {i + 1}</p>
                  <p style={{ ...bold, fontSize: 24, color: isNext ? V4.textOnDark : V4.text }}>
                    {fmtMin(tour.startTime)}
                  </p>
                  {/* {tour?.label && (
                    <p className="text-[10px] -mt-0.5" style={{ color: isNext ? 'rgba(255,255,255,0.45)' : V4.textDim }}>
                      {(tour.label ?? '').toString()}
                    </p>
                  )} */}
                  <p className="text-[11px] mt-0.5" style={{ color: isNext ? 'rgba(255,255,255,0.5)' : V4.textMuted }}>{t("45 mins")}</p>
                </div>
              )
            })}
          </div>

          {/* Arrival note: the orientation runs before the tour departs, so a
              visitor who shows up at the tour time has already missed it. */}
          <div
            className="mt-2 rounded-xl p-3.5 flex items-center gap-1"
            style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke={V4.accent}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 mt-0.5"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <div className="min-w-0">
              <p className="text-[13px] font-bold mt-0.5 leading-snug" style={{ color: V4.text }}>
                {t('Please arrive 15 minutes before your guided tour. If your tour starts at 9:00 am, please arrive by 8:45 am for orientation.')}
              </p>
            </div>
          </div>
        </section>

        {/* ─── BUGGY (light card) ─── */}
        <section ref={buggyRef} className="scroll-mt-4 rounded-2xl p-5" style={{ background: V4.card, boxShadow: V4.shadow, border: `1px solid ${V4.border}` }}>
          <div className="flex items-center justify-between mb-2">
            <p style={{ ...bold, color: V4.text, fontSize: 13, letterSpacing: '0.08em' }}>{t("getting around")}</p>
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: V4.bg, color: V4.textMuted }}>{t("EVERY 15 MINS")}</span>
          </div>
          <p className="text-sm mb-4" style={{ color: V4.textMuted }}>
            {t("Buggy from Welcome Centre")} at <span className="font-semibold" style={{ color: V4.text }}>{toLowerAmPm(nextBuggyTimeStr)}</span>
          </p>

          <ScrollableStrip
            wrapperClassName="mb-4"
            ariaLabel="Buggy stops"
            className="rounded-md p-3 mx-4 overflow-x-auto scrollbar-hide"
            style={{ background: V4.bg, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex items-center min-w-max mx-4">
              {buggyStops.map((stop, i) => (
                <div key={i} className="flex items-center">
                  <button
                    type="button"
                    // onClick={() => setActiveBuggyStopIndex(i)}
                    className="flex flex-col items-center w-[58px] active:opacity-80 transition-opacity"
                  >
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: i === activeBuggyStopIndex ? V4.accent : V4.cardDark }} />
                    <span className="text-[10px] mt-1 font-bold text-center leading-tight  whitespace-nowrap" style={{ color: i === activeBuggyStopIndex ? V4.text : V4.textMuted }}>{t(stop)}</span>
                  </button>
                  {i < buggyStops.length - 1 && (
                    <div
                      className="w-12 h-[1.5px] self-start mt-[5px]"
                      style={{ background: i < activeBuggyStopIndex ? V4.accentBorder : V4.border }}
                    />
                  )}
                </div>
              ))}
            </div>
          </ScrollableStrip>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={openBuggyModal}
              className="flex-1 py-3 rounded-full font-bold text-sm active:scale-[0.97] transition-transform"
              style={{ background: V4.cardDark, color: V4.textOnDark }}
            >
              {t("Buy Buggy Coupon")}
            </button>
            {/* Same Buggy Finder entry point as the orientation card, sized to sit
                beside Buy Buggy Coupon. */}
            <button
              type="button"
              onClick={() => setShowBuggyFinder(true)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-full font-bold text-sm active:scale-[0.97] transition-transform"
              style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
              </svg>
              {t("Buggy Finder") ?? 'Buggy Finder'}
            </button>
            {/* <button className="flex-1 py-3 rounded-full font-bold text-sm active:scale-[0.97] transition-transform"
              type="button"
              onClick={openRouteMapModal}
              style={{ background: V4.bg, color: V4.text, border: `1px solid ${V4.border}` }}>{t("Route Map")}</button> */}
          </div>
        </section>

        {/* ─── MEALS (dark card) ─── */}
        <section ref={mealsRef} className="scroll-mt-4 rounded-2xl p-4" style={{ background: V4.cardDark, boxShadow: V4.shadowMd }}>
          <p className="mb-2" style={{ ...bold, color: V4.textOnDark, fontSize: 11, letterSpacing: '0.08em' }}>
            {t("FOOD & DINING")}
          </p>
          <div className="rounded-xl overflow-hidden mb-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
            {filteredMeals.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {t("No Meals Available At This Time")}
              </div>
            ) : (
              filteredMeals.map((m, i) => (
                <div key={i} className="px-3 py-2"
                  style={{ borderBottom: i < filteredMeals.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: V4.textOnDark }}>{m.name}</span>
                    <span className="text-xs tabular-nums" style={{ color: 'rgba(255,255,255,0.5)' }}>{m.time}</span>
                  </div>
                  {m.note && (
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>* {m.note}</p>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="mb-2" style={{ color: V4.textOnDark, fontSize: 15 }}>
            {t("The cafeteria is also available for visitors and remains open from 8 am to sunset time.")}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={openMealModal}
              className="flex-1 py-2.5 rounded-full font-bold text-xs active:scale-[0.97] transition-transform"
              style={{ background: V4.accent, color: '#FFF' }}
            >
              {t("Buy Meal Coupon")}
            </button>
            <button className="flex-1 py-2.5 rounded-full font-bold text-xs active:scale-[0.97] transition-transform"
              onClick={() => setShowCouponsView(true)}
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
              {t("My Transactions")}
            </button>
          </div>
        </section>

        {/* ─── ASHRAM SCHEDULE, CULTURE & INFO ─── */}
        <section className="scroll-mt-4">
          <div className="grid grid-cols-3 gap-3">
            {tDeep(EN.placesExploreInfo).map((item, i) => {
              const isRelatedInfo = !item.href && item.icon === 'info'
              // Internal routes (e.g. "/ashram-culture") should navigate via React Router
              // instead of using <a> which triggers a full page reload.
              const isInternalHref = typeof item.href === 'string' && item.href.startsWith('/')

              const Wrapper = item.href ? 'a' : 'button'
              const wrapperProps = item.href
                ? (isInternalHref
                  ? {
                    href: item.href,
                    onClick: (e) => {
                      e.preventDefault()
                      navigate(item.href)
                    },
                  }
                  : { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                )
                : { type: 'button', onClick: isRelatedInfo ? openRelatedInfoModal : openAshramCultureModal }
              const iconColor = item.dark ? V4.textOnDark : V4.accent
              const iconBg = item.dark ? 'rgba(255,255,255,0.1)' : V4.accentBg
              return (
                <Wrapper
                  key={i}
                  {...wrapperProps}
                  className="rounded-2xl p-4 cursor-pointer active:scale-[0.98] transition-all duration-200 flex flex-col items-start border-l-4"
                  style={{
                    textAlign: 'left',
                    background: item.dark ? V4.cardDark : V4.card,
                    boxShadow: item.dark ? V4.shadowMd : '0 2px 8px rgba(62,42,28,0.06), 0 8px 24px rgba(62,42,28,0.04)',
                    borderLeftColor: item.dark ? V4.accent : V4.accentBorder,
                    borderTop: 'none',
                    borderRight: 'none',
                    borderBottom: 'none',
                    borderLeftWidth: 4,
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl mb-3 flex items-center justify-center flex-shrink-0"
                    style={{ background: iconBg }}
                  >
                    {item.icon === 'calendar' && (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    )}
                    {item.icon === 'people' && (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    )}
                    {item.icon === 'info' && (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    )}
                  </div>
                  <p style={{ ...bold, fontSize: 11, lineHeight: 1.2, color: item.dark ? V4.textOnDark : V4.text, letterSpacing: '0.04em' }} className="uppercase ">
                    {item.title}
                  </p>
                  <p className="text-[10px] mt-1 leading-snug" style={{ color: item.dark ? 'rgba(255,255,255,0.7)' : V4.textMuted }}>
                    {item.subtitle}
                  </p>
                </Wrapper>
              )
            })}
          </div>
        </section>

        {/* ─── MEAL COUPON MODAL ─── */}
        {showMealModal && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"

            style={{ background: 'rgba(44,30,20,0.4)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowMealModal(false)}
          >
            <div
              className="w-full max-w-sm rounded-2xl overflow-hidden shadow-xl max-h-[90vh] overflow-y-auto"
              style={{ background: V4.card }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4" style={{ background: V4.accent }}>
                <p style={{ ...bold, color: V4.textOnDark, fontSize: 14, letterSpacing: '0.06em' }}>{t("MEAL COUPON")}</p>
                <button
                  type="button"
                  onClick={() => setShowMealModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  <svg style={{ color: 'white' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm leading-relaxed mb-1" style={{ color: V4.textMuted }}>
                  {t("Purchase coupons for meals at the Ashram.")}
                </p>
                <p className="text-xs font-semibold mb-4" style={{ color: V4.accent }}>{t("For Day Visitors")}</p>
                {/* Day Pass option */}
                {dayPassAvailable && (
                  <div className="flex items-center justify-between py-3 mb-2 rounded-xl px-3" style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}>
                    <div>
                      <p className="text-sm font-bold uppercase" style={{ color: V4.text }}>{mealDayPass.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: V4.textMuted }}>{mealDayPass.desc}</p>
                      <p className="text-xs font-semibold mt-1" style={{ color: V4.accent }}>₹{mealDayPass.price}/person</p>
                    </div>
                    <div className="flex items-center rounded-full overflow-hidden" style={{ background: V4.bg, border: `1px solid ${V4.border}` }}>
                      <button
                        type="button"
                        onClick={() => handleMealDecrement(mealDayPassKey)}
                        className="w-9 h-9 flex items-center justify-center active:opacity-70"
                        style={{ color: V4.text }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14" /></svg>
                      </button>
                      <span className="w-8 text-center text-sm font-semibold tabular-nums" style={{ color: V4.text }}>{mealQty[mealDayPassKey] ?? 0}</span>
                      <button
                        type="button"
                        onClick={() => handleMealIncrement(mealDayPassKey)}
                        className="w-9 h-9 flex items-center justify-center active:opacity-70"
                        style={{ color: V4.text }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: V4.textMuted }}>{t("Or buy individual meals")}</p>
                {filteredMeals.length === 0 ? (
                  <div className="py-4 text-center text-xs font-semibold" style={{ color: V4.textMuted }}>
                    {t("No Meals Available At This Time")}
                  </div>
                ) : (
                  filteredMeals.map((m) => (
                    <div key={m.key ?? m.name} className="py-3" style={{ borderBottom: `1px solid ${V4.border}` }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold uppercase" style={{ color: V4.text }}>{m.name}</p>
                          <p className="text-xs mt-0.5" style={{ color: V4.textMuted }}>{m.time}</p>
                          {m.note && (
                            <p className="text-[10px] mt-0.5 italic" style={{ color: V4.textDim }}>* {m.note}</p>
                          )}
                          <p className="text-xs font-semibold mt-1" style={{ color: V4.accent }}>₹{m.price}/person</p>
                        </div>
                        <div
                          className={`flex items-center rounded-full overflow-hidden flex-shrink-0 ${(mealshake[m.key ?? m.name] ? 'shake' : '')}`}
                          style={{ background: V4.bg, border: `1px solid ${V4.border}` }}
                        >
                          <button
                            type="button"
                            onClick={() => handleMealDecrement(m.key ?? m.name)}
                            className="w-9 h-9 flex items-center justify-center active:opacity-70"
                            style={{ color: V4.text }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14" /></svg>
                          </button>
                          <span className="w-8 text-center text-sm font-semibold tabular-nums" style={{ color: V4.text }}>{mealQty[m.key ?? m.name] ?? 0}</span>
                          <button
                            type="button"
                            onClick={() => handleMealIncrement(m.key ?? m.name)}
                            className="w-9 h-9 flex items-center justify-center active:opacity-70"
                            style={{ color: V4.text }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div className="flex items-center justify-between py-3 mb-4">
                  <span className="text-sm font-semibold" style={{ color: V4.text }}>{t("Subtotal")}</span>
                  <span className="text-sm font-semibold" style={{ color: V4.text }}>
                    ₹{mealSubtotal}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={PayOnlineMeals}
                  className="w-full py-3.5 rounded-full font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: hasMealSelection ? V4.cardDark : V4.bg,
                    color: hasMealSelection ? '#FFF' : V4.text,
                    border: `1px solid ${V4.border}`,
                  }}
                  disabled={secIsLoading || !hasMealSelection}
                >
                  {secIsLoading ? t("Processing…") : t("Pay Online")}
                </button>
                <p className="text-xs text-center mt-4" style={{ color: V4.textDim }}>
                  {t("Please Note: Meal coupons can also be purchased at the Registration Office.")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── MY COUPONS / TRANSACTION HISTORY (separate page, styling matches Meal Coupon & dashboard) ─── */}

        {showCouponsView && (
          <div className="fixed inset-0 z-[200] flex flex-col" style={{ background: V4.bg }}>
            {/* Header bar: same dark style as Meal Coupon modal header */}
            <div className="flex items-center justify-between px-5 py-4" style={{ background: V4.accent, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
              <button
                type="button"
                onClick={() => setShowCouponsView(false)}
                className="flex items-center gap-2 font-bold text-sm"
                style={{ color: V4.textOnDark }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t("Back")}
              </button>
              <p style={{ ...bold, color: V4.textOnDark, fontSize: 14, letterSpacing: '0.06em' }}>{t("TRANSACTION HISTORY")}</p>
              <button
                type="button"
                onClick={() => setShowCouponsView(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto max-w-lg w-full mx-auto px-5 py-4">
              <p className="text-sm leading-relaxed mb-4" style={{ color: V4.textMuted }}>
                {t("Your meal and buggy coupon purchases.")}
              </p>
              <section
                className="rounded-2xl overflow-hidden touch-manipulation"
                style={{ background: V4.cardDark, boxShadow: V4.shadowMd }}
                onTouchStart={onPassTouchStart}
                onTouchEnd={onPassTouchEnd}
              >
                <button
                  type="button"
                  onClick={() => setShowQR(!showQR)}
                  className="w-full flex items-center gap-5 p-5 touch-manipulation text-left min-h-[88px] active:opacity-95 transition-opacity"
                >
                  <div className="w-12 h-12  flex items-center justify-center flex-shrink-0"
                  >
                    {/* iOS Safari gives a viewBox-only SVG no intrinsic size and collapses it
                        to 0 inside a flex row, so width/height must be explicit. */}
                    <svg
                      className="w-12 h-12 flex-shrink-0"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="#ffffff"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M4,4h6v6H4V4M20,4v6H14V4h6M14,15h2V13H14V11h2v2h2V11h2v2H18v2h2v3H18v2H16V18H13v2H11V16h3V15m2,0v3h2V15H16M4,20V14h6v6H4M6,6V8H8V6H6M16,6V8h2V6H16M6,16v2H8V16H6M4,11H6v2H4V11m5,0h4v4H11V13H9V11m2-5h2v4H11V6M2,2V6H0V2A2,2,0,0,1,2,0H6V2H2M22,0a2,2,0,0,1,2,2V6H22V2H18V0h4M2,18v4H6v2H2a2,2,0,0,1-2-2V18H2m20,4V18h2v4a2,2,0,0,1-2,2H18V22Z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-base truncate" style={{ color: V4.textOnDark }}>{activeVisitor?.FullName}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{t("Day Visitor Pass")}</p>
                    <p className="text-[11px] mt-2 font-semibold" style={{ color: V4.accent }}>
                      {showQR ? t("Tap to hide") : t("Tap to show QR code at gate")}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                      style={{ background: V4.accent, color: '#FFF' }}>
                      {t("Active")}
                    </span>
                    <svg className="w-5 h-5 transition-transform duration-300" style={{ color: 'rgba(255,255,255,0.5)', transform: showQR ? 'rotate(180deg)' : '' }}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {visitors.length > 1 && (
                  <div className="flex items-center justify-center gap-2 px-4 pb-2">
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{t("Swipe to switch pass")}</span>
                    <div className="flex gap-1.5">
                      {visitors.map((_, i) => (
                        <div
                          key={i}
                          className="rounded-full transition-all"
                          style={{
                            width: activeVisitorIndex === i ? 10 : 6,
                            height: 6,
                            background: activeVisitorIndex === i ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid transition-[grid-template-rows] duration-300 ease-in-out" style={{ gridTemplateRows: showQR ? '1fr' : '0fr' }}>
                  <div className="overflow-hidden min-h-0">
                    <div className="px-4 pb-6 pt-1 flex flex-col items-center">
                      <img
                        src={activeVisitor?.qrString}
                        alt="Scanner"
                        className="w-44 sm:w-42 h-42 cursor-pointer"
                      />
                      <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>{t("Show this at the gate")}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{activeVisitor?.FullName}</p>
                    </div>
                  </div>
                </div>
              </section>
              <div className="my-2 rounded-2xl p-5" style={{ background: V4.card, boxShadow: V4.shadow, border: `1px solid ${V4.border}` }}>
                <div className="flex gap-2 mb-4">
                  {['all', 'meals', 'buggy'].map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCouponFilter(key)}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
                      style={{
                        background: couponFilter === key ? V4.cardDark : V4.bg,
                        color: couponFilter === key ? V4.textOnDark : V4.text,
                        border: `1px solid ${couponFilter === key ? V4.cardDark : V4.border}`,
                      }}
                    >
                      {key === 'all' ? t("All") : key === 'meals' ? t("Meals") : t("Buggy")}
                    </button>
                  ))}
                </div>
                <div className="space-y-4">
                  {isTxnLoading ? (
                    <p className="text-sm text-center py-6" style={{ color: V4.textMuted }}>{t("Loading transactions…")}</p>
                  ) : transactionsForView.length === 0 ? (
                    <p className="text-sm text-center py-6" style={{ color: V4.textMuted }}>{t("No transactions in this category.")}</p>
                  ) : (
                    transactionsForView.map((tx) => (
                      <div key={tx.id} className="rounded-xl p-4" style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}>
                        <p style={{ ...bold, color: V4.text, fontSize: 11, letterSpacing: '0.06em' }}>
                          {tx.type === 'buggy' ? t("BUGGY COUPONS") : t("MEAL COUPONS")}
                        </p>
                        <p className="text-[11px] mt-0.5" style={{ color: V4.textMuted }}>{tx.date}</p>
                        <div className="mt-3 space-y-2">
                          {tx.items.map((item, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-sm font-medium" style={{ color: V4.text }}>{item.name}</span>
                              <span className="text-xs font-semibold tabular-nums" style={{ color: V4.accent }}>₹ {item.rate} each</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: `1px solid ${V4.accentBorder}` }}>
                          <span className="text-xs font-semibold" style={{ color: V4.textMuted }}>{t("Amount Paid")}</span>
                          <span className="text-sm font-bold tabular-nums" style={{ color: V4.accent }}>₹ {tx.amountPaid}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-2xl p-5 mt-4" style={{ background: V4.card, boxShadow: V4.shadow, border: `1px solid ${V4.border}` }}>
                <div className="flex items-center justify-between mb-4">
                  <p style={{ ...bold, color: V4.text, fontSize: 14, letterSpacing: '0.06em' }}>{t("SCANNING HISTORY")}</p>
                </div>
                <div className="space-y-3">
                  {isScanLoading ? (
                    <p className="text-sm text-center py-6" style={{ color: V4.textMuted }}>{t("Loading scans…")}</p>
                  ) : (scanningHistoryData?.length ?? 0) === 0 ? (
                    <p className="text-sm text-center py-6" style={{ color: V4.textMuted }}>{t("No scanning history found.")}</p>
                  ) : (
                    scanningHistoryData.slice(0, 4).map((s, idx) => (
                      <div key={idx} className="rounded-xl p-4" style={{ background: V4.accentBg, border: `1px solid ${V4.accentBorder}` }}>
                        <p style={{ ...bold, color: V4.text, fontSize: 11, letterSpacing: '0.06em' }}>
                          {(s?.categoryName ?? 'SCAN').toString().toUpperCase()}
                        </p>
                        <p className="text-[11px] mt-0.5" style={{ color: V4.textMuted }}>
                          {s?.date && s?.time ? formatDateTime(`${s?.date}T${s?.time}.000Z`) : ''}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── BUGGY FINDER (full screen) ─── */}
        {showBuggyFinder && (
          <div className="fixed inset-0 z-[300] overflow-y-auto" style={{ background: V4.bg }}>
            {/* Header */}
            <div className="sticky top-0 z-10" style={{ background: V4.accent }}>
              <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowBuggyFinder(false)}
                  aria-label="Back"
                  className="w-9 h-9 -ml-1 flex items-center justify-center rounded-full active:opacity-70"
                  style={{ color: V4.textOnDark }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <p className="flex-1 text-center pr-8" style={{ ...bold, color: V4.textOnDark, fontSize: 22, letterSpacing: '-0.01em', textTransform: 'none' }}>
                  {t("Buggy Finder") ?? 'Buggy Finder'}
                </p>
              </div>
            </div>

            <div className="max-w-lg mx-auto px-4 py-5">
              {/* Stop selector */}
              <p className="mb-2" style={{ ...bold, color: V4.text, fontSize: 16, textTransform: 'none', letterSpacing: '-0.01em' }}>
                {t("Where are you right now?") ?? 'Where are you right now?'}
              </p>
              <div className="flex items-stretch gap-2 mb-5">
                <TimeDropdown
                  ariaLabel="Current stop"
                  value={buggyFinderSelectedStop}
                  onChange={(val) => setBuggyFinderSelectedStop(String(val))}
                  items={buggyFinderStops.map((s) => ({ value: s, label: t((s ?? '').toString().trim()) }))}
                  menuMaxHeight={320}
                  buttonStyle={{
                    background: V4.card,
                    color: V4.text,
                    border: `1px solid ${V4.border}`,
                    boxShadow: V4.shadow,
                  }}
                />
                <button
                  type="button"
                  onClick={openBuggyWalking}
                  aria-label="Walking distances"
                  className="flex items-center justify-center rounded-xl flex-shrink-0 active:scale-95 transition-transform"
                  style={{ width: 52, background: V4.card, border: `1px solid ${V4.border}`, boxShadow: V4.shadow, color: V4.accent }}
                >
                  <svg width="22" height="22" viewBox="0 0 128 128" fill="currentColor">
                    <path d="M67.9,22.6c5.7,0.4,10.8-3.8,11.3-9.7c0.4-5.7-3.8-10.8-9.7-11.3c-5.7-0.4-10.8,3.8-11.3,9.7C57.8,17.1,62.2,22.2,67.9,22.6" />
                    <path d="M59,26.9c2-1.5,4.5-2.3,7.3-2.2c3.5,0.3,6.6,2.5,8.3,5.1l10.5,20.9l14.3,10c1.2,1,2,2.5,1.9,4.1c-0.1,2.6-2.5,4.5-5.1,4.2c-0.7,0-1.5-0.3-2.2-0.7L78.6,57.8c-0.4-0.4-0.9-0.9-1.2-1.5l-4-7.8l-4.7,20.8l18.6,22c0.4,0.7,0.7,1.5,0.9,2.2l5,26.5c0,0.6,0,1,0,1.5c-0.3,4-3.7,6.7-7.6,6.6c-3.2-0.3-5.6-2.6-6.4-5.6l-4.7-24.7L59.4,81l-3.5,16.1c-0.1,0.7-1.2,2.3-1.5,2.9L40,124.5c-1.5,2.2-3.8,3.7-6.6,3.4c-4-0.3-6.9-3.7-6.6-7.6c0.1-1.2,0.6-2.2,1-3.1l13.5-22.5L52.5,45l-7.3,5.9l-4,17.7c-0.4,2.2-2.6,4.1-5,4c-2.6-0.1-4.5-2.5-4.4-5.1c0-0.1,0-0.4,0.1-0.6l4.5-20.6c0.3-0.9,0.7-1.6,1.5-2.2L59,26.9z" />
                  </svg>
                </button>
              </div>

              {/* Routes */}
              {buggyFinderLoading ? (
                <div className="flex flex-col items-center justify-center py-16" style={{ color: V4.textMuted }}>
                  <svg className="animate-spin mb-3" width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" />
                    <path d="M4 12a8 8 0 018-8" stroke={V4.accent} strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  <p className="text-sm font-semibold">{t("Processing…") ?? 'Processing...'}</p>
                </div>
              ) : buggyFinderRoutes.length === 0 ? (
                <div className="rounded-2xl p-6 text-center" style={{ background: V4.card, border: `1px solid ${V4.border}` }}>
                  <p className="text-sm font-semibold" style={{ color: V4.textMuted }}>
                    {t("No buggy routes found for this stop.") ?? 'No buggy routes found for this stop.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tDeep(buggyFinderRoutes).map((route, rIdx) => {
                    const routeId = route?.RouteId ?? rIdx
                    const color = `#${(route?.ColorCode ?? '7A6B5E').toString().replace(/^#/, '')}`
                    const title = [route?.MainRouteName, route?.SubRouteName].filter(Boolean).join(' | ')
                    const timings = (route?.NextTiming ?? '').toString().split(',').map((x) => toLowerAmPm(x.trim())).filter(Boolean)
                    const rideNos = (route?.NextRideNo ?? '').toString().split(',').map((x) => x.trim())
                    return (
                      <div
                        key={String(routeId)}
                        className="rounded-2xl overflow-hidden flex"
                        style={{ background: V4.card, border: `1px solid ${V4.border}`, boxShadow: V4.shadow }}
                      >
                        <div style={{ width: 5, background: color, flexShrink: 0 }} />
                        <div className="flex-1 min-w-0 px-4 py-3.5">
                          <div className="flex items-start gap-3">
                            <p className="flex-1 text-[15px] font-bold leading-snug" style={{ color: V4.text }}>
                              {title}
                            </p>
                            <button
                              type="button"
                              onClick={() => setShowMapLegendModal(true)}
                              aria-label="View map"
                              className="flex-shrink-0 active:scale-90 transition-transform"
                            >
                              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2C8 2 5 5 5 9c0 1.3.4 2.5 1 3.6L12 22l6-9.4c.6-1.1 1-2.3 1-3.6 0-4-3-7-7-7z" />
                                <circle cx="12" cy="9" r="2.2" />
                              </svg>
                            </button>
                          </div>

                          <p className="mt-3 mb-1.5 text-[11px] font-bold tracking-wider" style={{ color: V4.accent }}>
                            {t("UPCOMING TIMINGS") ?? 'UPCOMING TIMINGS'}
                          </p>
                          {timings.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                              {timings.map((time, tIdx) => (
                                <span key={tIdx} className="inline-flex items-center gap-1 text-[15px] font-semibold tabular-nums" style={{ color: V4.text }}>
                                  {time}
                                  {rideNos[tIdx] && (
                                    <button
                                      type="button"
                                      onClick={() => openBuggyRide(route, rideNos[tIdx])}
                                      aria-label={`${t("Ride No.") ?? 'Ride No.'} ${rideNos[tIdx]}`}
                                      className="inline-flex active:opacity-60"
                                      style={{ color: V4.accent }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                                      </svg>
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm" style={{ color: V4.textDim }}>—</p>
                          )}

                          <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: `1px solid ${V4.border}` }}>
                            <button
                              type="button"
                              onClick={() => openBuggyFullSchedule(route)}
                              className="inline-flex items-center gap-1 text-[12px] font-bold tracking-wide active:opacity-70"
                              style={{ color: V4.accent }}
                            >
                              {t("VIEW FULL SCHEDULE") ?? 'VIEW FULL SCHEDULE'}
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => openBuggyRoute(route)}
                              className="inline-flex items-center gap-1 text-[12px] font-bold tracking-wide active:opacity-70"
                              style={{ color: V4.accent }}
                            >
                              {t("VIEW ROUTE") ?? 'VIEW ROUTE'}
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── BUGGY FULL SCHEDULE (bottom sheet) ─── */}
        {showBuggyFullSchedule && (
          <div
            className="fixed inset-0 z-[310] flex items-end justify-center"
            style={{ background: 'rgba(44,30,20,0.45)', backdropFilter: 'blur(2px)' }}
            onClick={() => setShowBuggyFullSchedule(false)}
          >
            <div
              className="w-full max-w-lg rounded-t-3xl overflow-y-auto"
              style={{ background: V4.bg, maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4" style={{ background: V4.bg }}>
                <p style={{ ...bold, color: V4.accent, fontSize: 24, textTransform: 'none', letterSpacing: '-0.01em' }}>
                  {t("Full Schedule") ?? 'Full Schedule'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowBuggyFullSchedule(false)}
                  aria-label="Close"
                  className="w-8 h-8 flex items-center justify-center active:opacity-70"
                  style={{ color: V4.accent }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-4 pb-8 space-y-3">
                {buggyFullScheduleLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke={V4.border} strokeWidth="3" />
                      <path d="M4 12a8 8 0 018-8" stroke={V4.accent} strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </div>
                ) : buggyFullScheduleSections.length === 0 ? (
                  <div className="rounded-2xl p-6 text-center" style={{ background: V4.card, boxShadow: V4.shadow }}>
                    <p className="text-sm font-semibold" style={{ color: V4.textMuted }}>
                      {t("No schedule available.") ?? 'No schedule available.'}
                    </p>
                  </div>
                ) : (
                  tDeep(buggyFullScheduleSections).map((sec, secIdx) => {
                    const key = (sec?.TimeOfDay ?? `sec-${secIdx}`).toString()
                    const labelMap = { Morning: t("Morning"), Afternoon: t("Afternoon"), Evening: t("Evening") }
                    const label = labelMap[sec?.TimeOfDay] ?? (sec?.TimeOfDay ?? '').toString()
                    const items = (sec?.BuggyTimings ?? '').toString().split(',').map((s) => toLowerAmPm(s.trim())).filter(Boolean)
                    const open = fullScheduleSection === key
                    const perCol = Math.max(1, Math.ceil(items.length / 3))
                    const cols = [items.slice(0, perCol), items.slice(perCol, perCol * 2), items.slice(perCol * 2)]
                    return (
                      <div key={key} className="rounded-2xl overflow-hidden" style={{ background: V4.card, boxShadow: V4.shadow }}>
                        <button
                          type="button"
                          onClick={() => setFullScheduleSection((cur) => (cur === key ? null : key))}
                          className="w-full flex items-center justify-between px-5 py-4 active:opacity-80"
                        >
                          <span className="flex items-center gap-1.5 text-[17px] font-bold" style={{ color: V4.accent }}>
                            {label}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </span>
                          <span className="text-[15px] font-semibold tabular-nums" style={{ color: V4.accentDark }}>{(sec?.TimeRange ?? '').toString()}</span>
                        </button>
                        {open && (
                          items.length > 0 ? (
                            <div className="grid grid-cols-3 px-5 pb-5 pt-1">
                              {cols.map((col, ci) => (
                                <div
                                  key={ci}
                                  className="flex flex-col gap-3"
                                  style={{ paddingLeft: ci > 0 ? 14 : 0, borderLeft: ci > 0 ? `1px solid ${V4.border}` : 'none' }}
                                >
                                  {col.map((tm, ti) => (
                                    <span key={ti} className="text-[15px] font-semibold tabular-nums" style={{ color: V4.accentDark }}>{tm}</span>
                                  ))}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="px-5 pb-5 text-sm" style={{ color: V4.textDim }}>—</p>
                          )
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── BUGGY ROUTE STOPS (bottom sheet) ─── */}
        {showBuggyRoute && (
          <div
            className="fixed inset-0 z-[310] flex items-end justify-center"
            style={{ background: 'rgba(44,30,20,0.45)', backdropFilter: 'blur(2px)' }}
            onClick={() => setShowBuggyRoute(false)}
          >
            <div
              className="w-full max-w-lg rounded-t-3xl overflow-y-auto"
              style={{ background: V4.bg, maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4" style={{ background: V4.bg }}>
                <div className="flex items-center gap-2 min-w-0">
                  {buggyRouteData?.color && (
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: buggyRouteData.color }} />
                  )}
                  <p className="truncate" style={{ ...bold, color: V4.accent, fontSize: 20, textTransform: 'none', letterSpacing: '-0.01em' }}>
                    {t(buggyRouteData?.title) || (t("VIEW ROUTE") ?? 'Route')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBuggyRoute(false)}
                  aria-label="Close"
                  className="w-8 h-8 flex items-center justify-center active:opacity-70 flex-shrink-0"
                  style={{ color: V4.accent }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-4 pb-8">
                <div className="rounded-2xl px-5 py-4" style={{ background: V4.card, boxShadow: V4.shadow }}>
                  <p className="text-[11px] font-bold tracking-wider mb-3" style={{ color: V4.textMuted }}>
                    {t("ROUTE STOPS") ?? 'ROUTE STOPS'}
                  </p>
                  {(buggyRouteData?.stops ?? []).length > 0 ? (
                    <div className="space-y-0">
                      {buggyRouteData.stops.map((stop, sIdx) => (
                        <div key={sIdx} className="flex items-start gap-3">
                          <div className="flex flex-col items-center flex-shrink-0" style={{ width: 14 }}>
                            <span className="w-3 h-3 rounded-full" style={{ background: buggyRouteData.color, marginTop: 3 }} />
                            {sIdx < buggyRouteData.stops.length - 1 && (
                              <span style={{ width: 2, flex: 1, minHeight: 22, background: V4.border }} />
                            )}
                          </div>
                          <span className="text-[14px] font-medium pb-3" style={{ color: V4.text }}>{t(stop)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: V4.textDim }}>—</p>
                  )}
                </div>

                {buggyRouteData?.mapUrl && (
                  <button
                    type="button"
                    onClick={() => window.open(buggyRouteData.mapUrl, '_blank', 'noopener,noreferrer')}
                    className="w-full mt-3 py-3.5 rounded-full font-bold text-sm active:scale-[0.98] transition-transform"
                    style={{ ...bold, fontSize: 13, letterSpacing: '0.04em', lineHeight: 1, background: V4.accent, color: '#FFF' }}
                  >
                    {t("VIEW ROUTE") ?? 'VIEW ROUTE'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── BUGGY WALKING DISTANCES (bottom sheet) ─── */}
        {showBuggyWalking && (
          <div
            className="fixed inset-0 z-[320] flex items-end justify-center"
            style={{ background: 'rgba(44,30,20,0.45)', backdropFilter: 'blur(2px)' }}
            onClick={() => setShowBuggyWalking(false)}
          >
            <div
              className="w-full max-w-lg rounded-t-3xl overflow-y-auto"
              style={{ background: V4.bg, maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4" style={{ background: V4.bg }}>
                <p style={{ ...bold, color: V4.accent, fontSize: 24, textTransform: 'none', letterSpacing: '-0.01em' }}>
                  {t("Walking Distances") ?? 'Walking Distances'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowBuggyWalking(false)}
                  aria-label="Close"
                  className="w-8 h-8 flex items-center justify-center active:opacity-70"
                  style={{ color: V4.accent }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-4 pb-8">
                {/* FROM card */}
                <div className="rounded-2xl px-5 py-4" style={{ background: V4.card, boxShadow: V4.shadow }}>
                  <p className="text-[11px] font-bold tracking-wider uppercase" style={{ color: V4.accent }}>{t("From") ?? 'FROM'}</p>
                  <p className="text-[18px] font-bold mt-0.5" style={{ color: V4.text }}>{buggyWalkingFrom}</p>
                </div>

                {/* connector */}
                <div className="flex flex-col items-center py-2" style={{ paddingLeft: 4 }}>
                  <span className="self-start w-3.5 h-3.5 rounded-full" style={{ background: V4.accent, marginLeft: 16 }} />
                  <span className="self-start" style={{ marginLeft: 22, width: 2, height: 14, background: 'transparent', borderLeft: `2px dotted ${V4.accent}` }} />
                  <span className="self-start w-3.5 h-3.5 rounded-full" style={{ background: V4.accent, marginLeft: 16 }} />
                </div>

                {/* TO list */}
                {buggyWalkingLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke={V4.border} strokeWidth="3" />
                      <path d="M4 12a8 8 0 018-8" stroke={V4.accent} strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </div>
                ) : buggyWalkingItems.length === 0 ? (
                  <div className="rounded-2xl p-6 text-center" style={{ background: V4.card, boxShadow: V4.shadow }}>
                    <p className="text-sm font-semibold" style={{ color: V4.textMuted }}>
                      {t("No walking distances available.") ?? 'No walking distances available.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tDeep(buggyWalkingItems).map((item, i) => (
                      <div key={i} className="rounded-2xl px-5 py-4 flex items-center gap-3" style={{ background: V4.card, boxShadow: V4.shadow }}>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold tracking-wider" style={{ color: V4.accent }}>{t("TO") ?? 'TO'}</p>
                          <p className="text-[17px] font-bold mt-0.5 leading-snug" style={{ color: V4.text }}>{(item?.ToStopName ?? '').toString()}</p>
                        </div>
                        <svg width="26" height="26" viewBox="0 0 128 128" fill={V4.accent} className="flex-shrink-0">
                          <path d="M67.9,22.6c5.7,0.4,10.8-3.8,11.3-9.7c0.4-5.7-3.8-10.8-9.7-11.3c-5.7-0.4-10.8,3.8-11.3,9.7C57.8,17.1,62.2,22.2,67.9,22.6" />
                          <path d="M59,26.9c2-1.5,4.5-2.3,7.3-2.2c3.5,0.3,6.6,2.5,8.3,5.1l10.5,20.9l14.3,10c1.2,1,2,2.5,1.9,4.1c-0.1,2.6-2.5,4.5-5.1,4.2c-0.7,0-1.5-0.3-2.2-0.7L78.6,57.8c-0.4-0.4-0.9-0.9-1.2-1.5l-4-7.8l-4.7,20.8l18.6,22c0.4,0.7,0.7,1.5,0.9,2.2l5,26.5c0,0.6,0,1,0,1.5c-0.3,4-3.7,6.7-7.6,6.6c-3.2-0.3-5.6-2.6-6.4-5.6l-4.7-24.7L59.4,81l-3.5,16.1c-0.1,0.7-1.2,2.3-1.5,2.9L40,124.5c-1.5,2.2-3.8,3.7-6.6,3.4c-4-0.3-6.9-3.7-6.6-7.6c0.1-1.2,0.6-2.2,1-3.1l13.5-22.5L52.5,45l-7.3,5.9l-4,17.7c-0.4,2.2-2.6,4.1-5,4c-2.6-0.1-4.5-2.5-4.4-5.1c0-0.1,0-0.4,0.1-0.6l4.5-20.6c0.3-0.9,0.7-1.6,1.5-2.2L59,26.9z" />
                        </svg>
                        <div className="flex-shrink-0">
                          <p className="text-[10px] font-bold tracking-wider" style={{ color: V4.accent }}>{t("DISTANCE") ?? 'DISTANCE'}</p>
                          <p className="text-[15px] font-bold mt-0.5" style={{ color: V4.text }}>{(item?.WalkingDistance ?? '').toString()}</p>
                        </div>
                        <div className="flex-shrink-0">
                          <p className="text-[10px] font-bold tracking-wider" style={{ color: V4.accent }}>{t("TIME") ?? 'TIME'}</p>
                          <p className="text-[15px] font-bold mt-0.5" style={{ color: V4.text }}>{(item?.WalkingMin ?? '').toString()} {t("mins") ?? 'mins'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── BUGGY RIDE STOPS (bottom sheet timeline) ─── */}
        {showBuggyRide && (
          <div
            className="fixed inset-0 z-[320] flex items-end justify-center"
            style={{ background: 'rgba(44,30,20,0.45)', backdropFilter: 'blur(2px)' }}
            onClick={() => setShowBuggyRide(false)}
          >
            <div
              className="w-full max-w-lg rounded-t-3xl overflow-y-auto"
              style={{ background: V4.bg, maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-4" style={{ background: V4.accent }}>
                <button
                  type="button"
                  onClick={() => setShowBuggyRide(false)}
                  aria-label="Back"
                  className="w-8 h-8 -ml-1 flex items-center justify-center active:opacity-70 flex-shrink-0"
                  style={{ color: V4.textOnDark }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <p className="truncate" style={{ ...bold, color: V4.textOnDark, fontSize: 20, textTransform: 'none', letterSpacing: '-0.01em' }}>
                  {buggyRideTitle || (t("VIEW ROUTE") ?? 'Route')}
                </p>
              </div>

              <div className="px-5 py-5">
                {buggyRideLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke={V4.border} strokeWidth="3" />
                      <path d="M4 12a8 8 0 018-8" stroke={V4.accent} strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </div>
                ) : buggyRideStops.length === 0 ? (
                  <p className="text-sm text-center py-10" style={{ color: V4.textMuted }}>
                    {t("No schedule available.") ?? 'No schedule available.'}
                  </p>
                ) : (
                  <div>
                    {tDeep(buggyRideStops).map((s, i) => {
                      const first = i === 0
                      const last = i === buggyRideStops.length - 1
                      return (
                        <div key={i} className="flex items-center gap-4" style={{ minHeight: 56 }}>
                          <span className="text-[15px] font-semibold tabular-nums text-right flex-shrink-0" style={{ width: 80, color: V4.accentDark }}>
                            {s.time}
                          </span>
                          <div className="relative self-stretch flex items-center justify-center flex-shrink-0" style={{ width: 18 }}>
                            <span
                              className="absolute"
                              style={{ left: '50%', transform: 'translateX(-50%)', width: 2, background: buggyRideColor || V4.accent, top: first ? '50%' : 0, bottom: last ? '50%' : 0 }}
                            />
                            <span className="relative w-3.5 h-3.5 rounded-full" style={{ background: buggyRideColor || V4.accent }} />
                          </div>
                          <span className="flex-1 text-[16px] font-semibold" style={{ color: V4.accentDark }}>{s.name}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── BUGGY COUPON MODAL ─── */}
        {showBuggyModal && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: 'rgba(44,30,20,0.4)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowBuggyModal(false)}
          >
            <div
              className="w-full max-w-sm rounded-2xl overflow-hidden shadow-xl"
              style={{ background: V4.card }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4" style={{ background: V4.accent }}>
                <p style={{ ...bold, color: V4.textOnDark, fontSize: 14, letterSpacing: '0.06em' }}>{t("BUGGY COUPON")}</p>
                <button
                  type="button"
                  onClick={() => setShowBuggyModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  <svg width="14" height="14" style={{ color: 'white' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm leading-relaxed mb-1" style={{ color: V4.textMuted }}>
                  {t("As the Ashram is spread across a vast land of 223 acres, it is recommended that you purchase buggy passes for easy commute around the Ashram.")}
                </p>
                <p className="text-xs font-semibold mb-5" style={{ color: V4.accent }}>{t("For Day Visitors")}</p>
                <div className="flex items-center justify-between py-3 mb-4" style={{ borderBottom: `1px solid ${V4.border}` }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: V4.accentBg, border: `2px solid ${V4.accentBorder}` }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={V4.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: V4.text }}>{t("DAILY PASS")}</p>
                      <p className="text-xs" style={{ color: V4.textMuted }}>₹{BuggyData?.categoryRate}/day</p>
                    </div>
                  </div>
                  <div
                    className={`flex items-center rounded-full overflow-hidden ${(shake ? 'shake' : '')}`}
                    style={{ background: V4.bg, border: `1px solid ${V4.border}` }}
                  >
                    <button
                      type="button"
                      onClick={handleBuggyDecrement}
                      className="w-9 h-9 flex items-center justify-center active:opacity-70"
                      style={{ color: V4.text }}
                    >

                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14" /></svg>
                    </button>
                    <span className="w-8 text-center text-sm font-semibold tabular-nums" style={{ color: V4.text }}>{buggyQty}</span>
                    <button
                      type="button"
                      onClick={handleBuggyIncrement}
                      disabled={noDayTourAvailable}
                      className="w-9 h-9 flex items-center justify-center active:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ color: V4.text }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 mb-4">
                  <span className="text-sm" style={{ color: V4.textMuted }}>{t("Subtotal")}</span>
                  <span className="text-sm font-semibold" style={{ color: V4.text }}>₹{buggyQty * BuggyData?.categoryRate}</span>
                </div>
                <button
                  type="button"
                  onClick={buggyQty > 0 && !secIsLoading && !noDayTourAvailable ? PayOnlineBuggy : undefined}
                  className="w-full py-3.5 rounded-full font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: buggyQty > 0 && !noDayTourAvailable ? V4.cardDark : V4.bg,
                    color: buggyQty > 0 && !noDayTourAvailable ? V4.textOnDark : V4.text,
                    border: `1px solid ${buggyQty > 0 && !noDayTourAvailable ? V4.cardDark : V4.border}`,
                  }}
                  disabled={buggyQty === 0 || noDayTourAvailable}
                >
                  {secIsLoading ? t("Processing…") : t("Pay Online")}
                </button>
                <p className="text-xs text-center mt-4" style={{ color: V4.textDim }}>
                  {t("Please note: Buggy passes can also be purchased at the Registration Office.")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── GUIDED TOUR TIMINGS (info icon on the plan's Ashram tour row) ─── */}
        {showTourTimesModal && (
          <div
            className="fixed inset-0 z-[300] flex items-center justify-center px-4"
            style={{ background: 'rgba(20,14,8,0.55)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowTourTimesModal(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="w-full max-w-sm rounded-2xl overflow-hidden"
              style={{ background: V4.card, boxShadow: V4.shadowMd }}
              // Taps inside the sheet must not close it.
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="flex items-start justify-between gap-3 px-5 py-4"
                style={{ background: V4.bg, borderBottom: `1px solid ${V4.border}` }}
              >
                <div className="min-w-0">
                  <p style={{ ...bold, color: V4.text, fontSize: 13, letterSpacing: '0.06em' }}>
                    {t('GUIDED ASHRAM TOURS')}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: V4.textMuted }}>
                    {t('45 mins from Welcome Centre')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTourTimesModal(false)}
                  aria-label={t('Close') ?? 'Close'}
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 active:opacity-70 transition-opacity"
                  style={{ background: V4.card, border: `1px solid ${V4.border}`, color: V4.textMuted }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {guidedToursDisplay.length === 0 ? (
                  <p className="px-5 py-4 text-xs" style={{ color: V4.textMuted }}>
                    {t('No plan available.')}
                  </p>
                ) : (
                  guidedToursDisplay.map((tour, i) => (
                    <div
                      key={tour?.keyId ?? `${tour?.startTime ?? 'tour'}-${i}`}
                      className="flex items-center gap-3 px-5 py-3"
                      style={{
                        borderBottom: i < guidedToursDisplay.length - 1 ? `1px solid ${V4.border}` : 'none',
                      }}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-widest flex-shrink-0" style={{ color: V4.textDim }}>
                        {t('Tour')} {i + 1}
                      </span>
                      <span className="text-sm font-bold flex-1 tabular-nums" style={{ color: V4.text }}>
                        {fmtMin(tour.startTime)}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div className="px-5 py-3" style={{ background: V4.accentBg, borderTop: `1px solid ${V4.accentBorder}` }}>
                <p className="text-[11px] leading-snug" style={{ color: V4.text }}>
                  {t('Please arrive 15 minutes before your guided tour. If your tour starts at 9:00 am, please arrive by 8:45 am for orientation.')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── MAP LEGEND MODAL ─── */}
        {showMapLegendModal && (
          <div
            className="fixed inset-0 z-[300] flex items-center justify-center"
            style={{ background: 'rgba(20,14,8,0.92)', backdropFilter: 'blur(6px)' }}
            onClick={() => setShowMapLegendModal(false)}
            role="dialog"
            aria-modal="true"
          >
            {/* Portrait orientation: rotate the landscape map 90° so it fills
                the phone screen — user tilts the device to read it. Landscape:
                no rotation, image fits naturally. */}
            <style>{`
              .map-legend-img-portrait {
                transform: rotate(90deg);
                max-width: 100vh;
                max-height: 100vw;
              }
              @media (orientation: landscape) {
                .map-legend-img-portrait {
                  transform: none;
                  max-width: 100vw;
                  max-height: 100vh;
                }
              }
            `}</style>

            <button
              type="button"
              onClick={() => setShowMapLegendModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
              style={{ background: 'rgba(255,255,255,0.18)', color: '#FFF', zIndex: 2 }}
              aria-label="Close map"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <img
              src={mapLegendImage}
              alt="Ashram map legend"
              draggable={false}
              onClick={(e) => e.stopPropagation()}
              className="map-legend-img-portrait"
              style={{
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                touchAction: 'pinch-zoom',
                userSelect: 'none',
                display: 'block',
              }}
            />
          </div>
        )}

        {/* ─── ROUTE MAP MODAL ─── */}
        {showRouteMapModal && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: 'rgba(44,30,20,0.4)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowRouteMapModal(false)}
          >
            <div
              className="w-full max-w-sm rounded-2xl overflow-hidden shadow-xl"
              style={{ background: V4.card }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4" style={{ background: V4.cardDark }}>
                <p style={{ ...bold, color: V4.textOnDark, fontSize: 14, letterSpacing: '0.06em' }}>{t("Route Map")}</p>
                <button
                  type="button"
                  onClick={() => setShowRouteMapModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-5 py-5">
                {isRouteMapLoading ? (
                  <p className="text-sm text-center py-6" style={{ color: V4.textMuted }}>
                    {'Loading route map...'}
                  </p>
                ) : (
                  <div className="rounded-xl p-4" style={{ background: V4.bg, border: `1px solid ${V4.accentBorder}` }}>
                    <div className="space-y-0">
                      {routeMapStopsData.map((stop, i) => (
                        <div key={`${stop}-${i}`} className="flex items-start gap-3">
                          <div className="pb-2">
                            <span className="text-[12px] font-bold leading-tight" style={{ color: V4.text }}>{toLowerAmPm(stop.NextTiming)}</span>
                          </div>
                          <div className="flex flex-col items-center pt-0.5">
                            <div className="w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: V4.accent, background: '#FFF' }} />
                            {i < routeMapStopsData.length - 1 && (
                              <div className="w-[2px] h-6" style={{ background: V4.accent }} />
                            )}
                          </div>
                          <div className="pb-2">
                            <span className="text-[12px] font-bold leading-tight" style={{ color: V4.text }}>{stop.StopName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-center mt-3" style={{ color: V4.textMuted }}>
                      {routeMapStopsData[0].StopName} → {routeMapStopsData[routeMapStopsData.length - 1].StopName}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ─── EXPLORE SPACES ─── */}
        <section ref={exploreRef} className="scroll-mt-4">
          <p className="mb-4" style={{ ...bold, color: V4.text, fontSize: 28, lineHeight: 1 }}>
            {t("EXPLORE")}<br /><span style={{ color: V4.accent }}>{t("ASHRAM")}</span>
          </p>

          {/* Top 3: grid with Jinmandir featured, Raj Sabhagruh, Raj Darbar */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {spacesToRender.slice(0, 3).map((space, i) => (
              <div
                key={i}
                className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.97] transition-transform"
                role="button"
                tabIndex={0}
                onClick={() => openExploreDetails(space)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openExploreDetails(space)
                }}
                style={{
                  gridColumn: space.featured ? 'span 2' : 'span 1',
                  height: space.featured ? 190 : 150,
                  boxShadow: V4.shadow,
                }}>
                <img src={space.img} alt={space.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.0) 55%)' }} />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p style={{ ...bold, color: '#FFFFFF', fontSize: space.featured ? 22 : 16, lineHeight: 1, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                    {space.name.toUpperCase()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Strip: Meditation Hall, Library, Meditation Garden, Divine Shop */}
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide snap-x" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {spacesToRender.slice(3).map((space, i) => (
              <div
                key={i}
                className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.97] transition-transform flex-shrink-0 w-[140px] snap-start"
                role="button"
                tabIndex={0}
                onClick={() => openExploreDetails(space)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openExploreDetails(space)
                }}
                style={{ height: 140, boxShadow: V4.shadow }}>
                <img src={space.img} alt={space.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.0) 55%)' }} />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p style={{ ...bold, color: '#FFFFFF', fontSize: 11, lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                    {space.name.toUpperCase()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div ref={nearbyRef} className="scroll-mt-4">
            <p className="mt-8 mb-4" style={{ ...bold, color: V4.text, fontSize: 28, lineHeight: 1 }}>
              {t("PLACES TO EXPLORE")}<br /><span style={{ color: V4.accent }}>{t("NEARBY")}</span>
            </p>
            {/* First row: image cards in horizontal slider (Ranchhodbhai House to the right of Lady Wilson Museum) */}
            <div className="flex gap-3 mb-4 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide snap-x" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {placesNearbyToRender.map((place, i) => (
                <div
                  key={i}
                  className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.97] transition-transform flex-shrink-0 w-[250px] snap-start"
                  role="button"
                  tabIndex={0}
                  onClick={() => place.post ? openNearByDetails(place) : null}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && place.post) openNearByDetails(place)
                  }}
                  style={{ height: 140, boxShadow: V4.shadow }}>
                  <img src={place.img} alt={place.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.0) 55%)' }} />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p style={{ ...bold, color: '#FFFFFF', fontSize: 11, lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                      {place.name.toUpperCase()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SELF CONNECT APP (accent card) ─── */}
        <section className="rounded-2xl p-5 relative overflow-hidden" style={{ background: V4.accent }}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
          <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />

          <div className="relative z-10">
            <p style={{ ...bold, color: '#FFF', fontSize: 11, letterSpacing: '0.12em', marginBottom: 8 }}>{t("RECOMMENDED")}</p>
            <div className="flex items-center gap-2 mb-2">
              <div className='w-16'>
                <img src={SelfConnectImg} alt="Self Connect App" className="" />
              </div>
              <div>
                <p style={{ ...bold, color: '#FFF', fontSize: 24, lineHeight: 1, marginBottom: 4 }}>{t("SELF CONNECT")}</p>
                <p className="text-xs " style={{ color: 'rgba(255,255,255,0.6)' }}>{t("A Free Mobile App for Inner Growth and Daily Spiritual Practice")}</p>
              </div>
            </div>


            <div className="flex gap-2">
              <a href="https://selfconnect.srmd.org/8dj4k3" target="_blank" rel="noopener noreferrer">
                <img src="https://d3fzplfbemxxxv.cloudfront.net/wp-content/uploads/2026/03/Download_on_the_App_Store_Badge.svg" alt="App Store" className="w-48" />
              </a>
              <a href="https://selfconnect.srmd.org/8dj4k3" target="_blank" rel="noopener noreferrer">
                <img src="https://d3fzplfbemxxxv.cloudfront.net/wp-content/uploads/2026/03/Google_Play_Store_badge_EN.svg" alt="Google Play" className="w-48" />
              </a>
            </div>
          </div>
        </section>

        <div className="h-4" />
      </main>



      {/* Font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');

        /* Related Info content (WordPress HTML) */
        .related-info-content ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0.5rem 0;
        }
        .related-info-content ol {
          list-style: decimal;
          padding-left: 1.25rem;
          margin: 0.5rem 0;
        }
        .related-info-content li {
          margin: 0.35rem 0;
          line-height: 1.4;
        }
        .related-info-content p {
          margin: 0.5rem 0;
        }
        .related-info-content a {
          color: ${V4.accent};
          text-decoration: underline;
        }
        .related-info-content strong {
          color: ${V4.text};
        }

        /* WP tables inside Related Info */
        .related-info-content .related-info-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid ${V4.accentBorder};
          background: #fff;
          margin: 0.75rem 0;
          overflow: hidden;
          border-radius: 12px;
        }
        .related-info-content .related-info-table th,
        .related-info-content .related-info-table td {
          border: 1px solid ${V4.accentBorder};
          padding: 10px 12px;
          text-align: left;
          vertical-align: top;
        }
        .related-info-content .related-info-table th {
          background: ${V4.accentBg};
          color: ${V4.text};
          font-weight: 700;
        }

        /* WP custom button tag: <srmdbtn> converted to <a> */
        .related-info-content .related-info-srmdbtn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 9999px;
          font-weight: 800;
          font-size: 12px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          background: ${V4.accent};
          color: #fff;
          text-decoration: none;
          box-shadow: 0 4px 16px rgba(212,98,43,0.25);
          margin-bottom: 10px;
        }
        .related-info-content .related-info-srmdbtn:active {
          transform: scale(0.98);
        }

        
      `}</style>

      {/* ─── EXPLORE DETAILS (FULL-SCREEN SHEET) ─── */}
      {showExploreDetails && (
        <div
          className="fixed inset-0 z-[250] flex flex-col"
          style={{ background: V4.bg }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4"
            style={{ background: V4.accent, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <button
              type="button"
              onClick={closeExploreDetails}
              className="flex items-center gap-2 font-bold text-sm"
              style={{ color: V4.textOnDark }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t("Back")}
            </button>
            <button
              type="button"
              onClick={closeExploreDetails}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
              style={{ background: 'rgba(255,255,255,0.15)', color: V4.textOnDark }}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto max-w-lg w-full mx-auto">
            {(() => {
              const post = selectedExplorePost
              const title = decodeHtmlEntities(post?.title?.rendered)
              const acf = post?.acf ?? {}

              const gallery = Array.isArray(acf?.vertical_gallery) && acf.vertical_gallery.length > 0
                ? acf.vertical_gallery
                : (Array.isArray(acf?.slider_gallery) ? acf.slider_gallery : [])

              const shortDescription = (acf?.short_description ?? '').toString().trim()
              const features = Array.isArray(acf?.features) ? acf.features : []
              const events = Array.isArray(acf?.events) ? acf.events : []

              // Card-specific inserts
              const isJinmandir = /jinmandir/i.test(title)

              const idx = Math.min(Math.max(0, exploreGalleryIndex), Math.max(0, gallery.length - 1))

              const getYouTubeCode = (value) => {
                const raw = (value ?? '').toString().trim()
                if (!raw) return ''
                // API is expected to send only the YouTube id (e.g. "fYTTRBFNWqQ")
                if (/^[a-zA-Z0-9_-]{6,}$/.test(raw)) return raw
                // fallback: try parse youtu.be / youtube.com
                const match = raw.match(/(?:youtu\.be\/|v=|embed\/)([a-zA-Z0-9_-]{6,})/)
                return match?.[1] ?? ''
              }

              return (
                <div className="px-5 py-5">
                  {/* Gallery */}
                  <div className='bg-white'>
                    <DragSnapSlider
                      images={gallery}
                      title={title}
                      index={idx}
                      onIndexChange={setExploreGalleryIndex}
                      showArrows
                      autoPlay
                      autoPlayInterval={3500}
                      cardStyle={{ background: '#FFF', border: `1px solid ${V4.accentBorder}`, boxShadow: V4.shadow }}
                    />

                    {/* Title + description */}
                    <div className="mt-5 flex flex-col w-full items-center justify-center">
                      <p style={{ ...bold, color: V4.accent, fontSize: 30, lineHeight: 1 }}>
                        {title}
                      </p>
                      {shortDescription && (

                        <div
                          className="mt-3 text-sm text-center"
                          style={{ color: V4.textMuted }}
                          dangerouslySetInnerHTML={{ __html: shortDescription }}
                        />
                      )}
                    </div>

                    {/* Jinmandir: Salient Features image (between Title+description and Features) */}
                    {isJinmandir && (
                      <div className="mt-6">
                        <div
                          className="overflow-hidden"

                        >
                          <img
                            src={jainMandirImage}
                            alt="Salient Features"
                            className="w-full h-auto"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  {features.length > 0 && (
                    <div className="mt-7">

                      <div className="mt-4 space-y-4">
                        {features.map((f, i) => {
                          const title = (f?.title ?? '').toString().trim()
                          const description = (f?.description ?? '').toString().trim()
                          const images = Array.isArray(f?.images) ? f.images.filter(Boolean) : []

                          return (
                            <div
                              key={`${title}-${i}`}
                              className="rounded-2xl overflow-hidden"

                            >
                              {images.length > 0 && (() => {
                                const featureKey = `${title || 'feature'}-${i}`
                                const currentIdxRaw = exploreFeatureCarouselIndexByKey?.[featureKey] ?? 0
                                const currentIdx = Math.min(Math.max(0, currentIdxRaw), Math.max(0, images.length - 1))

                                return (
                                  <div className="px-3 pb-2">
                                    <DragSnapSlider
                                      images={images}
                                      title={title || `Feature ${i + 1}`}
                                      index={currentIdx}
                                      onIndexChange={(nextIdx) =>
                                        setExploreFeatureCarouselIndexByKey((prev) => ({
                                          ...prev,
                                          [featureKey]: nextIdx,
                                        }))
                                      }
                                      showArrows
                                      autoPlay
                                      autoPlayInterval={3500}
                                      imgClassName="w-full object-cover"
                                      cardStyle={{
                                        border: `1px solid ${V4.accentBorder}`,
                                        background: '#FFF',
                                        boxShadow: V4.shadow,
                                        borderRadius: 6,
                                      }}
                                    />
                                  </div>
                                )
                              })()}
                              <div className="px-3 py-2">
                                {title && (
                                  <p
                                    className="mb-2"
                                    style={{
                                      fontFamily: "'Outfit', 'Inter', sans-serif",
                                      fontWeight: 700,
                                      color: V4.accent,
                                      fontSize: 22,
                                      letterSpacing: '-0.02em',
                                    }}
                                  >
                                    {title}
                                  </p>
                                )}
                                {description && (

                                  <div
                                    className="mt-3 text-sm "
                                    style={{ color: V4.textMuted }}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                  />
                                )}
                              </div>


                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Related Videos (thumbnail cards) */}
                  {events.length > 0 && (
                    <div className="mt-8 bg-white p-8">
                      <p style={{ ...bold, color: V4.text, fontSize: 14, letterSpacing: '0.08em' }}>
                        {t('Related Videos')}
                      </p>
                      <div
                        className="mt-4 flex gap-4 overflow-x-auto pb-2 -mx-5 px-5 snap-x snap-mandatory scrollbar-hide"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      >
                        {events.map((ev, i) => {
                          const title = (ev?.title ?? '').toString().trim()
                          const code = getYouTubeCode(ev?.youtube_url)
                          if (!code) return null

                          const thumbnail = `https://img.youtube.com/vi/${code}/mqdefault.jpg`
                          const watchUrl = `https://www.youtube.com/watch?v=${code}`

                          return (
                            <div
                              key={`${title}-${i}`}
                              // Fixed width ensures 1.5 cards visibility on most mobiles
                              className="overflow-hidden flex-shrink-0 snap-start w-[260px]"

                            >
                              <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="block">
                                <div className="w-full  aspect-video relative" >
                                  <img src={thumbnail} alt={title || `Video ${i + 1}`} className="w-full h-full object-cover rounded-l" />

                                  <div className=" absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center"
                                      style={{ background: 'rgba(255,255,255,0.9)', boxShadow: '0 6px 18px rgba(0,0,0,0.25)' }}>
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill={V4.accent}>
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </a>
                              {title && (
                                <div className="px-4 py-3">
                                  <p className="text-sm font-semibold" style={{ color: V4.text }}>
                                    {title}
                                  </p>
                                  <a
                                    href={watchUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center mt-3 px-4 py-2 rounded-md text-xs font-bold"
                                    style={{ color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                                  >
                                    {t('Watch Now')}
                                  </a>
                                </div>
                              )}
                            </div>
                          )
                        }).filter(Boolean)}
                      </div>
                    </div>
                  )}

                  <div className="h-10" />
                </div>
              )
            })()}
          </div>
        </div>
      )}
      {
        showAshramCultureModal && (
          <div className="fixed inset-0 z-[220] flex flex-col" style={{ background: V4.bg }}>
            {/* Header */}
            <div
              className="px-5 py-4 "
              style={{ background: V4.accent, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            >

              <button
                type="button"
                onClick={() => setShowAshramCultureModal(false)}
                className="flex items-center gap-2 font-bold text-sm"
                style={{ color: V4.textOnDark }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t("Back")}
              </button>






            </div>
            <div className="flex-1 overflow-y-auto max-w-lg w-full mx-auto ">
              {ashramcultureDetails.map((item) => (
                <article
                  key={item.id}
                  className=""
                >
                  <div className="grid grid-cols-1 md:grid-cols-12">


                    {/* Content */}
                    <div className={"md:col-span-12"}>
                      <div className="p-5 md:p-6">
                        {item.title ? (
                          <h3 className="text-xl md:text-2xl font-extrabold text-rose-900 mb-3">
                            {item.title}
                          </h3>
                        ) : null}
                        <div className="bg-white p-5 rounded-md shadow overflow-hidden">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className="w-full h-auto rounded-xl mb-4"
                              loading="lazy"
                            />
                          ) : null}
                          <div
                            className="prose prose-rose max-w-none prose-p:leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: item.contentHtml }}
                          />
                          
                        
                        {item.contact_person && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M20 21a8 8 0 0 0-16 0" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.contact_person }}
                                />
                              </div>
                            )}
                        {item.contact_number && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 
                                          19.79 19.79 0 0 1-8.63-3.07 
                                          19.5 19.5 0 0 1-6-6 
                                          19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 
                                          12.84 12.84 0 0 0 .7 2.81 
                                          2 2 0 0 1-.45 2.11L8.09 9.91 
                                          a16 16 0 0 0 6 6l1.27-1.27 
                                          a2 2 0 0 1 2.11-.45 
                                          12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.contact_number }}
                                />

                              </div>
                            )}
                            {item.contact2_number && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 
                                          19.79 19.79 0 0 1-8.63-3.07 
                                          19.5 19.5 0 0 1-6-6 
                                          19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 
                                          12.84 12.84 0 0 0 .7 2.81 
                                          2 2 0 0 1-.45 2.11L8.09 9.91 
                                          a16 16 0 0 0 6 6l1.27-1.27 
                                          a2 2 0 0 1 2.11-.45 
                                          12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.contact2_number }}
                                />

                              </div>
                            )}


                            {item.location && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.location }}
                                />
                              </div>
                            )}

                            {item.timings && (
                              <div className="mt-2 flex items-start gap-2">
                                {/* Time icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M12 6v6l4 2" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.timings }}
                                />
                              </div>
                            )}


                            {item.email && (
                              <div className="mt-2 flex items-start gap-2">
                                {/* Time icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <rect x="2" y="4" width="20" height="16" rx="2" />
                                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.email }}
                                />
                              </div>
                            )}

                            {item.instruction && (
                              <div className="mt-2 flex items-start gap-2">

                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.instruction }}
                                />
                              </div>
                            )}
                            </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
              
              <a href={`https://1342372186.rsc.cdn77.org/common/ashram_culture_english_2024.pdf`} target="_blank" rel="noopener noreferrer"
              className="w-full py-3.5 flex items-center justify-center rounded-md font-bold text-sm active:scale-[0.97] transition-all disabled:opacity-25"
              style={{
                ...bold, fontSize: 13, letterSpacing: '0.06em', lineHeight: 1,
                background:  V4.accent , color: '#FFF' ,
                boxShadow:'0 4px 16px rgba(212,98,43,0.25)'
              }}>
              DownLoad (PDF)
            </a>
          </div>


        )
      }
      {/* ─── RELATED INFO (FULL-SCREEN SHEET) ─── */}
      {showRelatedInfoModal && (
        <div className="fixed inset-0 z-[220] flex flex-col" style={{ background: V4.bg }}>
          {/* Header */}
          <div
            className="px-5 py-4 "
            style={{ background: V4.accent, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >

            {!relatedInfoSelectedCategory &&
              <button
                type="button"
                onClick={() => setShowRelatedInfoModal(false)}
                className="flex items-center gap-2 font-bold text-sm"
                style={{ color: V4.textOnDark }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t("Back")}
              </button>
            }
            <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
              {relatedInfoSelectedCategory && (
                <button
                  type="button"
                  onClick={() => {
                    setRelatedInfoSelectedCategory(null)
                    setRelatedInfoItems([])
                    setRelatedInfoItemsError('')
                    setExpandedRelatedInfoItemId(null)
                    setRelatedInfoQuery('')
                  }}
                  className="flex items-center gap-2 font-bold text-sm"
                  style={{ color: V4.textOnDark }}
                  aria-label={t("Back") ?? 'Back'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  {t("Back") ?? 'Back'}
                </button>
              )}


            </div>

            {/* Search only on category list screen */}
            {!relatedInfoSelectedCategory && (
              <div className="max-w-lg mx-auto mt-4">
                <div
                  className="flex items-center gap-2 rounded-xl px-3 py-2"
                  style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.16)' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    value={relatedInfoQuery}
                    onChange={(e) => setRelatedInfoQuery(e.target.value)}
                    placeholder={relatedInfoI18n.searchPlaceholder}
                    className="w-full bg-transparent placeholder:text-white outline-none text-sm"
                    style={{ color: V4.textOnDark }}
                  />
                </div>
              </div>
            )}

            {/* Category title (when inside) */}
            {relatedInfoSelectedCategory && (
              <div className="max-w-lg mx-auto mt-3">
                <p
                  className="truncate"
                  style={{ ...bold, color: V4.textOnDark, fontSize: 14, letterSpacing: '0.06em' }}
                  title={t(relatedInfoSelectedCategory?.name) ?? relatedInfoI18n.title}
                >
                  {t(relatedInfoSelectedCategory?.name) ?? relatedInfoI18n.title}
                </p>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto max-w-lg w-full mx-auto px-5 py-5">
            {isRelatedInfoLoading ? (
              <p className="text-sm text-center py-10" style={{ color: V4.textMuted }}>
                {relatedInfoI18n.loading}
              </p>
            ) : relatedInfoError ? (
              <div className="text-center py-10">
                <p className="text-sm" style={{ color: V4.textMuted }}>{relatedInfoError}</p>
                <button
                  type="button"
                  onClick={() => fetchRelatedInfoCategories()}
                  className="mt-4 px-4 py-2 rounded-full text-xs font-bold"
                  style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                >
                  Retry
                </button>
              </div>
            ) : relatedInfoSelectedCategory ? (
              isRelatedInfoItemsLoading ? (
                <p className="text-sm text-center py-10" style={{ color: V4.textMuted }}>
                  {relatedInfoI18n.loading}
                </p>
              ) : relatedInfoItemsError ? (
                <div className="text-center py-10">
                  <p className="text-sm" style={{ color: V4.textMuted }}>{relatedInfoItemsError}</p>
                  <button
                    type="button"
                    onClick={() => fetchRelatedInfoItems(relatedInfoSelectedCategory.id)}
                    className="mt-4 px-4 py-2 rounded-full text-xs font-bold"
                    style={{ background: V4.accentBg, color: V4.accent, border: `1px solid ${V4.accentBorder}` }}
                  >
                    Retry
                  </button>
                </div>
              ) : relatedInfoItemsFiltered.length === 0 ? (
                <p className="text-sm text-center py-10" style={{ color: V4.textMuted }}>
                  {'No information found.'}
                </p>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    const detailImage = relatedInfoSelectedCategory.detailImage
                    const rawImages = Array.isArray(relatedInfoSelectedCategory.images)
                      ? relatedInfoSelectedCategory.images.filter(Boolean)
                      : []
                    const sliderImages = rawImages?.length > 0 ? rawImages : [detailImage]

                    if (sliderImages.length === 0) return null

                    const idx = Math.min(
                      Math.max(0, relatedInfoCarouselIndex),
                      Math.max(0, sliderImages.length - 1)
                    )

                    return (
                      <DragSnapSlider
                        images={sliderImages}
                        title={relatedInfoSelectedCategory.name}
                        index={idx}
                        onIndexChange={setRelatedInfoCarouselIndex}
                        showArrows
                        autoPlay
                        autoPlayInterval={3500}
                        imgClassName="w-full h-auto object-cover related-info-carousel-img"
                        cardStyle={{
                          border: `1px solid ${V4.accentBorder}`,
                          background: '#FFF',
                          boxShadow: V4.shadow,
                          borderRadius: 6,
                        }}
                      />
                    )
                  })()}

                  {relatedInfoItemsFiltered.map((item) => {
                    const open = expandedRelatedInfoItemId === item.id
                    const hasAnyContent = Boolean(
                      (item?.contentHtml ?? '').toString().trim()
                      || (item?.instruction ?? '').toString().trim()
                      || (item?.location ?? '').toString().trim()
                      || (item?.timings ?? '').toString().trim()
                      || (item?.contact_person ?? '').toString().trim()
                      || (item?.contact_number ?? '').toString().trim()
                      || (item?.contact2_number ?? '').toString().trim()
                      || (item?.email ?? '').toString().trim()
                      || (Array.isArray(item?.images) && item.images.filter(Boolean).length > 0)
                    )
                    return (
                      <div
                        key={item.id}
                        className="rounded-2xl overflow-hidden"
                        style={{ border: `1px solid ${V4.accentBorder}`, background: '#FFF', boxShadow: V4.shadow }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (!hasAnyContent) return
                            setExpandedRelatedInfoItemId((prev) => prev === item.id ? null : item.id)
                          }}
                          className="w-full px-4 py-4 flex items-center justify-between gap-3 text-left"
                          disabled={!hasAnyContent}
                          aria-disabled={!hasAnyContent}
                          style={{ opacity: hasAnyContent ? 1 : 0.45, cursor: hasAnyContent ? 'pointer' : 'not-allowed' }}
                        >
                          <p className="font-semibold" style={{ color: V4.text }}>
                            {item.title}
                          </p>
                          <svg
                            className="w-5 h-5 flex-shrink-0 transition-transform"
                            style={{ color: V4.accent, transform: open ? 'rotate(180deg)' : undefined }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {open && (
                          <div className="px-4 pb-4">
                            {(() => {
                              const images = Array.isArray(item?.images)
                                ? item.images.filter(Boolean)
                                : []
                              if (images.length === 0) return null

                              const currentIdxRaw = relatedInfoItemCarouselIndexById?.[item.id] ?? 0
                              const currentIdx = Math.min(
                                Math.max(0, currentIdxRaw),
                                images.length - 1
                              )

                              return (
                                <div className="mt-3">
                                  <DragSnapSlider
                                    images={images}
                                    title={item.title}
                                    index={currentIdx}
                                    onIndexChange={(nextIdx) =>
                                      setRelatedInfoItemCarouselIndexById((prev) => ({
                                        ...prev,
                                        [item.id]: nextIdx,
                                      }))
                                    }
                                    showArrows
                                    autoPlay
                                    autoPlayInterval={3500}
                                    imgClassName="w-full h-auto object-cover related-info-carousel-img"
                                    cardStyle={{
                                      border: `1px solid ${V4.accentBorder}`,
                                      background: '#FFF',
                                      boxShadow: V4.shadow,
                                      borderRadius: 6,
                                    }}
                                  />
                                </div>
                              )
                            })()}
                            <div
                              className="related-info-content text-sm"
                              style={{ color: V4.textMuted }}
                              onClick={handleRelatedInfoContentClick}
                              dangerouslySetInnerHTML={{ __html: item.contentHtml }}
                            />

                            <div
                              className="related-info-content text-sm"
                              style={{ color: V4.textMuted }}
                              onClick={handleRelatedInfoContentClick}
                              dangerouslySetInnerHTML={{ __html: item.instruction }}
                            />


                            {item.contact_person && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M20 21a8 8 0 0 0-16 0" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.contact_person }}
                                />
                              </div>
                            )}

                            {item.contact_number && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 
                                          19.79 19.79 0 0 1-8.63-3.07 
                                          19.5 19.5 0 0 1-6-6 
                                          19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 
                                          12.84 12.84 0 0 0 .7 2.81 
                                          2 2 0 0 1-.45 2.11L8.09 9.91 
                                          a16 16 0 0 0 6 6l1.27-1.27 
                                          a2 2 0 0 1 2.11-.45 
                                          12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.contact_number }}
                                />

                              </div>
                            )}
                            {item.contact2_number && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 
                                          19.79 19.79 0 0 1-8.63-3.07 
                                          19.5 19.5 0 0 1-6-6 
                                          19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 
                                          12.84 12.84 0 0 0 .7 2.81 
                                          2 2 0 0 1-.45 2.11L8.09 9.91 
                                          a16 16 0 0 0 6 6l1.27-1.27 
                                          a2 2 0 0 1 2.11-.45 
                                          12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.contact2_number }}
                                />

                              </div>
                            )}


                            {item.location && (
                              <div className="mt-3 flex items-start gap-2">
                                {/* Location icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.location }}
                                />
                              </div>
                            )}

                            {item.timings && (
                              <div className="mt-2 flex items-start gap-2">
                                {/* Time icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M12 6v6l4 2" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.timings }}
                                />
                              </div>
                            )}


                            {item.email && (
                              <div className="mt-2 flex items-start gap-2">
                                {/* Time icon */}
                                <svg
                                  className="flex-shrink-0 mt-[2px]"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke={V4.accent}
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <rect x="2" y="4" width="20" height="16" rx="2" />
                                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                </svg>
                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.email }}
                                />
                              </div>
                            )}

                            {item.note && (
                              <div className="mt-2 flex items-start gap-2">

                                <div
                                  className="related-info-content text-sm"
                                  style={{ color: V4.textMuted }}
                                  onClick={handleRelatedInfoContentClick}
                                  dangerouslySetInnerHTML={{ __html: item.note }}
                                />
                              </div>
                            )}


                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {relatedInfoFiltered.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setRelatedInfoSelectedCategory(cat)
                      fetchRelatedInfoItems(cat.id)
                    }}
                    className="rounded-2xl p-4 flex flex-col items-center justify-center text-center active:scale-[0.98] transition-transform"
                    style={{ background: '#FFF', border: `1px solid ${V4.accentBorder}`, boxShadow: V4.shadow }}
                  >
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3" style={{ background: V4.accentBg }}>
                      {cat.image ? (
                        <img src={cat.image} alt={cat.name} className="w-10 h-10 object-contain" />
                      ) : (
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={V4.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                      )}
                    </div>
                    <p className="font-semibold" style={{ color: V4.accent, fontSize: 14 }}>
                      {cat.name}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {console.log(nearByDetails,">>>>>>>>nearByDetails")}
      {/* ─── ROUTE MAP MODAL ─── */}
      {nearByDetails?.post?.id && (
        <div
          className="fixed inset-0 z-[250] flex flex-col"
          style={{ background: V4.bg }}
        >

          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4"
            style={{ background: V4.accent, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <button
              type="button"
              onClick={() => { openNearByDetails({}) }}
              className="flex items-center gap-2 font-bold text-sm"
              style={{ color: V4.textOnDark }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t("Back")}
            </button>
            <button
              type="button"
              onClick={() => { openNearByDetails({}) }}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity active:opacity-80"
              style={{ background: 'rgba(255,255,255,0.15)', color: V4.textOnDark }}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-w-lg w-full mx-auto">
            <div className="">
              <div className="rounded-md p-4" >
                <div className='flex items-center justify-center text-md mt-2 font-bold' style={{ color: V4.accentDark }}>{nearByDetails?.post?.title?.rendered}</div>
                <div className=" pb-4">
                  {(() => {
                    const wpImages = Array.isArray(nearByDetails?.post?.acf?.images)
                      ? nearByDetails.post.acf.images.filter(Boolean)
                      : []
                    const extraImages = Array.isArray(nearByDetails?.extraImages)
                      ? nearByDetails.extraImages.filter(Boolean)
                      : []
                    // WordPress wins when it has photos; then the bundled ones for
                    // places whose post has none yet; then the card image.
                    const images = wpImages.length > 0
                      ? wpImages
                      : (extraImages.length > 0 ? extraImages : [nearByDetails?.img].filter(Boolean))
                    if (images.length === 0) return null

                    // Same swipeable slider the Help & Information sheets use, so
                    // multiple photos can be dragged/swiped instead of only being
                    // reachable by tapping a dot.
                    const currentIdxRaw = relatedInfoItemCarouselIndexById?.[nearByDetails?.post?.id] ?? 0
                    const currentIdx = Math.min(Math.max(0, currentIdxRaw), images.length - 1)

                    return (
                      <div className="mt-3">
                        <DragSnapSlider
                          images={images}
                          title={nearByDetails?.post?.title?.rendered}
                          index={currentIdx}
                          onIndexChange={(idx) =>
                            setRelatedInfoItemCarouselIndexById((prev) => ({
                              ...prev,
                              [nearByDetails?.post?.id]: idx,
                            }))
                          }
                          showArrows
                          imgClassName="w-full h-auto object-cover related-info-carousel-img"
                          cardStyle={{
                            border: `1px solid ${V4.accentBorder}`,
                            background: '#FFF',
                            boxShadow: V4.shadow,
                            borderRadius: 6,
                          }}
                        />
                      </div>
                    )
                  })()}

                  {(() => {
                    // Plays in place — the card no longer links out to YouTube.
                    const embedUrl = toYouTubeEmbedUrl(nearByDetails?.videoUrl)
                    if (!embedUrl) return null
                    return (
                      <div
                        className="mt-3 rounded-md overflow-hidden"
                        style={{
                          border: `1px solid ${V4.accentBorder}`,
                          background: '#000',
                          boxShadow: V4.shadow,
                        }}
                      >
                        {/* 16:9 box: the iframe has no intrinsic height of its own. */}
                        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                          <iframe
                            src={embedUrl}
                            title={nearByDetails?.post?.title?.rendered ?? 'Video'}
                            className="absolute inset-0 w-full h-full"
                            style={{ border: 0 }}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    )
                  })()}

                  <div
                    className="related-info-content mt-2 text-sm"
                    // style={{ color: V4.textMuted }}
                    onClick={handleRelatedInfoContentClick}
                    dangerouslySetInnerHTML={{ __html: nearByDetails?.post?.content?.rendered }}
                  />

                  {nearByDetails?.post?.acf?.contact_person && (
                    <div className="mt-3 flex items-start gap-2">
                      {/* Location icon */}
                      <svg
                        className="flex-shrink-0 mt-[2px]"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={V4.accent}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M20 21a8 8 0 0 0-16 0" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <div className='related-info-content text-sm'>{nearByDetails?.post?.acf?.contact_person}</div>

                    </div>
                  )}

                  {nearByDetails?.post?.acf?.contact_number && (
                    <div className="mt-3 flex items-start gap-2">
                      {/* Location icon */}
                      <svg
                        className="flex-shrink-0 mt-[2px]"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={V4.accent}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 
                                          19.79 19.79 0 0 1-8.63-3.07 
                                          19.5 19.5 0 0 1-6-6 
                                          19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 
                                          12.84 12.84 0 0 0 .7 2.81 
                                          2 2 0 0 1-.45 2.11L8.09 9.91 
                                          a16 16 0 0 0 6 6l1.27-1.27 
                                          a2 2 0 0 1 2.11-.45 
                                          12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <div className='related-info-content text-sm'>{nearByDetails?.post?.acf?.contact_number}</div>

                    </div>
                  )}

                  {nearByDetails?.post?.acf?.location && (
                    <div className="mt-3 flex items-start gap-2">
                      {/* Location icon */}
                      <svg
                        className="flex-shrink-0 mt-[2px]"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={V4.accent}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <div
                        className="related-info-content text-sm"
                        style={{ color: V4.textMuted }}
                        onClick={handleRelatedInfoContentClick}
                        dangerouslySetInnerHTML={{ __html: nearByDetails?.post?.acf?.location }}
                      />
                    </div>
                  )}

                  {nearByDetails?.post?.acf?.timings && (
                    <div className="mt-2 flex items-start gap-2">
                      {/* Time icon */}
                      <svg
                        className="flex-shrink-0 mt-[2px]"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={V4.accent}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      <div
                        className="related-info-content text-sm"
                        // style={{ color: V4.textMuted }}
                        onClick={handleRelatedInfoContentClick}
                        dangerouslySetInnerHTML={{ __html: nearByDetails?.post?.acf?.timings }}
                      />
                    </div>
                  )}


                </div>

              </div>

            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  )
}

export default DashBoardData
